import React, { useEffect, useState, Fragment, Suspense, lazy, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Shield, Sparkles, Gift, BookOpen, Repeat, Calendar, Video, Users, User, ArrowRight, Lock, MapPin, Sword, ChevronLeft, ChevronRight, Info, ExternalLink, Menu, X, Share2, MessageCircle, Copy, Check, Star, Award, Gem, Heart, Plus, Layers, Moon, ArrowDown, ArrowUp, Trophy, Zap, Coins, UserPlus } from 'lucide-react';
import { KnightingCeremony } from '../components/KnightingCeremony';
import { RewardsTabContent } from '../components/treasury/RewardsTabContent';
import { CreateMislPanel } from '../components/treasury/CreateMislPanel';
import { FoundingMislStatus } from '../components/treasury/FoundingMislStatus';
// Lazy-loaded heavy components — they only ship JS to the client when actually opened.
// TreasuryAdminDashboard (~3.3k lines) + TreasuryOnboarding are by far the biggest
// modules on this page; loading them on demand cuts initial bundle dramatically.
const TreasuryOnboarding = lazy(() => import('../components/TreasuryOnboarding').then(m => ({
  default: m.TreasuryOnboarding
})));
const TreasuryAdminDashboard = lazy(() => import('../components/TreasuryAdminDashboard').then(m => ({
  default: m.TreasuryAdminDashboard
})));
// --- Types & Interfaces ---
// ---------- Visual Atoms ----------
const OrnateDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex items-center justify-center gap-3 ${className}`} data-id="element-4657">
    <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/60" data-id="element-4658" />
    <svg width="28" height="14" viewBox="0 0 28 14" fill="none" data-id="element-4659">
      <path d="M14 0 L17 4 L24 5 L19 9 L20 14 L14 11 L8 14 L9 9 L4 5 L11 4 Z" fill="#D4AF37" opacity="0.6" data-id="element-4660" />
    </svg>
    <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/60" data-id="element-4661" />
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-4662" />
    <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-4663" />
    <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-4664" />
    <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-4665" />
  </>;
const SectionHeader = ({
  eyebrow,
  title,
  description
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
}) => <div className="text-center mb-12" data-id="element-4666">
    <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-4 font-light" data-id="element-4667">
      {eyebrow}
    </p>
    <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-4668">
      {title}
    </h2>
    <OrnateDivider className="mb-6" data-id="element-4669" />
    {description && <p className="text-[#FDFBF7]/70 text-sm sm:text-base font-light max-w-2xl mx-auto leading-relaxed" data-id="element-4670">
        {description}
      </p>}
  </div>;
const RarityBadge = ({
  rarity
}: {
  rarity: string;
}) => {
  let colorClass = 'border-[#FDFBF7]/20 text-[#FDFBF7]/60 bg-[#FDFBF7]/5';
  if (rarity.includes('Founders')) colorClass = 'border-[#D4AF37]/70 text-[#D4AF37] bg-[#D4AF37]/10';else if (rarity.includes('Treasury Rare')) colorClass = 'border-[#D4AF37]/50 text-[#D4AF37]/90 bg-[#D4AF37]/5';else if (rarity.includes('Epic')) colorClass = 'border-[#8B0000]/70 text-[#8B0000] bg-[#8B0000]/10';else if (rarity.includes('Live Night') || rarity.includes('Treasury Night')) colorClass = 'border-violet-400/50 text-violet-300 bg-violet-400/10';
  return <span className={`text-[9px] tracking-[0.25em] uppercase font-medium px-2 py-1 border whitespace-nowrap ${colorClass}`} data-id="element-4671">
      {rarity}
    </span>;
};
// ---------- Honour Glyph ----------
const HonourGlyph = ({
  type,
  className = ''
}: {
  type?: string;
  className?: string;
}) => {
  // Map honour type to an icon; default to Crown
  switch (type) {
    case 'order-of-rs':
      return <Star className={className} data-id="element-4672" />;
    case 'gold-medallion':
      return <Award className={className} data-id="element-4673" />;
    case 'silver-medallion':
      return <Shield className={className} data-id="element-4674" />;
    case 'sangat-seal':
      return <Users className={className} data-id="element-4675" />;
    case 'royal-sash':
      return <Crown className={className} data-id="element-4676" />;
    default:
      return <Crown className={className} data-id="element-4677" />;
  }
};
// ---------- 1. Hero Section ----------
const HeroSection = ({
  onJoin
}: {
  onJoin: () => void;
}) => <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-20 pb-16 overflow-hidden" data-id="element-4678">
    {/* Background Elements */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_70%)] pointer-events-none" data-id="element-4679" />
    <div className="absolute -left-24 -top-24 w-96 h-96 bg-[#8B0000]/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4680" />
    <div className="absolute -right-24 -bottom-24 w-96 h-96 bg-[#D4AF37]/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4681" />

    <div className="container mx-auto px-4 relative z-10 flex flex-col items-center text-center" data-id="element-4682">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="mb-6" data-id="element-4683">
        <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-bold border border-[#D4AF37]/30 px-4 py-2 bg-[#D4AF37]/5" data-id="element-4684">
          Enter the Lahore Court
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2
    }} className="font-serif text-5xl sm:text-7xl lg:text-8xl text-[#FDFBF7] mb-8 leading-[0.9] tracking-tight" data-id="element-4685">
        TREASURES OF THE <span className="text-[#D4AF37]" data-id="element-4686">SIKH EMPIRE</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.4
    }} className="text-[#FDFBF7]/60 text-lg sm:text-xl font-light tracking-[0.15em] uppercase mb-12 max-w-2xl leading-relaxed" data-id="element-4687">
        Recovering the lost stories of the Sikh Kingdoms.
      </motion.p>

      {/* Interactive Card Fan - Desktop Only */}
      <div className="hidden lg:block relative w-full max-w-4xl h-[300px] mb-12" data-id="element-4688">
        {[{
        img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
        rotate: -15,
        x: -220,
        y: 20,
        z: 10
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
        rotate: -5,
        x: -110,
        y: 0,
        z: 20
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
        rotate: 0,
        x: 0,
        y: -20,
        z: 30
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
        rotate: 5,
        x: 110,
        y: 0,
        z: 20
      }, {
        img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
        rotate: 15,
        x: 220,
        y: 20,
        z: 10
      }].map((card, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: 0,
        rotate: 0
      }} animate={{
        opacity: 1,
        x: card.x,
        rotate: card.rotate
      }} transition={{
        duration: 1,
        delay: 0.6 + i * 0.1
      }} whileHover={{
        y: card.y - 20,
        scale: 1.05,
        zIndex: 40
      }} className="absolute left-1/2 top-10 sm:top-0 w-[140px] sm:w-[220px] aspect-[4/5] -ml-[70px] sm:-ml-[110px] shadow-2xl cursor-pointer" style={{
        zIndex: card.z
      }} data-id="element-4689">
            <div className="w-full h-full border border-[#D4AF37]/40 bg-[#0B0F19] p-1" data-id="element-4690">
              <img src={card.img} alt="Court Card" className="w-full h-full object-cover" data-id="element-4691" />
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 1.2
    }} className="flex flex-col items-center gap-6 w-full" data-id="element-4692">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto" data-id="element-4693">
          <button type="button" onClick={onJoin} className="w-full sm:w-auto px-10 py-5 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#E5C158] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] flex items-center justify-center gap-3 group" data-id="element-4694">
            Join the Treasury
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-4695" />
          </button>
          <button type="button" onClick={onJoin} className="w-full sm:w-auto px-10 py-5 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-medium transition-colors text-center" data-id="element-4696">
            I Have an Invite
          </button>
        </div>

        <a href="#how-it-works" className="text-[#FDFBF7]/40 hover:text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase transition-colors" data-id="element-4697">
          See How It Works
        </a>
      </motion.div>
    </div>
  </section>;
// ---------- 2. What It Is Section ----------
const WhatItIsSection = () => {
  return <section className="py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-4698">
      <div className="max-w-4xl mx-auto text-center px-4 mb-16" data-id="element-4699">
        <SectionHeader eyebrow="The Concept" title={<>
              A COLLECTIBLE <span className="text-[#D4AF37]" data-id="element-4701">PRESTIGE</span>{' '}
              EXPERIENCE
            </>} data-id="element-4700" />
        <p className="text-[#FDFBF7]/80 text-lg sm:text-xl font-light leading-relaxed max-w-2xl mx-auto" data-id="element-4702">
          Treasures of the Sikh Empire is not a game. It is a digital extension
          of the Lahore Court. By collecting beautifully crafted artifacts,
          trading with the community, and attending the live event, you signal
          your status and unlock real-world rewards.
        </p>
      </div>

      {/* Centerpiece Collage */}
      <div className="relative w-full max-w-6xl mx-auto px-4" data-id="element-4703">
        <div className="grid grid-cols-12 grid-rows-6 gap-3 sm:gap-4 h-[400px] sm:h-[560px]" data-id="element-4704">
          {/* Large left — Lahore Fort */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="col-span-7 row-span-4 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4705">
            <CornerOrnaments data-id="element-4706" />
            <img src="https://cdn.magicpatterns.com/uploads/7Y6dgrsg2zL5S5gqJcmvvX/Lahore_Fort_and_Hazuri_Bagh.png" alt="Lahore Fort and Hazuri Bagh" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4707" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-4708" />
            <div className="absolute bottom-4 left-4 right-4" data-id="element-4709">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-4710">
                Places of Power
              </p>
              <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight" data-id="element-4711">
                Lahore Fort & Hazuri Bagh
              </h4>
            </div>
          </motion.div>

          {/* Top right — Maharaja portrait card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.15
        }} className="col-span-5 row-span-3 relative border border-[#D4AF37]/40 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4712">
            <CornerOrnaments data-id="element-4713" />
            <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Maharaja Ranjit Singh" className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4714" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-4715" />
            <div className="absolute bottom-4 left-4 right-4" data-id="element-4716">
              <p className="text-[#D4AF37] text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-4717">
                Founders' Edition
              </p>
              <h4 className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-4718">
                Maharaja Ranjit Singh
              </h4>
            </div>
          </motion.div>

          {/* Mid right — Harmandir Sahib */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="col-span-3 row-span-3 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4719">
            <img src="https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png" alt="Harmandir Sahib" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4720" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-4721" />
            <div className="absolute bottom-3 left-3 right-3" data-id="element-4722">
              <p className="font-serif text-xs sm:text-sm text-[#FDFBF7] leading-tight" data-id="element-4723">
                Harmandir Sahib
              </p>
            </div>
          </motion.div>

          {/* Bottom-mid — Valley of Kashmir */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.45
        }} className="col-span-2 row-span-2 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4724">
            <img src="https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png" alt="Valley of Kashmir" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4725" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/70 to-transparent" data-id="element-4726" />
          </motion.div>

          {/* Bottom-left of right column — Jind Kaur */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="col-span-3 row-span-2 relative border border-[#D4AF37]/40 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4727">
            <img src="https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png" alt="Maharani Jind Kaur" className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4728" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/80 via-transparent to-transparent" data-id="element-4729" />
            <div className="absolute bottom-3 left-3 right-3" data-id="element-4730">
              <p className="font-serif text-xs sm:text-sm text-[#FDFBF7] leading-tight" data-id="element-4731">
                Maharani Jind Kaur
              </p>
            </div>
          </motion.div>

          {/* Bottom-right — Jamrud Fort */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.75
        }} className="col-span-2 row-span-2 relative border border-[#D4AF37]/30 bg-[#131B2B] p-1.5 overflow-hidden shadow-2xl group" data-id="element-4732">
            <img src="https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png" alt="Jamrud Fort" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" data-id="element-4733" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/70 to-transparent" data-id="element-4734" />
            <div className="absolute bottom-2 left-2 right-2" data-id="element-4735">
              <p className="font-serif text-[10px] sm:text-xs text-[#FDFBF7] leading-tight" data-id="element-4736">
                Jamrud Fort
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
// ---------- 3. How It Works Section ----------
const HowItWorksSection = () => {
  const steps = [{
    num: '01',
    title: 'Sign Up',
    desc: 'Join the Lahore Court.',
    icon: Users
  }, {
    num: '02',
    title: 'Daily Packet',
    desc: 'Open one sealed envelope daily.',
    icon: Gift
  }, {
    num: '03',
    title: 'Collect',
    desc: 'Complete artifact sets.',
    icon: Crown
  }, {
    num: '04',
    title: 'Trade',
    desc: 'Exchange duplicates with others.',
    icon: Repeat
  }, {
    num: '05',
    title: 'Rank Up',
    desc: 'Increase your court influence.',
    icon: Trophy
  }, {
    num: '06',
    title: 'Live Unlocks',
    desc: 'Scan codes at the immersive event.',
    icon: Zap
  }, {
    num: '07',
    title: 'Treasury Nights',
    desc: 'Redeem progress for real rewards.',
    icon: Star
  }, {
    num: '08',
    title: 'Court Sessions',
    desc: 'Access private creator previews.',
    icon: Video
  }];
  return <section id="how-it-works" className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-4737">
      <div className="max-w-5xl mx-auto" data-id="element-4738">
        <SectionHeader eyebrow="The Journey" title={<>
              HOW IT <span className="text-[#D4AF37]" data-id="element-4740">WORKS</span>
            </>} data-id="element-4739" />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-4741">
          {steps.map(step => {
          const Icon = step.icon;
          return <div key={step.num} className="flex items-start gap-6 p-4 border-b border-[#D4AF37]/10 hover:bg-[#0B0F19] transition-colors group" data-id="element-4742">
                <span className="font-serif text-4xl text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-4743">
                  {step.num}
                </span>
                <div data-id="element-4744">
                  <h4 className="font-serif text-xl text-[#FDFBF7] mb-1 flex items-center gap-2" data-id="element-4745">
                    {step.title}
                    <Icon className="w-4 h-4 text-[#D4AF37]/60" data-id="element-4746" />
                  </h4>
                  <p className="text-[#FDFBF7]/60 text-sm font-light" data-id="element-4747">
                    {step.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// ---------- 4. Registration Flow Section ----------
const RegistrationFlowSection = () => <section id="signup" className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-4748">
    <div className="max-w-7xl mx-auto text-center" data-id="element-4749">
      <SectionHeader eyebrow="Sign-Up Journey" title={<>
            THE REGISTRATION <span className="text-[#D4AF37]" data-id="element-4751">FLOW</span>
          </>} description="Five simple steps from invitation to your first artifact." data-id="element-4750" />

      <div className="flex flex-col lg:flex-row items-center lg:items-center justify-start lg:justify-start gap-4 mt-16 overflow-x-auto pb-8 snap-x scrollbar-hide px-4 lg:px-0" data-id="element-4752">
        {/* Screen 01 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-4753">
          <div className="text-center mb-8 mt-4" data-id="element-4754">
            <p className="text-[#D4AF37] text-[8px] tracking-[0.3em] uppercase mb-2" data-id="element-4755">
              Treasures of the Sikh Empire
            </p>
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-4756">
              Join the Lahore Court
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full space-y-4" data-id="element-4757">
            <div className="w-full border border-[#D4AF37]/20 bg-[#0B0F19] p-3 text-left" data-id="element-4758">
              <span className="text-[#FDFBF7]/30 text-sm font-light" data-id="element-4759">
                your.name@email.com
              </span>
            </div>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-4760">
              Request Invitation
            </div>
          </div>
          <p className="text-[#FDFBF7]/40 text-[10px] mt-auto" data-id="element-4761">
            Existing members → Sign In
          </p>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-4762" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-4763" />

        {/* Screen 02 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-4764">
          <div className="text-center mb-8 mt-4" data-id="element-4765">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-4766">
              A seal arrived in your inbox
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full flex flex-col items-center" data-id="element-4767">
            <div className="flex gap-2 mb-4" data-id="element-4768">
              {[...Array(6)].map((_, i) => <div key={i} className="w-8 h-10 border border-[#D4AF37]/40 bg-[#0B0F19]" data-id="element-4769" />)}
            </div>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light text-center mb-8" data-id="element-4770">
              Enter the 6-digit code from the Darbar
            </p>
            <div className="w-full border border-[#D4AF37]/40 py-3 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-4771">
              Verify
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-4772" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-4773" />

        {/* Screen 03 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-4774">
          <div className="text-center mb-8 mt-4" data-id="element-4775">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight" data-id="element-4776">
              Your court name
            </h4>
          </div>
          <div className="mt-auto mb-auto w-full space-y-8" data-id="element-4777">
            <div className="w-full border-b border-[#D4AF37]/40 bg-transparent pb-2 text-center" data-id="element-4778">
              <span className="text-[#FDFBF7]/80 text-lg font-serif" data-id="element-4779">
                @harnoor
              </span>
            </div>
            <div className="flex justify-center gap-4" data-id="element-4780">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-4781">
                <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-4782" />
              </div>
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-4783">
                <Gem className="w-5 h-5 text-[#D4AF37]/40" data-id="element-4784" />
              </div>
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-4785">
                <Shield className="w-5 h-5 text-[#D4AF37]/40" data-id="element-4786" />
              </div>
            </div>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-4787">
              Enter the Court
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-4788" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-4789" />

        {/* Screen 04 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-4790">
          <div className="mt-auto mb-auto w-full flex flex-col items-center text-center" data-id="element-4791">
            <div className="relative w-32 h-auto mb-6" data-id="element-4792">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_70%)] blur-xl" data-id="element-4793" />
              <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Envelope" className="relative w-full drop-shadow-xl" data-id="element-4794" />
            </div>
            <h4 className="font-serif text-xl text-[#FDFBF7] leading-tight mb-2" data-id="element-4795">
              A welcome gift from the Darbar
            </h4>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light mb-8" data-id="element-4796">
              Your founder packet is ready
            </p>
            <div className="w-full bg-[#8B0000] py-3 text-[#FDFBF7] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-4797">
              Open Now
            </div>
          </div>
        </div>

        <ArrowDown className="lg:hidden w-6 h-6 text-[#D4AF37]/30 my-2 shrink-0" data-id="element-4798" />
        <ArrowRight className="hidden lg:block w-6 h-6 text-[#D4AF37]/30 mx-2 shrink-0" data-id="element-4799" />

        {/* Screen 05 */}
        <div className="w-full max-w-[260px] h-[460px] border border-[#D4AF37]/30 bg-[#131B2B] rounded-2xl p-6 flex flex-col relative shadow-2xl shrink-0 snap-center" data-id="element-4800">
          <div className="mt-auto mb-auto w-full flex flex-col items-center text-center" data-id="element-4801">
            <h4 className="font-serif text-2xl text-[#FDFBF7] leading-tight mb-6" data-id="element-4802">
              You are now Sevadar
            </h4>
            <div className="w-24 aspect-[4/5] border border-[#D4AF37] bg-[#0B0F19] p-1 shadow-[0_0_20px_rgba(212,175,55,0.3)] mb-6" data-id="element-4803">
              <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Card" className="w-full h-full object-cover" data-id="element-4804" />
            </div>
            <p className="text-[#FDFBF7]/50 text-[10px] font-light mb-8" data-id="element-4805">
              Your treasury begins.
            </p>
            <div className="w-full bg-[#D4AF37] py-3 text-[#0B0F19] text-[10px] tracking-[0.2em] uppercase font-medium text-center" data-id="element-4806">
              Enter Dashboard
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ---------- 4.5 Member Dashboard Preview Section ----------
// Image previews mirror the canonical artifacts in the Collections section below.
type DashboardCollection = {
  name: string;
  sub: string;
  rarity: string;
  total: number;
  status: string;
  isFlagship?: boolean;
  sponsoredBy?: string;
  unlockedCards: {
    img: string;
    name: string;
    role?: string;
  }[];
};
const dashboardCollections: DashboardCollection[] = [{
  name: 'Lahore Court Collection',
  sub: 'The Founding Faces · Flagship',
  rarity: "Founders' Edition",
  total: 8,
  status: '5 To Unlock',
  isFlagship: true,
  sponsoredBy: 'Akal Steel',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
    name: 'Maharaja Ranjit Singh',
    role: 'Sher-e-Punjab'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
    name: 'Maharani Jind Kaur',
    role: 'Regent of Punjab'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
    name: 'Hari Singh Nalwa',
    role: 'Commander-in-Chief'
  }]
}, {
  name: "Founders' Seals",
  sub: 'Sovereign Marks',
  rarity: "Founders' Edition",
  total: 6,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/ecLy6LTMHkK3K5wmXXnV8A/Royal_Seal_of_Maharaja_Ranjit_Singh.png',
    name: 'Royal Seal of Ranjit Singh',
    role: 'Sovereign Mohr'
  }]
}, {
  name: 'Court Treasures',
  sub: 'Royal Regalia',
  rarity: "Founders' Edition",
  total: 6,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
    name: 'Koh-i-Noor Armlet',
    role: 'Sovereign Jewel'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
    name: 'Golden Throne of Lahore',
    role: 'Seat of the Sarkar'
  }]
}, {
  name: 'Court Scenes',
  sub: 'Living Tableaux',
  rarity: 'Treasury Rare',
  total: 8,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png',
    name: 'The Lahore Darbar',
    role: 'The Full Court'
  }, {
    img: 'https://cdn.magicpatterns.com/uploads/ndFukn5EhjaGeCm7W2a9F8/Council_of_Empire.png',
    name: 'Council of Empire',
    role: 'War Council'
  }]
}, {
  name: 'Fracture & Memory',
  sub: 'The Empire After the Light',
  rarity: 'Treasury Rare',
  total: 8,
  status: 'In Progress',
  unlockedCards: [{
    img: 'https://cdn.magicpatterns.com/uploads/pUc7dhUDTQ8ibLS8igHkeA/Last_Light_of_Lahore.png',
    name: 'Last Light of Lahore',
    role: 'Memory Fragment'
  }]
}, {
  name: 'Sacred Authority',
  sub: 'Reverence Before Rule',
  rarity: "Founders' Edition",
  total: 6,
  status: 'Locked',
  unlockedCards: []
}, {
  name: 'Frontier Defenders',
  sub: 'Martial Prestige Objects',
  rarity: 'Epic',
  total: 5,
  status: 'Locked',
  unlockedCards: []
}, {
  name: 'Lahore Court Inner Life',
  sub: 'Intimate Spaces',
  rarity: 'Treasury Rare',
  total: 7,
  status: 'Locked',
  unlockedCards: []
}];
const MemberDashboardPreviewSection = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [tradeTab, setTradeTab] = useState<'duplicates' | 'market' | 'incoming'>('incoming');
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed' | 'added'>('sealed');
  // Per-event code-redeem state, keyed by `${eventType}-${eventIndex}`
  const [eventCodes, setEventCodes] = useState<Record<string, string>>({});
  const [redeemedEvents, setRedeemedEvents] = useState<Record<string, boolean>>({});
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [isKnightingOpen, setIsKnightingOpen] = useState(false);
  const [knightedAvatar, setKnightedAvatar] = useState<string | null>(null);
  const [mislView, setMislView] = useState<'home' | 'create'>('home');
  useEffect(() => {
    const handleOpen = () => setIsKnightingOpen(true);
    window.addEventListener('open-knighting-ceremony', handleOpen);
    return () => window.removeEventListener('open-knighting-ceremony', handleOpen);
  }, []);
  const tabs = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: Crown
  }, {
    id: 'daily-packet',
    label: 'Daily Packet',
    icon: Gift,
    badge: 'dot'
  }, {
    id: 'collections',
    label: 'Collections',
    icon: BookOpen
  }, {
    id: 'trade-inbox',
    label: 'Trade Inbox',
    icon: Repeat,
    badge: '2'
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: Trophy
  }, {
    id: 'my-misl',
    label: 'My Clan',
    icon: Shield
  }, {
    id: 'live-events',
    label: 'Live Events',
    icon: Calendar
  }, {
    id: 'referrals',
    label: 'Referrals',
    icon: Users
  }, {
    id: 'profile',
    label: 'Profile',
    icon: User
  }];
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <>
            {/* Header — Welcome row */}
            <div className="flex items-center gap-4" data-id="element-4807">
              {knightedAvatar ? <button type="button" onClick={() => setActiveTab('profile')} className="relative shrink-0 group" aria-label="View your knighted portrait" data-id="element-4808">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-[#D4AF37] overflow-hidden shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-4809">
                    <img src={knightedAvatar} alt="Your knighted portrait" className="w-full h-full object-cover" data-id="element-4810" />
                  </div>
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#D4AF37] border-2 border-[#0B0F19] flex items-center justify-center" data-id="element-4811">
                    <Shield className="w-2.5 h-2.5 text-[#0B0F19]" data-id="element-4812" />
                  </span>
                </button> : <button type="button" onClick={() => setIsKnightingOpen(true)} className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] bg-[#131B2B]/50 flex items-center justify-center shrink-0 transition-colors group" aria-label="Begin your knighting ceremony" data-id="element-4813">
                  <Shield className="w-5 h-5 text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-4814" />
                </button>}
              <div className="flex flex-col gap-1 min-w-0" data-id="element-4815">
                <div className="flex items-center gap-3 text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-4816">
                  <span data-id="element-4817">Court Dashboard</span>
                  <span className="text-[#D4AF37]/25" data-id="element-4818">·</span>
                  <span data-id="element-4819">Day 47</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-4820">
                  Welcome back, Harnoor
                </h3>
              </div>
            </div>

            {/* Virtual Knighting Ceremony CTA */}
            <div className="relative w-full border border-[#D4AF37] bg-gradient-to-r from-[#0B0F19] via-[#131B2B] to-[#0B0F19] p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden group" data-id="element-4821">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_70%)] pointer-events-none" data-id="element-4822" />
              <div className="relative z-10 flex items-center gap-6" data-id="element-4823">
                <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-4824">
                  <Shield className="w-8 h-8 text-[#D4AF37]" data-id="element-4825" />
                </div>
                <div data-id="element-4826">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4827">
                    <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-4828" />
                    <span className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-4829">
                      Optional Rite · +2,500 XP
                    </span>
                  </div>
                  <h4 className="font-serif text-2xl text-[#FDFBF7] mb-1" data-id="element-4830">
                    Begin Your Knighting Ceremony
                  </h4>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-4831">
                    Take the oath and receive your official Founders' Edition
                    portrait.
                  </p>
                </div>
              </div>
              <button onClick={() => {
              // We will add state for this modal
              const event = new CustomEvent('open-knighting-ceremony');
              window.dispatchEvent(event);
            }} className="relative z-10 shrink-0 px-8 py-4 bg-[#D4AF37] text-[#0B0F19] font-bold uppercase tracking-widest text-sm hover:bg-[#E5C158] transition-colors shadow-xl" data-id="element-4832">
                Start Ceremony
              </button>
            </div>

            {/* Full-width Court Standing strip — proper dashboard KPI bar */}
            <div className="relative w-full border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B] to-[#0B0F19] mt-6" data-id="element-4833">
              {/* Gold seam top */}
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-4834" />

              {/* Eyebrow strip */}
              <div className="px-4 sm:px-5 pt-2.5 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-4835">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-4836">
                  <Shield className="w-2.5 h-2.5" data-id="element-4837" />
                  Court Standing
                </p>
                <p className="text-[#D4AF37]/40 text-[8px] tracking-[0.25em] uppercase hidden xs:block" data-id="element-4838">
                  Live · Updated every drop
                </p>
              </div>

              {/* KPI row */}
              <div className="flex flex-col lg:flex-row items-stretch" data-id="element-4839">
                {/* Rank ladder — takes most space */}
                <div className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 flex-1 min-w-0" data-id="element-4840">
                  {/* Current */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0 w-[70px] sm:w-[80px]" data-id="element-4841">
                    <div className="relative w-10 h-10 sm:w-11 sm:h-11 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.35)]" data-id="element-4842">
                      <HonourGlyph type="gold-medallion" className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" data-id="element-4843" />
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#D4AF37] rounded-full shadow-[0_0_8px_rgba(212,175,55,0.8)]" data-id="element-4844" />
                    </div>
                    <span className="font-serif text-[10px] sm:text-[11px] text-[#FDFBF7] leading-tight text-center" data-id="element-4845">
                      Gold Medallion
                    </span>
                    <span className="text-[#D4AF37]/50 text-[7px] tracking-[0.25em] uppercase" data-id="element-4846">
                      You
                    </span>
                  </div>

                  {/* Progress track */}
                  <div className="flex-1 flex flex-col justify-center min-w-[50px]" data-id="element-4847">
                    <div className="flex items-center justify-between text-[7px] sm:text-[8px] tracking-[0.25em] uppercase mb-1.5" data-id="element-4848">
                      <span className="text-[#FDFBF7]/40" data-id="element-4849">Rising</span>
                      <span className="text-[#D4AF37] font-mono" data-id="element-4850">
                        4,150 / 5k
                      </span>
                    </div>
                    <div className="relative w-full h-1 bg-[#0B0F19] overflow-hidden" data-id="element-4851">
                      <div className="absolute inset-y-0 left-0 w-[83%] bg-gradient-to-r from-[#D4AF37] via-[#E5C158] to-[#D4AF37]" data-id="element-4852" />
                      <div className="absolute top-1/2 left-[83%] -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#FDFBF7] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-4853" />
                    </div>
                    <span className="text-[#FDFBF7]/40 text-[7px] sm:text-[8px] tracking-widest uppercase mt-1.5 text-center" data-id="element-4854">
                      850 XP to next
                    </span>
                  </div>

                  {/* Next */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0 w-[70px] sm:w-[80px] opacity-60" data-id="element-4855">
                    <div className="relative w-10 h-10 sm:w-11 sm:h-11 border border-dashed border-[#D4AF37]/40 bg-[#0B0F19]/60 flex items-center justify-center" data-id="element-4856">
                      <HonourGlyph type="order-of-rs" className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]/60" data-id="element-4857" />
                    </div>
                    <span className="font-serif text-[10px] sm:text-[11px] text-[#FDFBF7]/70 leading-tight text-center" data-id="element-4858">
                      Order of RS
                    </span>
                    <span className="text-[#D4AF37]/40 text-[7px] tracking-[0.25em] uppercase" data-id="element-4859">
                      Next
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:block w-px bg-[#D4AF37]/15 my-4" data-id="element-4860" />
                <div className="lg:hidden h-px bg-[#D4AF37]/15 mx-4" data-id="element-4861" />

                {/* Stats: XP / Streak / Sets */}
                <div className="grid grid-cols-3 lg:flex lg:items-stretch" data-id="element-4862">
                  {/* XP */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px]" data-id="element-4863">
                    <div className="flex items-center gap-1 mb-1" data-id="element-4864">
                      <Sparkles className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-4865" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-4866">
                        XP
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-4867">
                      4,150
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-4868">
                      Total
                    </span>
                  </div>

                  {/* Streak */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px] border-l border-[#D4AF37]/15" data-id="element-4869">
                    <div className="flex items-center gap-1 mb-1" data-id="element-4870">
                      <Calendar className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-4871" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-4872">
                        Streak
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-4873">
                      47
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-4874">
                      Days
                    </span>
                  </div>

                  {/* Sets */}
                  <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 min-w-[80px] sm:min-w-[90px] border-l border-[#D4AF37]/15" data-id="element-4875">
                    <div className="flex items-center gap-1 mb-1" data-id="element-4876">
                      <BookOpen className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-4877" />
                      <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-4878">
                        Sets
                      </span>
                    </div>
                    <span className="text-[#D4AF37] font-serif text-xl sm:text-2xl leading-none" data-id="element-4879">
                      4<span className="text-[#FDFBF7]/30 text-base" data-id="element-4880">/7</span>
                    </span>
                    <span className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase mt-1.5" data-id="element-4881">
                      Complete
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* HERO · Today's Packet — full-width, atmospheric, clickable */}
            <button type="button" onClick={() => setActiveTab('daily-packet')} className="group relative w-full text-left border border-[#D4AF37]/30 hover:border-[#D4AF37]/70 bg-gradient-to-br from-[#131B2B] via-[#0B0F19] to-[#0B0F19] transition-colors overflow-hidden" data-id="element-4882">
              {/* Atmospheric glow */}
              <div className="absolute left-0 top-0 bottom-0 w-1/2 bg-[radial-gradient(ellipse_at_left,_rgba(212,175,55,0.18),_transparent_65%)] pointer-events-none" data-id="element-4883" />
              <div className="absolute -left-20 -bottom-20 w-72 h-72 bg-[radial-gradient(circle,_rgba(139,0,0,0.18),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-4884" />
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-4885" />

              <div className="relative flex flex-col xl:flex-row items-center xl:items-center gap-6 xl:gap-10 p-5 sm:p-8 xl:p-10" data-id="element-4886">
                {/* Envelope + text row (always horizontal once room allows) */}
                <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8 flex-1 w-full" data-id="element-4887">
                  <div className="relative w-28 sm:w-40 xl:w-48 shrink-0" data-id="element-4888">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl group-hover:opacity-110" data-id="element-4889" />
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope from the Darbar" className="relative w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-1" data-id="element-4890" />
                  </div>

                  <div className="flex-1 min-w-0 text-center sm:text-left" data-id="element-4891">
                    <p className="text-[#8B0000] text-[9px] sm:text-[10px] uppercase tracking-[0.35em] font-bold mb-2 sm:mb-3" data-id="element-4892">
                      Today's Packet · Unopened
                    </p>
                    <h4 className="font-serif text-lg sm:text-2xl xl:text-3xl text-[#FDFBF7] mb-2 sm:mb-3 leading-tight" data-id="element-4893">
                      A sealed envelope from the Darbar awaits.
                    </h4>
                    <p className="text-[#FDFBF7]/50 text-[10px] sm:text-xs font-light tracking-wider flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1" data-id="element-4894">
                      <Calendar className="w-3 h-3 text-[#D4AF37]/60" data-id="element-4895" />
                      <span data-id="element-4896">Resets in</span>
                      <span className="text-[#D4AF37] font-mono tracking-wider" data-id="element-4897">
                        04:12:38
                      </span>
                    </p>
                  </div>
                </div>

                <div className="shrink-0 w-full xl:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[#D4AF37] group-hover:bg-[#E5C158] text-[#0B0F19] text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-bold transition-colors flex items-center justify-center gap-2 shadow-xl" data-id="element-4898">
                  <Sparkles className="w-3.5 h-3.5" data-id="element-4899" /> Open Packet
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" data-id="element-4900" />
                </div>
              </div>
            </button>

            {/* CENTERPIECE · What I Can Unlock Next — full width */}
            <button type="button" onClick={() => {
            setActiveTab('collections');
            setSelectedCollection('Lahore Court Collection');
          }} className="group w-full text-left bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 flex flex-col transition-colors" data-id="element-4901">
              <div className="flex items-start justify-between gap-4 mb-4" data-id="element-4902">
                <div data-id="element-4903">
                  <p className="text-[#D4AF37]/60 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-4904">
                    What I Can Unlock Next
                  </p>
                  <h4 className="font-serif text-xl sm:text-3xl text-[#FDFBF7] leading-tight group-hover:text-[#D4AF37] transition-colors" data-id="element-4905">
                    The Founder Seal
                  </h4>
                </div>
                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-[#8B0000] font-bold whitespace-nowrap bg-[#8B0000]/10 border border-[#8B0000]/30 px-2 sm:px-3 py-1 sm:py-1.5" data-id="element-4906">
                  1 To Go
                </span>
              </div>

              <p className="text-[#FDFBF7]/60 text-xs sm:text-sm font-light leading-relaxed mb-6 sm:mb-8 max-w-2xl" data-id="element-4907">
                Complete the Royal Court Collection to claim your{' '}
                <span className="text-[#D4AF37]" data-id="element-4908">Founder Seal</span> — a
                permanent identity badge — plus 500 XP.
              </p>

              {/* Big 5-card slot grid — full width, lots of breathing room */}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 mb-6 sm:mb-8" data-id="element-4909">
                {[{
                name: 'Ranjit Singh',
                img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
                isRarest: true
              }, {
                name: 'Jind Kaur',
                img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png'
              }, {
                name: 'Hari Singh Nalwa',
                img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png'
              }, {
                name: 'Akali Phula Singh',
                missing: true
              }, {
                name: 'Moran Sarkar',
                img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png'
              }].map((slot, i) => <div key={i} className={`relative aspect-[3/4] overflow-hidden ${slot.missing ? 'border-2 border-dashed border-[#8B0000]/60 bg-[#131B2B]/60' : slot.isRarest ? 'border-2 border-[#D4AF37] shadow-[0_0_25px_rgba(212,175,55,0.3)]' : 'border border-[#D4AF37]/30'}`} data-id="element-4910">
                    {slot.missing ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 sm:gap-2 p-1 sm:p-2 text-center" data-id="element-4911">
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#8B0000]/20 border border-[#8B0000]/50 flex items-center justify-center mb-0.5 sm:mb-1" data-id="element-4912">
                          <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-[#8B0000]" data-id="element-4913" />
                        </div>
                        <p className="text-[#8B0000] text-[7px] sm:text-[9px] uppercase tracking-widest font-bold" data-id="element-4914">
                          Missing
                        </p>
                        <p className="text-[#FDFBF7]/50 text-[7px] sm:text-[9px] font-light leading-snug px-0.5" data-id="element-4915">
                          {slot.name}
                        </p>
                      </div> : <>
                        <img src={slot.img} alt={slot.name} className="w-full h-full object-cover" data-id="element-4916" />
                        {slot.isRarest && <div className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 px-1 sm:px-1.5 py-0.5 bg-[#D4AF37] text-[#0B0F19] text-[6px] sm:text-[8px] uppercase tracking-widest font-bold flex items-center gap-0.5 sm:gap-1" data-id="element-4917">
                            <Crown className="w-2 sm:w-2.5 h-2 sm:h-2.5" data-id="element-4918" />{' '}
                            Rarest
                          </div>}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/80 to-transparent px-1.5 py-1 sm:px-2 sm:py-1.5" data-id="element-4919">
                          <p className="text-[7px] sm:text-[9px] uppercase tracking-widest text-[#FDFBF7]/90 truncate font-medium" data-id="element-4920">
                            {slot.name}
                          </p>
                        </div>
                      </>}
                  </div>)}
              </div>

              {/* Progress + Find a Trade footer */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-5 pt-5 sm:pt-6 border-t border-[#D4AF37]/15 mt-auto" data-id="element-4921">
                <div className="flex-1 min-w-0" data-id="element-4922">
                  <div className="flex justify-between text-[8px] sm:text-[9px] uppercase tracking-widest text-[#FDFBF7]/50 mb-2" data-id="element-4923">
                    <span data-id="element-4924">Progress · 4 / 5</span>
                    <span className="text-[#D4AF37] font-bold" data-id="element-4925">80%</span>
                  </div>
                  <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden" data-id="element-4926">
                    <div className="w-[80%] h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-4927" />
                  </div>
                </div>
                <span role="button" onClick={e => {
                e.stopPropagation();
                setActiveTab('trade-inbox');
                setTradeTab('market');
              }} className="px-5 py-2.5 sm:px-6 sm:py-3 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors whitespace-nowrap cursor-pointer" data-id="element-4928">
                  <Repeat className="w-3 h-3" data-id="element-4929" /> Find Trade
                  <ArrowRight className="w-3 h-3" data-id="element-4930" />
                </span>
              </div>
            </button>

            {/* My Misl — full-width prominent card */}
            <div className="relative bg-gradient-to-br from-[#131B2B] via-[#0B0F19] to-[#0B0F19] border border-[#D4AF37]/30 overflow-hidden" data-id="element-4931">
              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-4932" />
              <div className="absolute left-0 top-0 bottom-0 w-1/3 bg-[radial-gradient(ellipse_at_left,_rgba(139,0,0,0.15),_transparent_65%)] pointer-events-none" data-id="element-4933" />

              <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-4934">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-4935">
                  <Users className="w-2.5 h-2.5" data-id="element-4936" /> My Clan (Misl)
                </p>
                <button type="button" onClick={() => setActiveTab('referrals')} className="text-[#D4AF37] hover:text-[#E5C158] text-[8px] tracking-[0.3em] uppercase font-bold flex items-center gap-1.5" data-id="element-4937">
                  Invite to Clan <ArrowRight className="w-2.5 h-2.5" data-id="element-4938" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-px bg-[#D4AF37]/10" data-id="element-4939">
                {/* Left: Misl identity + your rank in it */}
                <div className="p-5 sm:p-6 bg-[#0B0F19] flex flex-col gap-4" data-id="element-4940">
                  <div className="flex items-center gap-4" data-id="element-4941">
                    <div className="relative w-14 h-14 border border-[#D4AF37] bg-[#131B2B] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)] shrink-0" data-id="element-4942">
                      <Crown className="w-6 h-6 text-[#D4AF37]" data-id="element-4943" />
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#8B0000] text-[#FDFBF7] text-[7px] tracking-[0.2em] uppercase font-bold whitespace-nowrap" data-id="element-4944">
                        #3 Globally
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-4945">
                      <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase mb-0.5" data-id="element-4946">
                        Sukerchakia Misl
                      </p>
                      <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight" data-id="element-4947">
                        Led by <span className="text-[#D4AF37]" data-id="element-4948">@ranjit_s</span>
                      </h4>
                      <p className="text-[#FDFBF7]/40 text-[10px] mt-1" data-id="element-4949">
                        1,520 members · 480k XP total · Founded Day 1
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-3 border-t border-[#D4AF37]/10" data-id="element-4950">
                    <div data-id="element-4951">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-4952">
                        Your Place
                      </p>
                      <p className="font-serif text-xl text-[#FDFBF7]" data-id="element-4953">
                        #47
                        <span className="text-[#FDFBF7]/30 text-sm" data-id="element-4954">
                          {' '}
                          / 1,520
                        </span>
                      </p>
                    </div>
                    <div data-id="element-4955">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-4956">
                        Clan Rank
                      </p>
                      <p className="font-serif text-xl text-[#D4AF37]" data-id="element-4957">#3</p>
                      <p className="text-[#FDFBF7]/35 text-[8px] uppercase tracking-widest mt-0.5" data-id="element-4958">
                        of 20 clans
                      </p>
                    </div>
                    <div data-id="element-4959">
                      <p className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase mb-1" data-id="element-4960">
                        Climbing
                      </p>
                      <p className="font-serif text-xl text-emerald-400 flex items-center gap-1" data-id="element-4961">
                        +12
                        <ArrowUp className="w-3.5 h-3.5" data-id="element-4962" />
                      </p>
                      <p className="text-[#FDFBF7]/35 text-[8px] uppercase tracking-widest mt-0.5" data-id="element-4963">
                        this week
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right: Mildars of the Month leaderboard */}
                <div className="p-5 sm:p-6 bg-[#0B0F19] flex flex-col gap-3" data-id="element-4964">
                  <div className="flex items-center justify-between" data-id="element-4965">
                    <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase flex items-center gap-1.5" data-id="element-4966">
                      <Trophy className="w-3 h-3" data-id="element-4967" /> Mildars of the Month
                    </p>
                    <p className="text-[#FDFBF7]/35 text-[8px] tracking-widest uppercase" data-id="element-4968">
                      Longest reigning
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5" data-id="element-4969">
                    {[{
                    rank: 1,
                    name: '@maharaja',
                    misl: 'Lahore Darbar',
                    days: 38,
                    color: 'text-[#D4AF37]',
                    bg: 'bg-[#D4AF37]/10 border-[#D4AF37]/40'
                  }, {
                    rank: 2,
                    name: '@ranjit_s',
                    misl: 'Sukerchakia',
                    days: 24,
                    color: 'text-[#FDFBF7]/80',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15',
                    isYou: true
                  }, {
                    rank: 3,
                    name: '@jassa_singh',
                    misl: 'Ahluwalia',
                    days: 19,
                    color: 'text-[#FDFBF7]/80',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15'
                  }, {
                    rank: 4,
                    name: '@khalsa_s',
                    misl: 'Sarkar-e-Khalsa',
                    days: 15,
                    color: 'text-[#FDFBF7]/60',
                    bg: 'bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15'
                  }].map(m => <div key={m.rank} className={`flex items-center gap-2.5 px-2.5 py-2 border ${m.bg} text-left`} data-id="element-4970">
                        <span className={`font-serif text-base w-5 text-center ${m.color}`} data-id="element-4971">
                          {m.rank}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-4972">
                          <p className="text-[#FDFBF7] text-[11px] font-medium truncate" data-id="element-4973">
                            {m.name}
                            {m.isYou && <span className="ml-1.5 text-[#D4AF37] text-[8px] tracking-[0.2em] uppercase" data-id="element-4974">
                                · Your Clan
                              </span>}
                          </p>
                          <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest truncate" data-id="element-4975">
                            {m.misl}
                          </p>
                        </div>
                        <span className="text-[#D4AF37] text-[10px] font-mono whitespace-nowrap" data-id="element-4976">
                          {m.days}d
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Take Action · XP Quests — what to do to earn */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/25 overflow-hidden" data-id="element-4977">
              <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-[#D4AF37]/15 flex items-center justify-between" data-id="element-4978">
                <p className="text-[#D4AF37]/60 text-[8px] tracking-[0.35em] uppercase flex items-center gap-2" data-id="element-4979">
                  <Zap className="w-2.5 h-2.5" data-id="element-4980" /> Earn XP · Take Action
                </p>
                <p className="text-[#FDFBF7]/35 text-[8px] tracking-[0.25em] uppercase" data-id="element-4981">
                  4 active quests
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#D4AF37]/10" data-id="element-4982">
                {[{
                icon: UserPlus,
                label: 'Invite a friend',
                xp: '+500 XP',
                sub: 'Each qualified referral',
                cta: 'Invite',
                tab: 'referrals',
                accent: 'text-[#D4AF37]'
              }, {
                icon: Repeat,
                label: 'Make a trade',
                xp: '+150 XP',
                sub: '2 incoming offers',
                cta: 'View Inbox',
                tab: 'trade-inbox',
                accent: 'text-[#D4AF37]',
                badge: '2'
              }, {
                icon: Calendar,
                label: 'Add Treasury Night',
                xp: '+200 XP',
                sub: 'RSVP to Jul 18 event',
                cta: 'Add to Calendar',
                tab: 'live-events',
                accent: 'text-[#8B0000]'
              }, {
                icon: Gift,
                label: 'Open daily packet',
                xp: '+50 XP',
                sub: 'Resets in 04:12:38',
                cta: 'Open Now',
                tab: 'daily-packet',
                accent: 'text-[#D4AF37]'
              }].map(q => {
                const Icon = q.icon;
                return <button key={q.label} type="button" onClick={() => setActiveTab(q.tab)} className="group flex flex-col items-start gap-2 p-4 hover:bg-[#D4AF37]/[0.04] transition-colors text-left" data-id="element-4983">
                      <div className="flex items-center justify-between w-full" data-id="element-4984">
                        <div className="w-8 h-8 border border-[#D4AF37]/30 bg-[#131B2B] flex items-center justify-center group-hover:border-[#D4AF37] transition-colors relative" data-id="element-4985">
                          <Icon className={`w-4 h-4 ${q.accent} opacity-80 group-hover:opacity-100 transition-opacity`} data-id="element-4986" />
                          {q.badge && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#8B0000] text-[#FDFBF7] text-[8px] flex items-center justify-center rounded-full font-bold" data-id="element-4987">
                              {q.badge}
                            </span>}
                        </div>
                        <span className={`text-[10px] font-bold ${q.accent}`} data-id="element-4988">
                          {q.xp}
                        </span>
                      </div>
                      <p className="text-[#FDFBF7] text-xs font-medium leading-tight" data-id="element-4989">
                        {q.label}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] leading-snug" data-id="element-4990">
                        {q.sub}
                      </p>
                      <span className="mt-1 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-4991">
                        {q.cta}
                        <ArrowRight className="w-3 h-3" data-id="element-4992" />
                      </span>
                    </button>;
              })}
              </div>
            </div>

            {/* Bottom 2-up: My Rank + Next Treasury Night */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-4993">
              {/* My Rank */}
              <button type="button" onClick={() => setActiveTab('profile')} className="group bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 flex flex-col text-left transition-colors" data-id="element-4994">
                <div className="flex items-center justify-between mb-5" data-id="element-4995">
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-4996">
                    My Rank
                  </p>
                  <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37]/70 transition-all -translate-x-2 group-hover:translate-x-0" data-id="element-4997" />
                </div>
                <div className="flex items-center gap-4 mb-6" data-id="element-4998">
                  <div className="w-12 h-12 border border-[#D4AF37] bg-[#131B2B] flex items-center justify-center shadow-[0_0_15px_rgba(212,175,55,0.2)]" data-id="element-4999">
                    <Sword className="w-6 h-6 text-[#D4AF37]" data-id="element-5000" />
                  </div>
                  <div data-id="element-5001">
                    <p className="font-serif text-2xl text-[#FDFBF7] leading-none" data-id="element-5002">
                      Sardar
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[10px] mt-1.5 uppercase tracking-widest" data-id="element-5003">
                      4,150 XP
                    </p>
                  </div>
                </div>
                <div className="mt-auto" data-id="element-5004">
                  <div className="flex justify-between text-[9px] uppercase tracking-widest text-[#FDFBF7]/50 mb-1.5" data-id="element-5005">
                    <span data-id="element-5006">Progress to Noble</span>
                    <span className="text-[#D4AF37]" data-id="element-5007">83%</span>
                  </div>
                  <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden" data-id="element-5008">
                    <div className="w-[83%] h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-5009" />
                  </div>
                  <p className="text-[#FDFBF7]/40 text-[10px] font-light mt-2" data-id="element-5010">
                    850 XP to next rank
                  </p>
                </div>
              </button>

              {/* Next Treasury Night */}
              <button type="button" onClick={() => setActiveTab('live-events')} className="group bg-[#0B0F19] border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 p-5 sm:p-8 relative overflow-hidden text-left transition-colors" data-id="element-5011">
                <div className="absolute top-0 inset-x-0 h-0.5 bg-[#8B0000]" data-id="element-5012" />
                <div className="flex items-center justify-between mb-5" data-id="element-5013">
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5014">
                    Next Treasury Night
                  </p>
                  <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37]/70 transition-all -translate-x-2 group-hover:translate-x-0" data-id="element-5015" />
                </div>
                <div className="flex items-start gap-4" data-id="element-5016">
                  <div className="flex flex-col items-center px-4 py-3 border border-[#8B0000]/40 bg-[#131B2B] shrink-0" data-id="element-5017">
                    <span className="text-[#8B0000] text-[10px] tracking-[0.2em] uppercase font-bold" data-id="element-5018">
                      JUL
                    </span>
                    <span className="font-serif text-3xl text-[#FDFBF7] leading-none my-1" data-id="element-5019">
                      18
                    </span>
                    <span className="text-[#D4AF37]/60 text-[9px] tracking-widest uppercase" data-id="element-5020">
                      7:00 PM
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5021">
                    <p className="font-serif text-lg text-[#FDFBF7] leading-snug mb-2" data-id="element-5022">
                      The Court Awakens
                    </p>
                    <p className="text-[#FDFBF7]/50 text-[11px] font-light flex items-center gap-1.5 mb-3" data-id="element-5023">
                      <MapPin className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5024" />{' '}
                      Illuminarium · Toronto
                    </p>
                    <div className="flex items-center gap-2" data-id="element-5025">
                      <span className="px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-widest font-bold" data-id="element-5026">
                        +200 XP RSVP
                      </span>
                      <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold" data-id="element-5027">
                        In 12 days
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </>;
      case 'daily-packet':
        return <div className="flex-1 flex flex-col items-center justify-center text-center min-h-[560px] relative overflow-hidden py-8" data-id="element-5028">
            {/* Ambient atmospheric background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-5029" />

            <AnimatePresence mode="wait" data-id="element-5030">
              {/* ============ SEALED ============ */}
              {packetState === 'sealed' && <motion.div key="sealed" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.5
            }} className="flex flex-col items-center text-center max-w-md relative z-10" data-id="element-5031">
                  <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-56 mb-10" data-id="element-5032">
                    <motion.div animate={{
                  opacity: [0.4, 0.7, 0.4]
                }} transition={{
                  duration: 2.8,
                  repeat: Infinity
                }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-5033" />
                    <motion.button type="button" onClick={() => {
                  setPacketState('opening');
                  setTimeout(() => setPacketState('revealed'), 1900);
                }} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.96
                }} aria-label="Break the seal and open your daily packet" className="relative block w-full cursor-pointer" data-id="element-5034">
                      <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-5035" />
                      <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-5036">
                        Click to open
                      </span>
                    </motion.button>
                  </motion.div>
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5037">
                    Day 47 · The Darbar
                  </p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5038">
                    Your Daily Packet has Arrived
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm font-light mb-8 leading-relaxed" data-id="element-5039">
                    A sealed envelope from the Maharaja's court awaits. Break
                    the wax — discover the artifact within.
                  </p>
                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => {
                setPacketState('opening');
                setTimeout(() => setPacketState('revealed'), 1900);
              }} className="px-10 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-5040">
                    <Sparkles className="w-4 h-4" data-id="element-5041" /> Break the Seal
                  </motion.button>
                </motion.div>}

              {/* ============ OPENING ============ */}
              {packetState === 'opening' && <motion.div key="opening" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="relative flex items-center justify-center w-full h-full min-h-[560px]" data-id="element-5042">
                  {/* Expanding gold burst */}
                  <motion.div initial={{
                scale: 0.4,
                opacity: 0
              }} animate={{
                scale: 4,
                opacity: [0, 0.9, 0]
              }} transition={{
                duration: 1.8,
                ease: 'easeOut'
              }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-5043" />

                  {/* Shaking + shrinking envelope */}
                  <motion.div initial={{
                scale: 1,
                opacity: 1
              }} animate={{
                scale: [1, 1.1, 0.6],
                opacity: [1, 1, 0],
                rotateZ: [0, -3, 4, -2, 0]
              }} transition={{
                duration: 1.8,
                times: [0, 0.5, 1],
                ease: 'easeInOut'
              }} className="relative w-64 sm:w-72 z-10" data-id="element-5044">
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-5045" />
                  </motion.div>

                  {/* Radial sparkle particles */}
                  {Array.from({
                length: 20
              }).map((_, i) => {
                const angle = i / 20 * Math.PI * 2;
                const distance = 280;
                return <motion.div key={i} initial={{
                  x: 0,
                  y: 0,
                  opacity: 0,
                  scale: 0
                }} animate={{
                  x: Math.cos(angle) * distance,
                  y: Math.sin(angle) * distance,
                  opacity: [0, 1, 0],
                  scale: [0, 1.2, 0]
                }} transition={{
                  duration: 1.6,
                  delay: 0.3 + i % 4 * 0.05,
                  ease: 'easeOut'
                }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-5046" />;
              })}

                  {/* Caption */}
                  <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: [0, 1, 1, 0],
                y: 0
              }} transition={{
                duration: 1.8,
                times: [0, 0.3, 0.7, 1]
              }} className="absolute bottom-16 font-serif text-xl text-[#D4AF37] tracking-[0.35em] uppercase" data-id="element-5047">
                    The seal breaks…
                  </motion.p>
                </motion.div>}

              {/* ============ REVEALED ============ */}
              {packetState === 'revealed' && <motion.div key="revealed" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              y: -30
            }} transition={{
              duration: 0.6
            }} className="flex flex-col items-center text-center max-w-2xl mx-auto" data-id="element-5048">
                  <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4
              }} className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4" data-id="element-5049">
                    A Treasure from the Court
                  </motion.p>

                  <motion.div initial={{
                y: 200,
                scale: 0.5,
                rotateY: 90,
                opacity: 0
              }} animate={{
                y: 0,
                scale: 1,
                rotateY: 0,
                opacity: 1
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} className="relative mb-6" style={{
                perspective: 1000
              }} data-id="element-5050">
                    {/* Gold glow halo */}
                    <motion.div animate={{
                  opacity: [0.4, 0.75, 0.4]
                }} transition={{
                  duration: 3,
                  repeat: Infinity
                }} className="absolute -inset-10 bg-[radial-gradient(circle,_rgba(212,175,55,0.4),_transparent_60%)] blur-2xl" data-id="element-5051" />

                    <motion.div animate={{
                  y: [0, -8, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative w-64 sm:w-72" data-id="element-5052">
                      <div className="border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden bg-[#0B0F19]" data-id="element-5053">
                        <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5054" />
                      </div>
                      <div className="absolute -top-3 -right-3 px-3 py-1 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold shadow-lg" data-id="element-5055">
                        Treasury Rare
                      </div>
                    </motion.div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.9,
                duration: 0.6
              }} className="text-center" data-id="element-5056">
                    <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-5057">
                      Moran Sarkar
                    </h3>
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5058">
                      Royal Court Collection · Treasury Rare
                    </p>
                    <p className="text-[#FDFBF7]/60 text-sm font-light italic max-w-md mx-auto mb-8 leading-relaxed" data-id="element-5059">
                      "A dancer who became consort to the Sher-e-Punjab — beauty
                      and intellect at the heart of the Darbar."
                    </p>

                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setPacketState('added')} className="px-10 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-xs tracking-[0.3em] uppercase font-bold shadow-2xl transition-colors flex items-center gap-3 mx-auto" data-id="element-5060">
                      <BookOpen className="w-4 h-4" data-id="element-5061" /> Add to Royal Court
                    </motion.button>
                  </motion.div>
                </motion.div>}

              {/* ============ ADDED ============ */}
              {packetState === 'added' && <motion.div key="added" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-5062">
                  {/* Flying card animation */}
                  <motion.div initial={{
                x: 0,
                y: 0,
                scale: 1,
                opacity: 1
              }} animate={{
                x: -260,
                y: -180,
                scale: 0.18,
                opacity: 0,
                rotate: -15
              }} transition={{
                duration: 1.1,
                ease: [0.7, 0, 0.84, 0]
              }} className="relative w-44 mb-8 absolute" data-id="element-5063">
                    <div className="border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.6)] overflow-hidden" data-id="element-5064">
                      <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5065" />
                    </div>
                  </motion.div>

                  {/* Sparkle trail */}
                  {Array.from({
                length: 10
              }).map((_, i) => <motion.div key={i} initial={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0
              }} animate={{
                x: -260 + Math.random() * 50,
                y: -180 + Math.random() * 50,
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }} transition={{
                duration: 1.3,
                delay: 0.15 + i * 0.06,
                ease: 'easeOut'
              }} className="absolute w-1.5 h-1.5 bg-[#D4AF37] rounded-full shadow-[0_0_8px_rgba(212,175,55,1)]" data-id="element-5066" />)}

                  {/* Success state */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.0,
                duration: 0.6
              }} className="flex flex-col items-center" data-id="element-5067">
                    <div className="w-14 h-14 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-5" data-id="element-5068">
                      <Sparkles className="w-6 h-6 text-[#D4AF37]" data-id="element-5069" />
                    </div>
                    <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5070">
                      Added to your Archive
                    </p>
                    <h3 className="font-serif text-3xl text-[#FDFBF7] mb-3" data-id="element-5071">
                      Moran Sarkar joins your Court
                    </h3>
                    <p className="text-[#FDFBF7]/60 text-sm font-light leading-relaxed mb-2 max-w-sm" data-id="element-5072">
                      Filed under the{' '}
                      <span className="text-[#D4AF37]" data-id="element-5073">
                        Royal Court Collection
                      </span>{' '}
                      · You now own this artifact{' '}
                      <span className="text-[#D4AF37] font-bold" data-id="element-5074">×2</span>.
                    </p>
                    <p className="text-[#FDFBF7]/40 text-xs font-light mb-8" data-id="element-5075">
                      Your duplicate is now tradeable on the Open Market.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-5076">
                      <button onClick={() => {
                    setPacketState('sealed');
                    setActiveTab('collections');
                  }} className="flex-1 px-6 py-3.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold transition-colors flex items-center justify-center gap-2" data-id="element-5077">
                        <BookOpen className="w-3.5 h-3.5" data-id="element-5078" /> View in Collection
                      </button>
                      <button onClick={() => {
                    setPacketState('sealed');
                    setActiveTab('trade-inbox');
                    setTradeTab('duplicates');
                  }} className="flex-1 px-6 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] tracking-[0.3em] uppercase transition-colors flex items-center justify-center gap-2" data-id="element-5079">
                        <Repeat className="w-3.5 h-3.5" data-id="element-5080" /> Post Duplicate
                      </button>
                    </div>

                    <button onClick={() => setPacketState('sealed')} className="mt-6 text-[#FDFBF7]/40 hover:text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase transition-colors" data-id="element-5081">
                      Replay the unboxing
                    </button>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </div>;
      case 'collections':
        if (selectedCollection) {
          return <div className="flex flex-col gap-8" data-id="element-5082">
              <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5083">
                <div data-id="element-5084">
                  <button onClick={() => setSelectedCollection(null)} className="text-[#D4AF37]/60 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest flex items-center gap-2 mb-4 transition-colors" data-id="element-5085">
                    ← Back to Collections
                  </button>
                  <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5086">
                    {selectedCollection}
                  </h3>
                  <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5087">
                    Collection Details
                  </p>
                </div>
                <div className="text-right" data-id="element-5088">
                  <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5089">
                    {(() => {
                    const col = dashboardCollections.find(c => c.name === selectedCollection);
                    if (!col) return '0/0';
                    return `${col.unlockedCards.length}/${col.total}`;
                  })()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-5090">
                {(() => {
                const col = dashboardCollections.find(c => c.name === selectedCollection);
                if (!col) return null;
                return Array.from({
                  length: col.total
                }).map((_, i) => {
                  const card = col.unlockedCards[i];
                  const isUnlocked = !!card;
                  return <div key={i} className={`aspect-[2.5/3.5] border ${isUnlocked ? 'border-[#D4AF37]/50 bg-[#131B2B] shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'border-[#FDFBF7]/10 bg-[#0B0F19]'} relative overflow-hidden group/card`} data-id="element-5091">
                        {isUnlocked ? <>
                            <img src={card.img} alt={card.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-5092" />
                            {/* Dark gradient overlay for legibility */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" data-id="element-5093" />
                            {/* Caption */}
                            <div className="absolute inset-x-0 bottom-0 p-2.5" data-id="element-5094">
                              <p className="font-serif text-[11px] sm:text-xs text-[#FDFBF7] leading-tight line-clamp-2" data-id="element-5095">
                                {card.name}
                              </p>
                              <p className="text-[#D4AF37]/80 text-[8px] uppercase tracking-widest mt-1" data-id="element-5096">
                                Unlocked
                              </p>
                            </div>
                            {/* Founders crown chip top-right */}
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full border border-[#D4AF37]/60 bg-[#0B0F19]/80 backdrop-blur-sm flex items-center justify-center" data-id="element-5097">
                              <Crown className="w-2.5 h-2.5 text-[#D4AF37]" data-id="element-5098" />
                            </div>
                          </> : <div className="absolute inset-0 flex flex-col items-center justify-center p-3" data-id="element-5099">
                            <Lock className="w-6 h-6 text-[#FDFBF7]/20 mb-2" data-id="element-5100" />
                            <p className="text-[#FDFBF7]/30 text-[10px] uppercase tracking-widest" data-id="element-5101">
                              Locked
                            </p>
                          </div>}
                      </div>;
                });
              })()}
              </div>
            </div>;
        }
        return <div className="flex flex-col gap-8" data-id="element-5102">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5103">
              <div data-id="element-5104">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5105">
                  Your Collections
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5106">
                  The Archive
                </p>
              </div>
              <div className="text-right" data-id="element-5107">
                <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5108">
                  12
                  <span className="text-[#FDFBF7]/30 text-lg" data-id="element-5109"> artifacts</span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5110">
              {dashboardCollections.map((col, i) => {
              const unlockedCount = col.unlockedCards.length;
              const pct = unlockedCount / col.total * 100;
              const isLocked = col.status === 'Locked';
              const rarityColor = col.rarity.includes('Founders') ? 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10' : col.rarity === 'Treasury Rare' ? 'text-[#D4AF37]/80 border-[#D4AF37]/30 bg-[#D4AF37]/5' : 'text-[#8B0000] border-[#8B0000]/40 bg-[#8B0000]/10';
              return <div key={i} onClick={() => setSelectedCollection(col.name)} className={`relative bg-[#0B0F19] border ${col.isFlagship ? 'border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.12)]' : 'border-[#D4AF37]/20'} p-6 flex flex-col cursor-pointer hover:border-[#D4AF37]/60 transition-colors group overflow-hidden`} data-id="element-5111">
                    {col.isFlagship && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5112" />}
                    {col.sponsoredBy && <div className="mb-3 -mt-1 flex items-center gap-2" data-id="element-5113">
                        <span className="text-[#D4AF37]/50 text-[8px] tracking-[0.3em] uppercase" data-id="element-5114">
                          Sponsored by
                        </span>
                        <span className="font-serif text-[11px] text-[#D4AF37] leading-none" data-id="element-5115">
                          {col.sponsoredBy}
                        </span>
                      </div>}
                    <div className="flex justify-between items-start mb-3 gap-3" data-id="element-5116">
                      <div className="min-w-0 flex-1" data-id="element-5117">
                        <span className={`inline-block px-2 py-0.5 text-[8px] tracking-[0.25em] uppercase font-bold border ${rarityColor} mb-2`} data-id="element-5118">
                          {col.rarity}
                        </span>
                        <h4 className="font-serif text-xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors leading-tight" data-id="element-5119">
                          {col.name}
                        </h4>
                        <p className="text-[#D4AF37]/50 text-[9px] tracking-[0.25em] uppercase mt-1" data-id="element-5120">
                          {col.sub}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#D4AF37]/0 group-hover:text-[#D4AF37] transition-all transform -translate-x-2 group-hover:translate-x-0 shrink-0 mt-1" data-id="element-5121" />
                    </div>

                    {/* Mini card slot strip with real artifact images for unlocked slots */}
                    <div className="flex gap-1 mb-4" data-id="element-5122">
                      {Array.from({
                    length: col.total
                  }).map((_, idx) => {
                    const card = col.unlockedCards[idx];
                    const unlocked = !!card;
                    return <div key={idx} className={`flex-1 aspect-[2.5/3.5] overflow-hidden ${unlocked ? col.isFlagship ? 'border border-[#D4AF37]/60 shadow-[0_0_8px_rgba(212,175,55,0.25)]' : 'border border-[#D4AF37]/40' : 'bg-[#131B2B] border border-[#FDFBF7]/10 flex items-center justify-center'}`} data-id="element-5123">
                            {unlocked ? <img src={card.img} alt={card.name} className="w-full h-full object-cover" loading="lazy" data-id="element-5124" /> : <Lock className="w-2 h-2 text-[#FDFBF7]/25" data-id="element-5125" />}
                          </div>;
                  })}
                    </div>

                    <div className="flex justify-between text-[10px] uppercase tracking-widest mb-2 mt-auto" data-id="element-5126">
                      <span className="text-[#D4AF37]/80 font-mono" data-id="element-5127">
                        {unlockedCount}/{col.total}
                      </span>
                      <span className={isLocked ? 'text-[#FDFBF7]/30' : col.status.includes('Unlock') ? 'text-[#8B0000] font-bold' : 'text-[#D4AF37]/70'} data-id="element-5128">
                        {col.status}
                      </span>
                    </div>
                    <div className="w-full h-1 bg-[#131B2B] rounded-full overflow-hidden border border-[#D4AF37]/20" data-id="element-5129">
                      <div className="h-full bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" style={{
                    width: `${pct}%`
                  }} data-id="element-5130" />
                    </div>
                  </div>;
            })}
            </div>
          </div>;
      case 'trade-inbox':
        return <div className="flex flex-col gap-8" data-id="element-5131">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5132">
              <div data-id="element-5133">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5134">
                  Trade Inbox
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5135">
                  The Exchange
                </p>
              </div>
              <div className="px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest cursor-pointer hover:bg-[#D4AF37]/20 transition-colors" data-id="element-5136">
                New Trade
              </div>
            </div>

            <div className="flex gap-6 border-b border-[#FDFBF7]/10" data-id="element-5137">
              <button onClick={() => setTradeTab('incoming')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'incoming' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5138">
                Incoming Requests
                <span className="ml-2 bg-[#8B0000] text-[#FDFBF7] px-1.5 py-0.5 rounded-full text-[8px]" data-id="element-5139">
                  2
                </span>
                {tradeTab === 'incoming' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5140" />}
              </button>
              <button onClick={() => setTradeTab('market')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'market' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5141">
                Market
                {tradeTab === 'market' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5142" />}
              </button>
              <button onClick={() => setTradeTab('duplicates')} className={`pb-3 text-[10px] uppercase tracking-widest transition-colors relative ${tradeTab === 'duplicates' ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5143">
                My Duplicates
                {tradeTab === 'duplicates' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" data-id="element-5144" />}
              </button>
            </div>

            <div className="space-y-4" data-id="element-5145">
              {tradeTab === 'incoming' && <>
                  {(() => {
                const cardImg: Record<string, string> = {
                  'Jamrud Fort': 'https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png',
                  'Moran Sarkar': 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
                  'Akali Phula Singh': 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
                  'Hari Singh Nalwa': 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
                  'Maharaja Ranjit Singh': 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
                  'Harmandir Sahib': 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
                  'Valley of Kashmir': 'https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png',
                  'Jind Kaur': 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png'
                };
                const trades = [{
                  user: '@arjun',
                  rank: 'Sardar',
                  offer: 'Jamrud Fort',
                  seek: 'Moran Sarkar',
                  status: 'pending'
                }, {
                  user: '@simran',
                  rank: 'Courtier',
                  offer: 'Akali Phula Singh',
                  seek: 'Hari Singh Nalwa',
                  status: 'pending'
                }, {
                  user: '@karan',
                  rank: 'Noble',
                  offer: 'Maharaja Ranjit Singh',
                  seek: 'Harmandir Sahib',
                  status: 'completed'
                }];
                return trades.map((trade, i) => <div key={i} className={`bg-[#0B0F19] border ${trade.status === 'pending' ? 'border-[#D4AF37]/30' : 'border-[#FDFBF7]/10 opacity-60'} p-5 flex flex-col lg:flex-row lg:items-center gap-5`} data-id="element-5146">
                        <div className="flex items-center gap-3 lg:w-44 shrink-0" data-id="element-5147">
                          <div className="w-9 h-9 rounded-full bg-[#131B2B] border border-[#D4AF37]/30 flex items-center justify-center text-[10px] text-[#D4AF37]" data-id="element-5148">
                            {trade.user.substring(1, 3).toUpperCase()}
                          </div>
                          <div className="min-w-0" data-id="element-5149">
                            <p className="text-sm text-[#D4AF37] font-medium truncate" data-id="element-5150">
                              {trade.user}
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[9px] uppercase tracking-widest" data-id="element-5151">
                              {trade.rank}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-5152">
                          <div className="flex-1 flex items-center gap-3 min-w-0" data-id="element-5153">
                            <img src={cardImg[trade.offer]} alt={trade.offer} className="w-12 h-12 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5154" />
                            <div className="min-w-0" data-id="element-5155">
                              <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5156">
                                Offers
                              </p>
                              <p className="font-serif text-sm text-[#FDFBF7] truncate" data-id="element-5157">
                                {trade.offer}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#D4AF37]/40 shrink-0" data-id="element-5158" />
                          <div className="flex-1 flex items-center gap-3 min-w-0" data-id="element-5159">
                            <img src={cardImg[trade.seek]} alt={trade.seek} className="w-12 h-12 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5160" />
                            <div className="min-w-0" data-id="element-5161">
                              <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5162">
                                Wants
                              </p>
                              <p className="font-serif text-sm text-[#FDFBF7] truncate" data-id="element-5163">
                                {trade.seek}
                              </p>
                            </div>
                          </div>
                        </div>
                        {trade.status === 'pending' ? <div className="flex gap-2 w-full lg:w-auto shrink-0" data-id="element-5164">
                            <button className="flex-1 lg:flex-none px-5 py-2 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 transition-colors" data-id="element-5165">
                              Accept
                            </button>
                            <button className="flex-1 lg:flex-none px-4 py-2 text-[#FDFBF7]/60 text-[10px] uppercase tracking-widest border border-[#FDFBF7]/20 hover:text-[#FDFBF7] hover:border-[#D4AF37] transition-colors" data-id="element-5166">
                              Counter
                            </button>
                            <button className="flex-1 lg:flex-none px-4 py-2 text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest hover:text-[#FDFBF7]/80 transition-colors" data-id="element-5167">
                              Decline
                            </button>
                          </div> : <span className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/30 border border-[#FDFBF7]/10 px-4 py-1.5 shrink-0" data-id="element-5168">
                            Completed
                          </span>}
                      </div>);
              })()}
                </>}

              {tradeTab === 'market' && <>
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-5169">
                    {['All', "Founders' Edition", 'Epic', 'Treasury Rare'].map((chip, i) => <span key={chip} className={`px-3 py-1 text-[9px] uppercase tracking-widest border cursor-pointer transition-colors ${i === 0 ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40' : 'text-[#FDFBF7]/40 border-[#FDFBF7]/10 hover:border-[#D4AF37]/30 hover:text-[#D4AF37]/70'}`} data-id="element-5170">
                          {chip}
                        </span>)}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5171">
                    {[{
                  user: '@neha',
                  offer: 'Valley of Kashmir',
                  img: 'https://cdn.magicpatterns.com/uploads/ac4oQu8QYLHUDnoQhYPvFC/Valley_of_Kashmir.png',
                  rarity: 'Epic',
                  seeking: 'Any Founders Edition',
                  time: '2m ago'
                }, {
                  user: '@vikram',
                  offer: 'Jind Kaur',
                  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
                  rarity: "Founders' Edition",
                  seeking: 'Maharaja Ranjit Singh',
                  time: '1h ago'
                }, {
                  user: '@prabh',
                  offer: 'Harmandir Sahib',
                  img: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
                  rarity: 'Treasury Rare',
                  seeking: 'Hari Singh Nalwa',
                  time: '5h ago'
                }, {
                  user: '@tara',
                  offer: 'Akali Phula Singh',
                  img: 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
                  rarity: 'Epic',
                  seeking: 'Open to offers',
                  time: '3h ago'
                }].map((trade, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-4 flex gap-4 hover:border-[#D4AF37]/40 transition-colors" data-id="element-5172">
                        <img src={trade.img} alt={trade.offer} className="w-20 h-24 object-cover border border-[#D4AF37]/30 shrink-0" data-id="element-5173" />
                        <div className="flex-1 flex flex-col min-w-0" data-id="element-5174">
                          <p className="text-[#D4AF37]/60 text-[9px] uppercase tracking-widest" data-id="element-5175">
                            {trade.rarity}
                          </p>
                          <h4 className="font-serif text-base text-[#FDFBF7] truncate" data-id="element-5176">
                            {trade.offer}
                          </h4>
                          <p className="text-xs text-[#FDFBF7]/50 mt-1 truncate" data-id="element-5177">
                            <span className="text-[#D4AF37]" data-id="element-5178">{trade.user}</span>{' '}
                            · {trade.time}
                          </p>
                          <p className="text-[10px] text-[#FDFBF7]/70 mt-2" data-id="element-5179">
                            <span className="text-[#D4AF37]/60 uppercase tracking-widest text-[9px]" data-id="element-5180">
                              Wants:
                            </span>{' '}
                            {trade.seeking}
                          </p>
                          <div className="flex gap-2 mt-auto pt-3" data-id="element-5181">
                            <button className="flex-1 py-1.5 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors" data-id="element-5182">
                              Make Offer
                            </button>
                            <button className="px-3 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37]/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors" data-id="element-5183">
                              <Heart className="w-3 h-3" data-id="element-5184" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </>}

              {tradeTab === 'duplicates' && <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-5185">
                    {[{
                  name: 'Moran Sarkar',
                  count: 2,
                  collection: 'Royal Court',
                  rarity: 'Treasury Rare',
                  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png'
                }, {
                  name: 'Jamrud Fort',
                  count: 3,
                  collection: 'Frontier / Martial',
                  rarity: 'Epic',
                  img: 'https://cdn.magicpatterns.com/uploads/qPy5nBUtnmTQ5b2tqAUf9h/Jamrud_Fort.png'
                }, {
                  name: 'Maharaja Ranjit Singh',
                  count: 2,
                  collection: 'Royal Court',
                  rarity: "Founders' Edition",
                  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png'
                }].map((dup, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 flex flex-col relative overflow-hidden hover:border-[#D4AF37]/40 transition-colors" data-id="element-5186">
                        <div className="relative aspect-[3/4]" data-id="element-5187">
                          <img src={dup.img} alt={dup.name} className="w-full h-full object-cover" data-id="element-5188" />
                          <div className="absolute top-2 right-2 px-2 py-1 bg-[#8B0000] border border-[#D4AF37] text-[#FDFBF7] text-[10px] uppercase tracking-widest font-bold" data-id="element-5189">
                            ×{dup.count}
                          </div>
                        </div>
                        <div className="p-3 flex flex-col flex-1" data-id="element-5190">
                          <p className="text-[#D4AF37]/60 text-[8px] uppercase tracking-widest" data-id="element-5191">
                            {dup.rarity}
                          </p>
                          <h4 className="font-serif text-sm text-[#FDFBF7] leading-tight mb-1 truncate" data-id="element-5192">
                            {dup.name}
                          </h4>
                          <p className="text-[#FDFBF7]/40 text-[8px] uppercase tracking-widest mb-3" data-id="element-5193">
                            {dup.collection}
                          </p>
                          <div className="space-y-1.5 mt-auto" data-id="element-5194">
                            <button className="w-full py-1.5 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold hover:bg-[#E5C158] transition-colors" data-id="element-5195">
                              Post to Market
                            </button>
                            <button className="w-full py-1.5 border border-[#D4AF37]/30 text-[#D4AF37]/80 text-[9px] uppercase tracking-widest hover:bg-[#D4AF37]/10 transition-colors" data-id="element-5196">
                              Direct Trade
                            </button>
                          </div>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-2 p-3 border border-[#D4AF37]/10 bg-[#0B0F19]/50 text-[#FDFBF7]/50 text-[10px] font-light" data-id="element-5197">
                    Duplicates carry no penalty — they are tradeable assets.
                    Open Market listings carry a 1 XP Darbar fee per successful
                    trade.
                  </div>
                </>}
            </div>
          </div>;
      case 'live-events':
        {
          const nights = [{
            month: 'JUL',
            day: '18',
            time: '7:00 PM',
            title: 'The Court Awakens — Opening Preview',
            loc: 'Illuminarium · Toronto',
            req: 'All Ranks'
          }, {
            month: 'AUG',
            day: '15',
            time: '7:00 PM',
            title: 'The Chamber of Scent & Song',
            loc: 'Illuminarium · Toronto',
            req: 'All Ranks'
          }, {
            month: 'SEP',
            day: '19',
            time: '7:00 PM',
            title: 'The Royal Regalia Reveal',
            loc: 'Illuminarium · Toronto',
            req: 'Courtier+'
          }, {
            month: 'OCT',
            day: '17',
            time: '7:00 PM',
            title: "Founders' Feast",
            loc: 'Illuminarium · Toronto',
            req: 'Sardar+'
          }];
          const sessions = [{
            month: 'JUL',
            day: '25',
            time: '8:00 PM',
            title: 'The AI Artists Behind the Faces',
            loc: 'Virtual · Court Channel',
            req: 'All Ranks'
          }, {
            month: 'AUG',
            day: '29',
            time: '8:00 PM',
            title: 'Building the Darbar — Toronto Immersive Team',
            loc: 'Virtual · Court Channel',
            req: 'Courtier+'
          }, {
            month: 'SEP',
            day: '26',
            time: '8:00 PM',
            title: 'The Sound of the Kingdom — Composing the Score',
            loc: 'Virtual · Court Channel',
            req: 'All Ranks'
          }, {
            month: 'OCT',
            day: '24',
            time: '8:00 PM',
            title: 'Taste of Lahore — The Royal Culinary Reveal',
            loc: 'Virtual · Court Channel',
            req: 'Sardar+'
          }];
          const renderEventCard = (evt: {
            month: string;
            day: string;
            time: string;
            title: string;
            loc: string;
            req: string;
          }, i: number, isNights: boolean) => {
            const key = `${isNights ? 'nights' : 'sessions'}-${i}`;
            const codeValue = eventCodes[key] ?? '';
            const isRedeemed = !!redeemedEvents[key];
            return <div key={key} className={`bg-[#0B0F19] border p-6 flex flex-col relative overflow-hidden transition-colors ${isNights ? 'border-[#D4AF37]/25 hover:border-[#D4AF37]/50' : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/45'}`} data-id="element-5198">
              {/* Refined gold top accent — solid for Nights, hairline for Sessions */}
              <div className={`absolute top-0 left-0 right-0 ${isNights ? 'h-[2px] bg-[#D4AF37]/70' : 'h-px bg-[#D4AF37]/40'}`} data-id="element-5199" />
              {/* Subtle in-person glow for Nights */}
              {isNights && <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-5200" />}
              <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-5201">
                <div className="flex flex-col" data-id="element-5202">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#D4AF37]" data-id="element-5203">
                    {evt.month}
                  </span>
                  <span className="font-serif text-3xl text-[#FDFBF7] leading-none mt-1" data-id="element-5204">
                    {evt.day}
                  </span>
                  <span className="text-[#D4AF37]/60 text-[10px] tracking-widest uppercase mt-2" data-id="element-5205">
                    {evt.time}
                  </span>
                </div>
                <span className="px-2 py-1 text-[8px] uppercase tracking-widest border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37]/80" data-id="element-5206">
                  {isNights ? <span className="inline-flex items-center gap-1.5" data-id="element-5207">
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37]/80" data-id="element-5208" />
                      {evt.req}
                    </span> : evt.req}
                </span>
              </div>
              <h4 className="font-serif text-lg text-[#FDFBF7] mb-4 leading-snug" data-id="element-5209">
                {evt.title}
              </h4>
              <p className="text-[#FDFBF7]/50 text-xs font-light flex items-center gap-2 mb-6" data-id="element-5210">
                {isNights ? <MapPin className="w-4 h-4 text-[#D4AF37]/60" data-id="element-5211" /> : <Video className="w-4 h-4 text-[#D4AF37]/60" data-id="element-5212" />}
                {evt.loc}
              </p>

              <button className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-colors mt-auto" data-id="element-5213">
                <Plus className="w-3 h-3" data-id="element-5214" /> Add to Calendar
              </button>

              <div className="mt-4 pt-4 border-t border-dashed border-[#D4AF37]/15" data-id="element-5215">
                {isRedeemed ? <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[#D4AF37]/40 bg-[#D4AF37]/10" data-id="element-5216">
                    <span className="flex items-center gap-2 text-[#D4AF37] text-[10px] tracking-widest uppercase font-bold" data-id="element-5217">
                      <Check className="w-3.5 h-3.5" data-id="element-5218" /> Code Redeemed
                    </span>
                    <span className="text-[#D4AF37] text-[10px] font-mono font-bold" data-id="element-5219">
                      +200 XP
                    </span>
                  </div> : <>
                    <div className="flex items-center justify-between mb-2" data-id="element-5220">
                      <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5221">
                        Code provided at event
                      </p>
                      <p className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5222">
                        +200 XP
                      </p>
                    </div>
                    <div className="flex gap-2" data-id="element-5223">
                      <input type="text" value={codeValue} onChange={e => setEventCodes(prev => ({
                      ...prev,
                      [key]: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
                    }))} placeholder="ENTER CODE" maxLength={12} className="flex-1 min-w-0 px-3 py-2.5 bg-[#070A12] border border-[#D4AF37]/15 text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase placeholder:text-[#FDFBF7]/20 font-mono focus:outline-none focus:border-[#D4AF37]/50 transition-colors" data-id="element-5224" />
                      <button type="button" disabled={codeValue.trim().length < 4} onClick={() => setRedeemedEvents(prev => ({
                      ...prev,
                      [key]: true
                    }))} className="shrink-0 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#E5C158] disabled:bg-[#D4AF37]/30 disabled:cursor-not-allowed text-[#0B0F19] text-[10px] tracking-[0.25em] uppercase font-bold transition-colors" data-id="element-5225">
                        Redeem
                      </button>
                    </div>
                    <p className="text-[#FDFBF7]/35 text-[10px] mt-2 leading-snug" data-id="element-5226">
                      {isNights ? 'Your code is announced during the live experience at the Illuminarium.' : 'Your code is shared at the end of the virtual session.'}
                    </p>
                  </>}
              </div>
            </div>;
          };
          return <div className="flex flex-col gap-10" data-id="element-5227">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5228">
              <div data-id="element-5229">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5230">
                  Live Events
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5231">
                  In-person & Virtual · The Court Calendar
                </p>
              </div>
            </div>

            {/* Treasury Nights group */}
            <div className="flex flex-col gap-5" data-id="element-5232">
              <div className="flex items-end justify-between gap-4 border-l-2 border-[#8B0000] pl-4" data-id="element-5233">
                <div data-id="element-5234">
                  <h4 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5235">
                    Treasury Nights
                  </h4>
                  <p className="text-[#8B0000]/80 text-[10px] tracking-[0.3em] uppercase mt-1" data-id="element-5236">
                    In Person · Illuminarium Toronto
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-5237">
                  {nights.length} Events
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5238">
                {nights.map((e, i) => renderEventCard(e, i, true))}
              </div>
            </div>

            {/* Court Sessions group */}
            <div className="flex flex-col gap-5" data-id="element-5239">
              <div className="flex items-end justify-between gap-4 border-l-2 border-[#D4AF37] pl-4" data-id="element-5240">
                <div data-id="element-5241">
                  <h4 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5242">
                    Court Sessions
                  </h4>
                  <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.3em] uppercase mt-1" data-id="element-5243">
                    Virtual · Behind the Scenes
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-5244">
                  {sessions.length} Sessions
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5245">
                {sessions.map((e, i) => renderEventCard(e, i, false))}
              </div>
            </div>
          </div>;
        }
      case 'my-misl':
        {
          const misl = {
            name: 'Sukerchakia',
            tagline: 'The Founding House · Gujranwala',
            category: 'Historic Prestige',
            banner: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
            motto: 'Akal Sahai — the Timeless One is our help.',
            members: '1,520',
            xp: '480,210',
            rank: '#1 · Historic Prestige',
            founded: '1748',
            region: 'Gujranwala · Punjab',
            history: 'Founded in 1748, the Sukerchakia Misl rose to dominate the western Punjab and ultimately gave the Khalsa Confederacy its most consequential ruler — Maharaja Ranjit Singh — whose unification of the Misls in 1799 founded the Sikh Empire. To stand under this Misl is to stand at the source of sovereignty.',
            misldar: {
              handle: '@ranjit_s',
              name: 'Ranjit S.',
              xp: '12,400',
              artifacts: 47,
              joined: 'Mar 2024'
            },
            you: {
              rank: 14,
              xp: '2,840',
              artifacts: 12,
              contribution: '+520 this week'
            },
            leaderboard: [{
              rank: 1,
              handle: '@ranjit_s',
              name: 'Ranjit S.',
              xp: '12,400',
              isMisldar: true
            }, {
              rank: 2,
              handle: '@harjeet_k',
              name: 'Harjeet K.',
              xp: '9,820'
            }, {
              rank: 3,
              handle: '@arjan_s',
              name: 'Arjan S.',
              xp: '8,150'
            }, {
              rank: 4,
              handle: '@simran_k',
              name: 'Simran K.',
              xp: '7,640'
            }, {
              rank: 5,
              handle: '@dilraj_s',
              name: 'Dilraj S.',
              xp: '6,910'
            }, {
              rank: 14,
              handle: '@harnoor_s',
              name: 'Harnoor S. (you)',
              xp: '2,840',
              isYou: true
            }],
            activity: [{
              who: 'Ranjit S.',
              what: 'unlocked',
              target: 'Maharaja Ranjit Singh',
              when: '2h ago',
              tone: 'gold'
            }, {
              who: 'Simran K.',
              what: 'traded',
              target: 'Royal Seal of Ranjit Singh',
              when: '6h ago',
              tone: 'neutral'
            }, {
              who: 'Harjeet K.',
              what: 'attended',
              target: 'The Court Awakens — Opening Preview',
              when: '1d ago',
              tone: 'red'
            }, {
              who: 'Arjan S.',
              what: 'completed',
              target: "Founders' Seals (1/6)",
              when: '2d ago',
              tone: 'gold'
            }]
          };
          // Create-your-own-Misl flow inside the My Clan tab
          if (mislView === 'create') {
            return <CreateMislPanel embedded onClose={() => setMislView('home')} data-id="element-5246" />;
          }
          return <div className="flex flex-col gap-8" data-id="element-5247">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-[#D4AF37]/20 pb-4" data-id="element-5248">
              <div data-id="element-5249">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5250">
                  My Clan{' '}
                  <span className="text-[#FDFBF7]/40 text-lg" data-id="element-5251">(Misl)</span>
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5252">
                  Your House · Your Sangat
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-5253">
                <button onClick={() => setMislView('create')} className="flex items-center gap-1.5 bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] text-[10px] uppercase tracking-widest px-3 py-2 transition-colors font-bold shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-5254">
                  <Plus className="w-3 h-3" data-id="element-5255" />
                  Create Your Own Misl
                </button>
                <button className="text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-5256">
                  Switch Clan
                </button>
              </div>
            </div>

            {/* Founding Misl status — appears if user has created their own */}
            <FoundingMislStatus onManage={() => setMislView('create')} data-id="element-5257" />

            {/* Banner / hero */}
            <div className="relative overflow-hidden border border-[#D4AF37]/40 bg-[#0B0F19]" data-id="element-5258">
              <div className="absolute inset-0" data-id="element-5259">
                <img src={misl.banner} alt={misl.name} className="w-full h-full object-cover opacity-30" data-id="element-5260" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]/20" data-id="element-5261" />
              </div>
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5262" />
              <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-5263">
                <div className="w-14 h-14 shrink-0 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-5264">
                  <Shield className="w-7 h-7 text-[#D4AF37]" data-id="element-5265" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-5266">
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-1.5" data-id="element-5267">
                    {misl.category}
                  </p>
                  <h4 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight" data-id="element-5268">
                    {misl.name} Misl
                  </h4>
                  <p className="text-[#FDFBF7]/55 text-sm mt-2 italic" data-id="element-5269">
                    {misl.tagline}
                  </p>
                </div>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-4 border-t border-[#D4AF37]/20" data-id="element-5270">
                {[{
                  label: 'Members',
                  value: misl.members,
                  icon: Users
                }, {
                  label: 'Clan XP',
                  value: misl.xp,
                  icon: Sparkles
                }, {
                  label: 'Global Rank',
                  value: misl.rank,
                  icon: Crown
                }, {
                  label: 'Founded',
                  value: misl.founded,
                  icon: BookOpen
                }].map((kpi, i) => <div key={i} className={`p-4 sm:p-5 text-center ${i < 3 ? 'border-r border-[#D4AF37]/15' : ''} ${i < 2 ? 'sm:border-r border-b sm:border-b-0 border-[#D4AF37]/15' : ''} ${i < 2 ? 'border-b' : ''}`} data-id="element-5271">
                    <kpi.icon className="w-3.5 h-3.5 text-[#D4AF37]/60 mx-auto mb-2" data-id="element-5272" />
                    <p className="font-serif text-base sm:text-lg text-[#D4AF37] leading-tight" data-id="element-5273">
                      {kpi.value}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-1" data-id="element-5274">
                      {kpi.label}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Two-column: Misldar + Your Standing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5275">
              {/* Misldar */}
              <div className="border border-[#D4AF37]/30 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5276">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-5277" />
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5278">
                  The Misldar{' '}
                  <span className="text-[#FDFBF7]/40" data-id="element-5279">(Clan Chief)</span>
                </p>
                <div className="flex items-center gap-4" data-id="element-5280">
                  <div className="w-12 h-12 shrink-0 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-5281">
                    <Crown className="w-5 h-5 text-[#D4AF37]" data-id="element-5282" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5283">
                    <p className="font-serif text-lg text-[#FDFBF7] leading-tight" data-id="element-5284">
                      {misl.misldar.name}
                    </p>
                    <p className="text-[#D4AF37]/70 text-[10px] tracking-widest uppercase font-mono mt-0.5" data-id="element-5285">
                      {misl.misldar.handle}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 mt-5 pt-4 border-t border-[#D4AF37]/15 text-center" data-id="element-5286">
                  <div data-id="element-5287">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5288">
                      {misl.misldar.xp}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5289">
                      XP
                    </p>
                  </div>
                  <div className="border-x border-[#D4AF37]/15" data-id="element-5290">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5291">
                      {misl.misldar.artifacts}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5292">
                      Artifacts
                    </p>
                  </div>
                  <div data-id="element-5293">
                    <p className="font-mono text-[#FDFBF7]/70 text-sm" data-id="element-5294">
                      {misl.misldar.joined}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5295">
                      Joined
                    </p>
                  </div>
                </div>
              </div>

              {/* Your Standing */}
              <div className="border border-[#D4AF37]/30 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5296">
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-5297" />
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5298">
                  Your Standing
                </p>
                <div className="flex items-baseline gap-2" data-id="element-5299">
                  <span className="font-serif text-4xl text-[#D4AF37]" data-id="element-5300">
                    #{misl.you.rank}
                  </span>
                  <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-5301">
                    in {misl.name}
                  </span>
                </div>
                <p className="text-emerald-400/80 text-[10px] tracking-widest uppercase mt-1.5" data-id="element-5302">
                  ↑ {misl.you.contribution}
                </p>
                <div className="grid grid-cols-2 mt-5 pt-4 border-t border-[#D4AF37]/15 text-center" data-id="element-5303">
                  <div className="border-r border-[#D4AF37]/15" data-id="element-5304">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5305">
                      {misl.you.xp}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5306">
                      Your XP
                    </p>
                  </div>
                  <div data-id="element-5307">
                    <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5308">
                      {misl.you.artifacts}
                    </p>
                    <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5309">
                      Artifacts
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6" data-id="element-5310">
              <div className="flex justify-between items-center mb-4" data-id="element-5311">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase" data-id="element-5312">
                  Clan Leaderboard
                </p>
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase" data-id="element-5313">
                  Top 5 + You
                </p>
              </div>
              <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5314">
                {misl.leaderboard.map(row => <div key={row.handle} className={`flex items-center gap-3 py-3 first:pt-0 last:pb-0 ${row.isYou ? 'bg-[#D4AF37]/[0.04] -mx-3 px-3 border-l-2 border-[#D4AF37]' : ''}`} data-id="element-5315">
                    <span className={`w-7 text-center font-mono text-sm ${row.rank <= 3 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-5316">
                      {row.rank}
                    </span>
                    <div className={`w-8 h-8 shrink-0 rounded-full border ${row.isMisldar ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'} flex items-center justify-center`} data-id="element-5317">
                      {row.isMisldar ? <Crown className="w-3.5 h-3.5 text-[#D4AF37]" data-id="element-5318" /> : <User className="w-3.5 h-3.5 text-[#D4AF37]/60" data-id="element-5319" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5320">
                      <p className={`text-sm leading-tight truncate ${row.isYou ? 'text-[#D4AF37] font-bold' : 'text-[#FDFBF7]'}`} data-id="element-5321">
                        {row.name}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[9px] tracking-widest uppercase font-mono mt-0.5 truncate" data-id="element-5322">
                        {row.handle}
                      </p>
                    </div>
                    <span className={`font-mono text-xs font-bold whitespace-nowrap ${row.isYou ? 'text-[#D4AF37]' : 'text-[#D4AF37]/70'}`} data-id="element-5323">
                      {row.xp} XP
                    </span>
                  </div>)}
              </div>
            </div>

            {/* History + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-5324">
              {/* History */}
              <div className="lg:col-span-3 border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6 relative" data-id="element-5325">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-5326">
                  The Story of {misl.name}
                </p>
                <p className="text-[#FDFBF7]/75 text-sm leading-relaxed font-light mb-5" data-id="element-5327">
                  {misl.history}
                </p>
                <blockquote className="font-serif italic text-[#D4AF37]/90 text-base leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4" data-id="element-5328">
                  "{misl.motto}"
                </blockquote>
                <p className="text-[#D4AF37]/40 text-[9px] tracking-widest uppercase mt-3 pl-4" data-id="element-5329">
                  Clan Motto
                </p>
              </div>

              {/* Activity */}
              <div className="lg:col-span-2 border border-[#D4AF37]/25 bg-[#0B0F19] p-5 sm:p-6" data-id="element-5330">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5331">
                  Clan Activity
                </p>
                <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5332">
                  {misl.activity.map((a, i) => <div key={i} className="py-3 first:pt-0 last:pb-0" data-id="element-5333">
                      <p className="text-[#FDFBF7]/80 text-xs leading-snug" data-id="element-5334">
                        <span className="font-bold text-[#FDFBF7]" data-id="element-5335">
                          {a.who}
                        </span>{' '}
                        <span className="text-[#FDFBF7]/55" data-id="element-5336">{a.what}</span>{' '}
                        <span className={a.tone === 'gold' ? 'text-[#D4AF37]' : a.tone === 'red' ? 'text-[#8B0000]' : 'text-[#FDFBF7]'} data-id="element-5337">
                          {a.target}
                        </span>
                      </p>
                      <p className="text-[#FDFBF7]/35 text-[9px] tracking-widest uppercase mt-1" data-id="element-5338">
                        {a.when}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>

            {/* CTA strip */}
            <div className="border border-[#D4AF37]/30 bg-[#131B2B] p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-5339">
              <div className="text-center sm:text-left" data-id="element-5340">
                <p className="font-serif text-lg text-[#FDFBF7]" data-id="element-5341">
                  Climb the Clan
                </p>
                <p className="text-[#FDFBF7]/55 text-xs mt-1" data-id="element-5342">
                  Earn XP through packets, trades, events and referrals. The top
                  XP earner becomes Misldar (Clan Chief).
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-5343">
                <button type="button" onClick={() => setActiveTab('referrals')} className="px-4 py-2.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2" data-id="element-5344">
                  <UserPlus className="w-3.5 h-3.5" data-id="element-5345" /> Invite to Clan
                </button>
                <button type="button" onClick={() => setActiveTab('daily-packet')} className="px-4 py-2.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2" data-id="element-5346">
                  <Gift className="w-3.5 h-3.5" data-id="element-5347" /> Earn XP
                </button>
              </div>
            </div>
          </div>;
        }
      case 'referrals':
        {
          const inviteLink = 'https://lahore-treasury.app/join/HARNOOR-123';
          const inviteMessage = `You're invited to Treasures of the Sikh Empire — a premium collectible experience built around the Sikh Empire.\n\n` + `I've just joined Sukerchakia Misl. Every artifact (Maharajas, generals, jewels, fortresses) carries real historical weight. Open daily packets, complete sets, and build a treasury that means something.\n\n` + `Join me here and claim your free Founders' Edition card:\n${inviteLink}`;
          const tiers = [{
            count: 1,
            reward: '+500 XP',
            label: 'First invite bonus',
            achieved: true
          }, {
            count: 3,
            reward: 'Bonus Packet',
            label: 'A free Treasury packet',
            achieved: false
          }, {
            count: 5,
            reward: 'Founder Seal',
            label: 'A permanent identity seal',
            achieved: false
          }, {
            count: 10,
            reward: 'Live Night ticket',
            label: 'Reserved seat at Treasury Nights',
            achieved: false
          }];
          return <div className="flex flex-col gap-8" data-id="element-5348">
            {/* Header */}
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5349">
              <div data-id="element-5350">
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-5351">
                  Bring the Court to Life
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5352">
                  Your Royal Decree
                </p>
              </div>
              <div className="text-right" data-id="element-5353">
                <span className="font-serif text-2xl text-[#D4AF37]" data-id="element-5354">
                  2<span className="text-[#FDFBF7]/30 text-lg" data-id="element-5355"> referred</span>
                </span>
              </div>
            </div>

            {/* Invite message preview */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5356">
              <div className="flex items-center justify-between mb-3" data-id="element-5357">
                <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase font-bold" data-id="element-5358">
                  Your Invitation
                </p>
                <p className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase font-bold" data-id="element-5359">
                  +500 XP / invite
                </p>
              </div>
              <p className="text-[#FDFBF7]/80 text-sm leading-relaxed whitespace-pre-line" data-id="element-5360">
                {inviteMessage.split(inviteLink)[0]}
                <a href={inviteLink} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 underline-offset-2 hover:decoration-[#D4AF37] break-all" data-id="element-5361">
                  {inviteLink}
                </a>
              </p>
            </div>

            {/* Share buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-5362">
              <a href={`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`} target="_blank" rel="noopener noreferrer" className="py-3.5 bg-[#25D366] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg" data-id="element-5363">
                <MessageCircle className="w-3.5 h-3.5" data-id="element-5364" /> WhatsApp
              </a>
              <button type="button" onClick={() => {
                navigator.clipboard.writeText(inviteMessage);
                alert('Invitation copied!');
              }} className="py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-5365">
                <Copy className="w-3.5 h-3.5" data-id="element-5366" /> Copy Message
              </button>
              <button type="button" onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Treasures of the Sikh Empire',
                    text: inviteMessage
                  });
                } else {
                  navigator.clipboard.writeText(inviteMessage);
                  alert('Invitation copied!');
                }
              }} className="py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-5367">
                <Share2 className="w-3.5 h-3.5" data-id="element-5368" /> More
              </button>
            </div>

            {/* Reward ladder */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5369">
              <div className="flex items-center justify-between mb-5" data-id="element-5370">
                <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase font-bold" data-id="element-5371">
                  Reward Ladder
                </p>
                <p className="text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase" data-id="element-5372">
                  2 / 10 invites
                </p>
              </div>

              {/* Progress rail */}
              <div className="relative w-full h-1 bg-[#131B2B] rounded-full overflow-hidden mb-6" data-id="element-5373">
                <div className="absolute inset-y-0 left-0 w-[20%] bg-gradient-to-r from-[#8B0000] to-[#D4AF37]" data-id="element-5374" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-5375">
                {tiers.map(t => <div key={t.count} className={`relative border p-4 ${t.achieved ? 'border-[#D4AF37]/60 bg-[#D4AF37]/10' : 'border-[#FDFBF7]/15 bg-[#131B2B]/40'}`} data-id="element-5376">
                    <div className="flex items-center justify-between mb-2" data-id="element-5377">
                      <span className={`font-serif text-lg ${t.achieved ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/70'}`} data-id="element-5378">
                        {t.count} invite{t.count > 1 ? 's' : ''}
                      </span>
                      {t.achieved ? <Check className="w-4 h-4 text-[#D4AF37]" data-id="element-5379" /> : <Lock className="w-3.5 h-3.5 text-[#FDFBF7]/30" data-id="element-5380" />}
                    </div>
                    <p className={`text-[10px] tracking-[0.2em] uppercase font-bold mb-1 ${t.achieved ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-5381">
                      {t.reward}
                    </p>
                    <p className="text-[#FDFBF7]/45 text-[10px] leading-snug" data-id="element-5382">
                      {t.label}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Referred friends list */}
            <div className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-5383">
              <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-4" data-id="element-5384">
                Your Court
              </p>
              <div className="flex flex-col divide-y divide-[#D4AF37]/10" data-id="element-5385">
                {[{
                  name: 'Gurjeet K.',
                  misl: 'Sukerchakia Misl',
                  joined: '3 days ago',
                  xp: '+500'
                }, {
                  name: 'Manveer S.',
                  misl: 'Sukerchakia Misl',
                  joined: '1 week ago',
                  xp: '+500'
                }].map((f, i) => <div key={i} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0" data-id="element-5386">
                    <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0" data-id="element-5387">
                      <User className="w-4 h-4 text-[#D4AF37]" data-id="element-5388" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5389">
                      <p className="text-[#FDFBF7] text-sm font-medium leading-tight" data-id="element-5390">
                        {f.name}
                      </p>
                      <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-5391">
                        {f.misl} · joined {f.joined}
                      </p>
                    </div>
                    <span className="text-[#D4AF37] text-[10px] font-mono font-bold whitespace-nowrap" data-id="element-5392">
                      {f.xp} XP
                    </span>
                  </div>)}
                <div className="flex items-center gap-3 py-3 last:pb-0 opacity-40" data-id="element-5393">
                  <div className="w-9 h-9 rounded-full border border-dashed border-[#FDFBF7]/25 flex items-center justify-center shrink-0" data-id="element-5394">
                    <UserPlus className="w-4 h-4 text-[#FDFBF7]/40" data-id="element-5395" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5396">
                    <p className="text-[#FDFBF7]/60 text-sm italic leading-tight" data-id="element-5397">
                      One more invite to your next bonus packet…
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>;
        }
      case 'profile':
        return <div className="flex flex-col gap-8" data-id="element-5398">
            <div className="flex justify-between items-end border-b border-[#D4AF37]/20 pb-4" data-id="element-5399">
              <div data-id="element-5400">
                <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5401">
                  Your Profile
                </h3>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mt-1" data-id="element-5402">
                  Identity & Status
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-5403">
                <button type="button" onClick={() => setShowAdminDashboard(true)} className="px-4 py-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-2" data-id="element-5404">
                  <Shield className="w-3.5 h-3.5" data-id="element-5405" /> Admin Dashboard
                </button>
                <button type="button" className="text-[#D4AF37] text-[10px] uppercase tracking-widest border-b border-[#D4AF37]/40 pb-0.5" data-id="element-5406">
                  Edit Profile
                </button>
              </div>
            </div>
            <div className="p-8 border border-[#D4AF37]/30 bg-[#0B0F19] flex flex-col sm:flex-row items-center gap-8" data-id="element-5407">
              {knightedAvatar ? <div className="relative group" data-id="element-5408">
                  <div className="w-32 h-32 border-2 border-[#D4AF37] bg-[#131B2B] overflow-hidden relative shadow-[0_0_30px_rgba(212,175,55,0.25)]" data-id="element-5409">
                    <img src={knightedAvatar} alt="Your knighted portrait" className="w-full h-full object-cover" data-id="element-5410" />
                    <div className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/40 pointer-events-none" data-id="element-5411" />
                  </div>
                  <div className="absolute -bottom-3 -right-3 w-8 h-8 rounded-full bg-[#D4AF37] border-2 border-[#0B0F19] flex items-center justify-center shadow-lg" data-id="element-5412">
                    <Shield className="w-4 h-4 text-[#0B0F19]" data-id="element-5413" />
                  </div>
                  <button type="button" onClick={() => setIsKnightingOpen(true)} className="absolute inset-0 bg-[#0B0F19]/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-5414">
                    <span className="text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase" data-id="element-5415">
                      Retake
                    </span>
                  </button>
                </div> : <button type="button" onClick={() => setIsKnightingOpen(true)} className="w-32 h-32 border-2 border-dashed border-[#D4AF37]/40 bg-[#131B2B]/50 hover:border-[#D4AF37] hover:bg-[#131B2B] flex flex-col items-center justify-center gap-2 transition-colors relative group" data-id="element-5416">
                  <Shield className="w-8 h-8 text-[#D4AF37]/40 group-hover:text-[#D4AF37] transition-colors" data-id="element-5417" />
                  <span className="text-[#D4AF37]/50 group-hover:text-[#D4AF37] text-[8px] tracking-[0.25em] uppercase transition-colors text-center px-2 leading-tight" data-id="element-5418">
                    Awaiting
                    <br data-id="element-5419" />
                    Knighting
                  </span>
                </button>}
              <div className="text-center sm:text-left flex-1" data-id="element-5420">
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5421">
                  Gold Medallion of Ranjit Singh · 4,150 XP
                </p>
                <h3 className="font-serif text-4xl text-[#FDFBF7] mb-4" data-id="element-5422">
                  Harnoor S.
                </h3>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start" data-id="element-5423">
                  <span className="px-3 py-1.5 bg-[#8B0000]/20 border border-[#8B0000]/50 text-[#8B0000] text-[9px] uppercase tracking-widest" data-id="element-5424">
                    Founder
                  </span>
                  <span className="px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-widest" data-id="element-5425">
                    Live Attended
                  </span>
                  <span className="px-3 py-1.5 bg-[#131B2B] border border-[#FDFBF7]/20 text-[#FDFBF7]/60 text-[9px] uppercase tracking-widest" data-id="element-5426">
                    Early Adopter
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-5427">
              {[{
              label: 'Artifacts',
              value: '12'
            }, {
              label: 'Sets Complete',
              value: '4'
            }, {
              label: 'Trades Made',
              value: '7'
            }, {
              label: 'Events Attended',
              value: '1'
            }].map((stat, i) => <div key={i} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-6 text-center" data-id="element-5428">
                  <p className="font-serif text-3xl text-[#D4AF37] mb-2" data-id="element-5429">
                    {stat.value}
                  </p>
                  <p className="text-[#FDFBF7]/50 text-[10px] uppercase tracking-widest" data-id="element-5430">
                    {stat.label}
                  </p>
                </div>)}
            </div>
          </div>;
      case 'rewards':
        {
          return <RewardsTabContent data-id="element-5431" />;
        }
      default:
        return null;
    }
  };
  return <section id="member-dashboard" className="py-16 sm:py-24 bg-[#0B0F19] relative overflow-hidden" data-id="element-5432">
      <div className="container mx-auto px-4 relative z-10" data-id="element-5433">
        <div className="max-w-6xl mx-auto" data-id="element-5434">
          <div className="flex flex-col gap-8 sm:gap-12" data-id="element-5435">
            {/* Section Header */}
            <SectionHeader eyebrow="Member Experience" title={<>
                  ONCE YOU'RE <span className="text-[#D4AF37]" data-id="element-5437">IN</span>
                </>} description="The Treasury is more than a collection. It's your ticket to the inner circle of the Sikh Kingdoms." data-id="element-5436" />

            {/* Main Dashboard UI Container — swaps to inline Admin when active */}
            {showAdminDashboard ? <Suspense fallback={<div className="border border-[#D4AF37]/20 bg-[#131B2B]/30 min-h-[700px] flex items-center justify-center" data-id="element-5439">
                    <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase animate-pulse" data-id="element-5440">
                      Loading Admin Console…
                    </p>
                  </div>} data-id="element-5438">
                <TreasuryAdminDashboard inline onClose={() => setShowAdminDashboard(false)} data-id="element-5441" />
              </Suspense> : <div className="flex flex-col lg:flex-row border border-[#D4AF37]/20 bg-[#131B2B]/30 backdrop-blur-sm min-h-[700px]" data-id="element-5442">
                {/* Left Sidebar Navigation — Scrollable on mobile */}
                <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-[#D4AF37]/20 flex lg:flex-col overflow-x-auto lg:overflow-x-visible no-scrollbar" data-id="element-5443">
                  {tabs.map(tab => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-5 py-4 lg:py-5 text-left transition-all relative shrink-0 lg:shrink ${activeTab === tab.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-[#FDFBF7]/40 hover:text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5'}`} data-id="element-5444">
                      {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 lg:h-auto lg:right-auto bg-[#D4AF37]" data-id="element-5445" />}
                      <tab.icon className="w-4 h-4" data-id="element-5446" />
                      <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap" data-id="element-5447">
                        {tab.label}
                      </span>
                      {tab.badge && <span className="ml-auto w-4 h-4 bg-[#8B0000] text-[#FDFBF7] text-[8px] flex items-center justify-center rounded-full font-bold" data-id="element-5448">
                          {tab.badge === 'dot' ? '' : tab.badge}
                        </span>}
                    </button>)}
                </div>

                {/* Right Content Area */}
                <div className="flex-1 p-5 sm:p-8 lg:p-10 flex flex-col gap-8 sm:gap-10 overflow-hidden" data-id="element-5449">
                  {renderContent()}
                </div>
              </div>}
          </div>
        </div>
      </div>

      <KnightingCeremony isOpen={isKnightingOpen} onClose={() => setIsKnightingOpen(false)} onComplete={url => {
      setKnightedAvatar(url);
    }} data-id="element-5450" />
    </section>;
};
// ---------- 5. Daily Court Packet Section ----------
const DailyPacketSection = ({
  onRegister
}: {
  onRegister?: () => void;
}) => {
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed' | 'added'>('sealed');
  // Once the "added" animation has played, hand off to registration.
  useEffect(() => {
    if (packetState !== 'added' || !onRegister) return;
    const timer = setTimeout(() => {
      onRegister();
      setPacketState('sealed');
    }, 1800);
    return () => clearTimeout(timer);
  }, [packetState, onRegister]);
  return <section id="how-it-works" className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5451">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-5452" />
      <div className="max-w-4xl mx-auto relative text-center" data-id="element-5453">
        <SectionHeader eyebrow="The Habit Loop" title={<>
              THE DAILY <span className="text-[#D4AF37]" data-id="element-5455">PACKET</span>
            </>} description="Every 24 hours, a sealed leather envelope arrives from the Darbar. Open it to reveal new artifacts, and answer the optional Court Question for bonus influence." data-id="element-5454" />

        <div className="relative mt-12 min-h-[640px] flex items-center justify-center" data-id="element-5456">
          <AnimatePresence mode="wait" data-id="element-5457">
            {/* ============ SEALED ============ */}
            {packetState === 'sealed' && <motion.div key="sealed" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center text-center max-w-md mx-auto" data-id="element-5458">
                <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative w-64 sm:w-72 mb-10" data-id="element-5459">
                  <motion.div animate={{
                opacity: [0.4, 0.7, 0.4]
              }} transition={{
                duration: 2.8,
                repeat: Infinity
              }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-5460" />
                  <motion.button type="button" onClick={() => {
                setPacketState('opening');
                setTimeout(() => setPacketState('revealed'), 1900);
              }} whileHover={{
                scale: 1.04
              }} whileTap={{
                scale: 0.96
              }} aria-label="Break the seal and open your daily packet" className="relative block w-full cursor-pointer" data-id="element-5461">
                    <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-5462" />
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-5463">
                      Click to open
                    </span>
                  </motion.button>
                </motion.div>
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5464">
                  Day 47 · The Darbar
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5465">
                  Your Daily Packet has Arrived
                </h3>
                <p className="text-[#FDFBF7]/60 text-sm font-light mb-8 leading-relaxed" data-id="element-5466">
                  A sealed envelope from the Maharaja's court awaits. Break the
                  wax — discover the artifact within.
                </p>
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => {
              setPacketState('opening');
              setTimeout(() => setPacketState('revealed'), 1900);
            }} className="px-12 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-5467">
                  <Sparkles className="w-4 h-4" data-id="element-5468" /> Break the Seal
                </motion.button>
              </motion.div>}

            {/* ============ OPENING ============ */}
            {packetState === 'opening' && <motion.div key="opening" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="relative flex items-center justify-center w-full h-full min-h-[560px]" data-id="element-5469">
                {/* Expanding gold burst */}
                <motion.div initial={{
              scale: 0.4,
              opacity: 0
            }} animate={{
              scale: 4,
              opacity: [0, 0.9, 0]
            }} transition={{
              duration: 1.8,
              ease: 'easeOut'
            }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-5470" />

                {/* Shaking + shrinking envelope */}
                <motion.div initial={{
              scale: 1,
              opacity: 1
            }} animate={{
              scale: [1, 1.1, 0.6],
              opacity: [1, 1, 0],
              rotateZ: [0, -3, 4, -2, 0]
            }} transition={{
              duration: 1.8,
              times: [0, 0.5, 1],
              ease: 'easeInOut'
            }} className="relative w-64 sm:w-72 z-10" data-id="element-5471">
                  <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-5472" />
                </motion.div>

                {/* Radial sparkle particles */}
                {Array.from({
              length: 20
            }).map((_, i) => {
              const angle = i / 20 * Math.PI * 2;
              const distance = 280;
              return <motion.div key={i} initial={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0
              }} animate={{
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance,
                opacity: [0, 1, 0],
                scale: [0, 1.2, 0]
              }} transition={{
                duration: 1.6,
                delay: 0.3 + i % 4 * 0.05,
                ease: 'easeOut'
              }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-5473" />;
            })}

                {/* Caption */}
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: [0, 1, 1, 0],
              y: 0
            }} transition={{
              duration: 1.8,
              times: [0, 0.3, 0.7, 1]
            }} className="absolute bottom-12 font-serif text-lg text-[#D4AF37] tracking-[0.3em] uppercase" data-id="element-5474">
                  The seal breaks…
                </motion.p>
              </motion.div>}

            {/* ============ REVEALED ============ */}
            {packetState === 'revealed' && <motion.div key="revealed" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6
          }} className="flex flex-col items-center text-center max-w-2xl mx-auto" data-id="element-5475">
                <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4" data-id="element-5476">
                  A Treasure from the Court
                </motion.p>

                <motion.div initial={{
              y: 200,
              scale: 0.5,
              rotateY: 90,
              opacity: 0
            }} animate={{
              y: 0,
              scale: 1,
              rotateY: 0,
              opacity: 1
            }} transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative mb-6" style={{
              perspective: 1000
            }} data-id="element-5477">
                  <motion.div animate={{
                opacity: [0.4, 0.75, 0.4]
              }} transition={{
                duration: 3,
                repeat: Infinity
              }} className="absolute -inset-10 bg-[radial-gradient(circle,_rgba(212,175,55,0.4),_transparent_60%)] blur-2xl" data-id="element-5478" />
                  <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-64 sm:w-72" data-id="element-5479">
                    <div className="border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden bg-[#0B0F19]" data-id="element-5480">
                      <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5481" />
                    </div>
                    <div className="absolute -top-3 -right-3 px-3 py-1 bg-[#D4AF37] text-[#0B0F19] text-[9px] uppercase tracking-widest font-bold shadow-lg" data-id="element-5482">
                      Treasury Rare
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.9,
              duration: 0.6
            }} className="text-center" data-id="element-5483">
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-5484">
                    Moran Sarkar
                  </h3>
                  <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-4" data-id="element-5485">
                    Royal Court Collection · Treasury Rare
                  </p>
                  <p className="text-[#FDFBF7]/60 text-sm font-light italic max-w-md mx-auto mb-8 leading-relaxed" data-id="element-5486">
                    "A dancer who became consort to the Sher-e-Punjab — beauty
                    and intellect at the heart of the Darbar."
                  </p>

                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => setPacketState('added')} className="px-10 py-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B0F19] text-xs tracking-[0.3em] uppercase font-bold shadow-2xl transition-colors flex items-center gap-3 mx-auto" data-id="element-5487">
                    <BookOpen className="w-4 h-4" data-id="element-5488" /> Register to Add to Your
                    Collection
                  </motion.button>
                </motion.div>
              </motion.div>}

            {/* ============ ADDED ============ */}
            {packetState === 'added' && <motion.div key="added" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-5489">
                {/* Flying card animation */}
                <motion.div initial={{
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1
            }} animate={{
              x: -260,
              y: -180,
              scale: 0.18,
              opacity: 0,
              rotate: -15
            }} transition={{
              duration: 1.1,
              ease: [0.7, 0, 0.84, 0]
            }} className="relative w-44 mb-8 absolute" data-id="element-5490">
                  <div className="border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.6)] overflow-hidden" data-id="element-5491">
                    <img src="https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png" alt="Moran Sarkar" className="w-full" data-id="element-5492" />
                  </div>
                </motion.div>

                {/* Sparkle trail */}
                {Array.from({
              length: 10
            }).map((_, i) => <motion.div key={i} initial={{
              x: 0,
              y: 0,
              opacity: 0,
              scale: 0
            }} animate={{
              x: -260 + Math.random() * 50,
              y: -180 + Math.random() * 50,
              opacity: [0, 1, 0],
              scale: [0, 1, 0]
            }} transition={{
              duration: 1.3,
              delay: 0.15 + i * 0.06,
              ease: 'easeOut'
            }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-5493" />)}

                {/* Success state */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.0,
              duration: 0.6
            }} className="flex flex-col items-center" data-id="element-5494">
                  <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center mb-5" data-id="element-5495">
                    <Sparkles className="w-7 h-7 text-[#D4AF37]" data-id="element-5496" />
                  </div>
                  <p className="text-[#D4AF37]/80 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5497">
                    Reserved for your Archive
                  </p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-3" data-id="element-5498">
                    Opening the Darbar gates…
                  </h3>
                  <p className="text-[#FDFBF7]/60 text-sm font-light leading-relaxed mb-2 max-w-md" data-id="element-5499">
                    Moran Sarkar is being filed under the{' '}
                    <span className="text-[#D4AF37]" data-id="element-5500">
                      Royal Court Collection
                    </span>
                    . Finish registration to claim her.
                  </p>
                  <p className="text-[#FDFBF7]/40 text-xs font-light mb-8" data-id="element-5501">
                    Redirecting you to the registry…
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
};
// ---------- 6. Collections Section ----------
interface CourtCard {
  img: string;
  name: string;
  rarity: string;
  role?: string;
  type?: string;
  set?: string;
  oneLineLore?: string;
  collectibleDescription?: string;
  historicalMeaning?: string;
  whyItIsRare?: string;
  collectorMeaning?: string;
  unlockPotential?: string;
  emblem?: string;
  isLocked?: boolean;
}
const CardModal = ({
  card,
  onClose
}: {
  card: CourtCard;
  onClose: () => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-5502">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-5503">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.15),_transparent_50%)]" data-id="element-5504" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 p-6 sm:p-10 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-5505">
        <CornerOrnaments data-id="element-5506" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-5507">
          <X className="w-6 h-6" data-id="element-5508" />
        </button>

        <div className="flex flex-col md:flex-row gap-8 sm:gap-12" data-id="element-5509">
          {/* Left Col: Card artwork */}
          <div className="w-full md:w-2/5 shrink-0 flex flex-col items-center gap-4" data-id="element-5510">
            <div className="w-full aspect-[4/5] bg-[#131B2B] shadow-[0_0_40px_rgba(212,175,55,0.18)] overflow-hidden" data-id="element-5511">
              <img src={card.img} alt={card.name} className="w-full h-full object-cover" data-id="element-5512" />
            </div>
            <RarityBadge rarity={card.rarity} data-id="element-5513" />
            {(card.type || card.set) && <div className="text-center" data-id="element-5514">
                {card.type && <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-5515">
                    Type · {card.type}
                  </p>}
                {card.set && <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase mt-1" data-id="element-5516">
                    Set · {card.set}
                  </p>}
              </div>}
          </div>

          {/* Right Col: Details */}
          <div className="flex-1 flex flex-col" data-id="element-5517">
            <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5518">
              {card.role || 'Court Figure'}
            </p>
            <h3 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-5" data-id="element-5519">
              {card.name}
            </h3>
            <OrnateDivider className="mb-6 justify-start" data-id="element-5520" />
            {card.oneLineLore && <p className="font-serif italic text-lg sm:text-xl text-[#FDFBF7]/90 mb-8 leading-relaxed" data-id="element-5521">
                "{card.oneLineLore}"
              </p>}

            <div className="grid grid-cols-1 gap-6" data-id="element-5522">
              {card.collectibleDescription && <div data-id="element-5523">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5524">
                    Collectible Description
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5525">
                    {card.collectibleDescription}
                  </p>
                </div>}
              {card.historicalMeaning && <div data-id="element-5526">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5527">
                    Historical Meaning
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5528">
                    {card.historicalMeaning}
                  </p>
                </div>}
              {card.whyItIsRare && <div data-id="element-5529">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5530">
                    Why It Is Rare
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5531">
                    {card.whyItIsRare}
                  </p>
                </div>}
              {card.collectorMeaning && <div data-id="element-5532">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5533">
                    Collector Meaning
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5534">
                    {card.collectorMeaning}
                  </p>
                </div>}
              {card.unlockPotential && <div data-id="element-5535">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5536">
                    Unlock Potential
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5537">
                    {card.unlockPotential}
                  </p>
                </div>}
              {card.emblem && <div data-id="element-5538">
                  <p className="text-[#D4AF37]/70 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-5539">
                    Emblem
                  </p>
                  <p className="text-[#FDFBF7]/75 text-sm font-light leading-relaxed" data-id="element-5540">
                    {card.emblem}
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
};
// Modal for browsing a collection that doesn't have full cards yet
interface FutureCollection {
  name: string;
  sub: string;
  rarity: string;
  img?: string;
  gradient?: string;
  description?: string;
  cardCount?: number;
  cards?: CourtCard[];
  isLive?: boolean;
}
const CollectionDetailModal = ({
  collection,
  onClose,
  onOpenCard
}: {
  collection: FutureCollection;
  onClose: () => void;
  onOpenCard?: (card: CourtCard) => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  const cardCount = collection.cardCount ?? 8;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-5541">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-5542">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.12),_transparent_50%)]" data-id="element-5543" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 p-6 sm:p-10 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-5544">
        <CornerOrnaments data-id="element-5545" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-5546">
          <X className="w-6 h-6" data-id="element-5547" />
        </button>

        <div className="text-center mb-8" data-id="element-5548">
          <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5549">
            {collection.sub}
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-5550">
            {collection.name}
          </h3>
          <div className="flex justify-center mb-5" data-id="element-5551">
            <RarityBadge rarity={collection.rarity} data-id="element-5552" />
          </div>
          <p className="max-w-2xl mx-auto text-[#FDFBF7]/65 text-sm font-light leading-relaxed" data-id="element-5553">
            {collection.description ?? 'This collection is being assembled in the Treasury workshop. Cards will be revealed in upcoming drops. Members at this rank receive priority access to first editions.'}
          </p>
        </div>

        <OrnateDivider className="mb-8" data-id="element-5554" />

        {collection.cards && collection.cards.length > 0 ? <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-5555">
              {collection.cards.map((card, i) => <FlipCard key={i} card={card} onOpenModal={() => {
            if (onOpenCard) {
              onClose();
              setTimeout(() => onOpenCard(card), 250);
            }
          }} data-id="element-5556" />)}
            </div>
            <p className="text-center text-[#D4AF37]/50 text-[10px] tracking-[0.3em] uppercase mt-10" data-id="element-5557">
              Hover to flip · Tap a card for the full lore
            </p>
          </> : <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-5558">
              {[...Array(cardCount)].map((_, i) => <div key={i} className="flex flex-col gap-2" data-id="element-5559">
                  <div className="relative aspect-[4/5] border border-[#D4AF37]/15 border-dashed bg-[#131B2B] overflow-hidden" data-id="element-5560">
                    <div className="absolute inset-3 border border-[#D4AF37]/10 rounded-t-full opacity-60" data-id="element-5561" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5562">
                      <Lock className="w-5 h-5 text-[#D4AF37]/40 mb-2" data-id="element-5563" />
                      <p className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37]/50" data-id="element-5564">
                        In Production
                      </p>
                    </div>
                  </div>
                  <p className="font-serif text-xs text-[#FDFBF7]/40 text-center" data-id="element-5565">
                    Card {String(i + 1).padStart(2, '0')}
                  </p>
                </div>)}
            </div>
            <p className="text-center text-[#D4AF37]/50 text-[10px] tracking-[0.3em] uppercase mt-8" data-id="element-5566">
              Drop schedule announced to members first
            </p>
          </>}
      </motion.div>
    </div>;
};
const FlipCard = ({
  card,
  onOpenModal
}: {
  card: CourtCard;
  onOpenModal: () => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  if (card.isLocked) {
    return <div className="flex flex-col gap-3" data-id="element-5567">
        <div className="relative aspect-[4/5] border border-dashed border-[#D4AF37]/20 bg-[#131B2B] flex flex-col items-center justify-center" data-id="element-5568">
          <Lock className="w-5 h-5 text-[#D4AF37]/40 mb-2" data-id="element-5569" />
          <p className="text-[8px] uppercase tracking-[0.25em] text-[#D4AF37]/50" data-id="element-5570">
            Locked
          </p>
        </div>
        <p className="font-serif text-xs text-[#FDFBF7]/40 text-center" data-id="element-5571">
          {card.name}
        </p>
      </div>;
  }
  return <div className="flex flex-col gap-3 cursor-pointer group" onMouseEnter={() => setIsFlipped(true)} onMouseLeave={() => setIsFlipped(false)} onClick={onOpenModal} data-id="element-5572">
      <div className="relative aspect-[4/5] perspective-1000" data-id="element-5573">
        <motion.div className="relative w-full h-full preserve-3d" animate={{
        rotateY: isFlipped ? 180 : 0
      }} transition={{
        duration: 0.6
      }} data-id="element-5574">
          {/* Front */}
          <div className="absolute inset-0 backface-hidden border border-[#D4AF37]/30 bg-[#131B2B] overflow-hidden" data-id="element-5575">
            <img src={card.img} alt={card.name} className="w-full h-full object-cover" data-id="element-5576" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" data-id="element-5577" />
            <div className="absolute bottom-0 left-0 right-0 p-3" data-id="element-5578">
              <p className="font-serif text-sm text-[#FDFBF7] leading-tight" data-id="element-5579">
                {card.name}
              </p>
              {card.role && <p className="text-[8px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mt-1" data-id="element-5580">
                  {card.role}
                </p>}
            </div>
          </div>
          {/* Back */}
          <div className="absolute inset-0 backface-hidden border border-[#D4AF37]/40 bg-[#0B0F19] p-4 flex flex-col" style={{
          transform: 'rotateY(180deg)'
        }} data-id="element-5581">
            <div className="flex justify-between items-start mb-3" data-id="element-5582">
              <RarityBadge rarity={card.rarity} data-id="element-5583" />
            </div>
            <p className="font-serif text-sm text-[#FDFBF7] mb-2 leading-tight" data-id="element-5584">
              {card.name}
            </p>
            {card.oneLineLore && <p className="text-[10px] text-[#FDFBF7]/60 italic font-light leading-relaxed mb-3" data-id="element-5585">
                "{card.oneLineLore}"
              </p>}
            <div className="mt-auto pt-2 border-t border-[#D4AF37]/20" data-id="element-5586">
              <p className="text-[8px] tracking-[0.25em] uppercase text-[#D4AF37] text-center" data-id="element-5587">
                Tap for full lore
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
};
// ---------- Collections Data ----------
const lahoreCourtCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  name: 'Maharaja Ranjit Singh',
  rarity: "Founders' Edition",
  role: 'Sher-e-Punjab',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The foundational portrait of Lahore Treasury.',
  collectibleDescription: 'Maharaja Ranjit Singh stands at the centre of the entire world: not as a fantasy emperor, but as the one-eyed sovereign who turned Lahore into the heart of Sikh power. This card captures him as calm, weathered, restrained, and impossible to ignore. His authority is not loud. It is gravitational. The pearls, ivory court dress, jewelwork, and Lahore backdrop all point to a ruler whose strength came from coalition-building, diplomacy, military discipline, and symbolic command.',
  historicalMeaning: 'Ranjit Singh unified the Sikh misls and built the Sikh Empire with Lahore as its capital. His court became a space where Sikh, Muslim, Hindu, European, Punjabi, Persianate, and regional traditions intersected. He is remembered as Sher-e-Punjab — the Lion of Punjab — but the best version of this card should emphasize his intelligence as much as his power.',
  whyItIsRare: 'This is a Founders’ Edition because it is the anchor card of the whole system. Without Ranjit Singh, there is no Lahore Treasury. This card should be treated like card #001: the sovereign key to the collection.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
  name: 'Maharani Jind Kaur',
  rarity: "Founders' Edition",
  role: 'Regent of Punjab',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The regent flame of the Khalsa court: composed, elegant, politically aware, and unbroken.',
  collectibleDescription: 'Maharani Jind Kaur appears as the regent flame of the Khalsa court: composed, elegant, politically aware, and unbroken. This card should feel powerful without making her look ornamental. She is not merely royal decoration. She is authority under pressure. The strongest version shows her in refined court dress, surrounded by manuscripts, jewels, and chamber architecture — a woman of strategy, inheritance, and resistance.',
  historicalMeaning: 'Maharani Jind Kaur, the youngest wife of Maharaja Ranjit Singh and mother of Maharaja Duleep Singh, became regent after Ranjit Singh’s death. Her life carries the drama of empire, loss, colonial pressure, exile, and memory. She is one of the most important female figures connected to the fall and afterlife of the Sikh Empire.',
  whyItIsRare: 'Her card deserves Founders’ Edition treatment because she is essential to the story of sovereignty after Ranjit Singh. She represents continuity, resistance, and the cost of imperial collapse. For collectors, this is not just a portrait. It is a card of endurance.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
  name: 'Hari Singh Nalwa',
  rarity: 'Epic',
  role: 'Commander-in-Chief',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The frontier prestige card. His presence should feel martial, strategic, and severe.',
  collectibleDescription: 'Hari Singh Nalwa is the frontier prestige card. His presence should feel martial, strategic, and severe — the empire’s shield facing west. The portrait uses battlefield depth, dark blue court textiles, gold military detail, and frontier architecture to frame him as both commander and legend. This is one of the most naturally collectible warrior cards in the whole system.',
  historicalMeaning: 'Hari Singh Nalwa was one of the most famous commanders of the Sikh Empire. He is closely associated with campaigns and administration along the northwestern frontier, including Peshawar and Jamrud. His reputation became almost mythic because he represented the empire’s ability to project power into contested frontier zones.',
  whyItIsRare: 'This card should be Epic because Nalwa is not simply a court figure. He is a symbol of military reach. He belongs to the tier of cards that collectors will want for strength, courage, and prestige.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/h1jugirBbdMAbGN9UBoKma/Akali_Phula_Singh.png',
  name: 'Akali Phula Singh',
  rarity: 'Epic',
  role: 'Jathedar · Akal Takht',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'He represents religious conviction, martial discipline, and the fierce independence of the Akali tradition.',
  collectibleDescription: 'Akali Phula Singh’s card should feel sharper and more austere than the other court portraits. He represents religious conviction, martial discipline, and the fierce independence of the Akali tradition. The blue and saffron visual language works well here: deep indigo for discipline, gold for authority, and weaponry as a sign of duty rather than ornament.',
  historicalMeaning: 'Akali Phula Singh was a prominent Nihang/Akali leader and military figure associated with the Sikh Empire. His identity sits at the intersection of faith, martial culture, and sovereign obligation. He was not a polished courtier. He carried the energy of the Panth’s warrior tradition into the imperial world.',
  whyItIsRare: 'This card should be Epic because it represents sacred martial authority, not just court rank. Collectors should feel this is a card of discipline, fearlessness, and spiritual force.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  name: 'Moran Sarkar',
  rarity: 'Treasury Rare',
  role: 'Royal Consort',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'She should feel composed, intelligent, and magnetic.',
  collectibleDescription: 'Moran Sarkar’s card adds beauty, artistry, and cultural sophistication to Lahore Treasury. She should feel composed, intelligent, and magnetic — not generic, not over-sexualized, and not reduced to performance. Her card works best as a chamber-culture collectible: music, poetry, fragrance, candlelight, embroidered textiles, and inner court refinement. It represents the cultural life of Lahore beyond military power.',
  historicalMeaning: 'Moran Sarkar is remembered as a dancer and companion associated with Maharaja Ranjit Singh’s court. Her story opens a different doorway into the empire: not armies and treaties, but music, patronage, intimacy, performance, and courtly culture.',
  whyItIsRare: 'This is Treasury Rare because it captures a softer but very powerful part of the court. Many people will collect warriors and rulers. Moran’s card gives the collection depth, elegance, and emotional contrast.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/4QNmccUw9BGQnbMJ8r6PyR/Fakir_Azzizuddin.png',
  name: 'Fakir Azizuddin',
  rarity: 'Treasury Rare',
  role: 'Foreign Minister',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'The card of diplomacy, counsel, and administrative intelligence.',
  collectibleDescription: 'Fakir Azizuddin is the card of diplomacy, counsel, and administrative intelligence. His portrait should feel learned, composed, and quietly powerful — a man whose influence operated through words, judgment, and trust. The manuscripts, books, lamps, and court setting make this card feel like a piece of political memory rather than a battlefield trophy.',
  historicalMeaning: 'Fakir Azizuddin was one of Maharaja Ranjit Singh’s most trusted ministers and diplomats. He played an important role in the Lahore Darbar’s external relations and helped represent the sophistication of the court’s political machinery.',
  whyItIsRare: 'This is Treasury Rare because his value is not obvious to casual viewers but essential to anyone who understands how empires actually function. Armies win battles; advisers keep states alive.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5exEPKf1ian4PFKh4zGwAK/Lehna_Singh_Majathia.png',
  name: 'Lehna Singh Majithia',
  rarity: 'Treasury Rare',
  role: 'Governor · Inventor',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'Elite governance, aristocratic service, and strategic continuity.',
  collectibleDescription: 'Lehna Singh Majithia’s card represents elite governance, aristocratic service, and strategic continuity within the Sikh Empire. The card should feel refined, intelligent, and administrative rather than purely martial. This is a court-insider collectible — the kind of card that rewards people who go deeper than the headline figures.',
  historicalMeaning: 'The Majithia family was important within Sikh political and military networks. Lehna Singh Majithia is associated with the aristocratic and administrative world that helped sustain the empire’s institutions.',
  whyItIsRare: 'This is Treasury Rare because it is a knowledge card. It signals that the collector understands the deeper machinery of the Lahore Court, not just the famous names.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/t7XKonR6TRn3gfVAMxWwao/Dhian_Singh_Dhogra.png',
  name: 'Dhian Singh Dogra',
  rarity: 'Treasury Rare',
  role: 'Prime Minister',
  type: 'Portrait',
  set: 'Lahore Court',
  oneLineLore: 'High office, ambition, influence, and the complicated inner politics of the Lahore Darbar.',
  collectibleDescription: 'Dhian Singh Dogra’s card should feel politically charged. He represents high office, ambition, influence, and the complicated inner politics of the Lahore Darbar. Visually, this card should be regal but slightly tense — a court figure whose power came from proximity to the throne.',
  historicalMeaning: 'Dhian Singh Dogra was a major noble and court figure under Maharaja Ranjit Singh. The Dogra brothers played significant roles in the politics of the Sikh Empire, especially during the unstable succession period after Ranjit Singh’s death.',
  whyItIsRare: 'This is Treasury Rare because it belongs to the politics-behind-the-throne layer of the collection. It is not just a portrait. It is a card of influence, court strategy, and risk.'
}];
const foundersSealsCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/ecLy6LTMHkK3K5wmXXnV8A/Royal_Seal_of_Maharaja_Ranjit_Singh.png',
  name: 'Royal Seal of Ranjit Singh',
  rarity: "Founders' Edition",
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'The sovereign mark of the Lahore Treasury system.',
  collectibleDescription: 'This is the sovereign mark of the Lahore Treasury system. The emerald-like signet, wax impression, and royal document setting make it feel like a direct artifact from the court’s inner archive. The card’s power comes from restraint: one seal, one name, one sovereign presence.',
  historicalMeaning: 'Maharaja Ranjit Singh’s seal is associated with sovereign authority and the phrase “Akal Sahai” — with the help of the Timeless One. This linked rulership to divine assistance rather than personal vanity.',
  whyItIsRare: 'This is Founders’ Edition because it is the signature of sovereignty. It should function as one of the most important non-portrait collectibles in the entire system.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/8bHt2Mjm6ohqpqMMqE5QjE/Seal_of_Lahore_Darbar.png',
  name: 'Seal of the Lahore Darbar',
  rarity: 'Treasury Rare',
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'This card represents the authority of the court itself.',
  collectibleDescription: 'This card represents the authority of the court itself. Unlike the personal seal of the Maharaja, this is the voice of the Darbar — the institution, the chamber, the administrative machine. The composition of documents, wax, and court instruments makes it feel procedural, official, and intimate.',
  historicalMeaning: 'The Lahore Darbar was not just a royal court. It was the political centre of the Sikh Empire, where petitions, appointments, diplomatic decisions, revenue matters, and military administration flowed.',
  whyItIsRare: 'This is Treasury Rare because it represents institutional authority. Collectors who hold this card are not just collecting a ruler. They are collecting the machinery of rule.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/doWCiDvV7FAzbmKrT4JQRB/Mohr_of_Khalsa_Sarkar.png',
  name: 'Mohr of Khalsa Sarkar',
  rarity: 'Epic',
  type: 'Seal',
  set: "Founders' Seals",
  oneLineLore: 'This is the state-power seal.',
  collectibleDescription: 'This is the state-power seal. It should feel more severe than decorative: metal, wax, paper, dark velvet, and the Khalsa symbol of command. This collectible works because it connects sacred identity with state authority.',
  historicalMeaning: '“Khalsa Sarkar” refers to the Sikh state under the Lahore Darbar. The phrase carries the idea that sovereignty was not merely personal rule, but bound to the Khalsa and the authority of the Panth.',
  whyItIsRare: 'It is Epic because it rises above individual court identity. It represents statehood itself: sovereignty under the Khalsa name.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/4hxNpSJLoi4bC8T9wwuQC5/Akal_Sahai_Signet.png',
  name: 'Akal Sahai Signet',
  rarity: 'Treasury Night Exclusive',
  type: 'Seal / Signet',
  set: "Founders' Seals",
  oneLineLore: 'A night-unlock object: intimate, luminous, and spiritually charged.',
  collectibleDescription: 'The Akal Sahai Signet is a night-unlock object: intimate, luminous, and spiritually charged. The emerald face, red wax, pearl strands, and shadowed court setting make it feel like a relic revealed under candlelight.',
  historicalMeaning: '“Akal Sahai” means “with the help of the Timeless One.” As a phrase of sovereign authority, it links rule, legitimacy, and divine protection.',
  whyItIsRare: 'This should be a Treasury Night Exclusive because it feels ceremonial. It is the kind of object that should only unlock through attendance, participation, or founder-level access.'
}];
const courtTreasuresCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
  name: 'Koh-i-Noor Armlet',
  rarity: "Founders' Edition",
  type: 'Treasure / Regalia',
  set: 'Court Treasures',
  oneLineLore: 'The jewel card of the entire treasury.',
  collectibleDescription: 'The Koh-i-Noor Armlet is the jewel card of the entire treasury. It should feel heavy, luminous, and dangerous — beauty carrying political consequence. The card’s power comes from placing the diamond within a court object, not simply showing the stone. The armlet implies possession, sovereignty, ceremony, and loss.',
  historicalMeaning: 'The Koh-i-Noor became associated with Maharaja Ranjit Singh after it came into his possession from Shah Shuja. It later became one of the most famous symbols of imperial transfer, colonial possession, and contested memory.',
  whyItIsRare: 'This is Founders’ Edition because the Koh-i-Noor is not just a jewel. It is one of the most recognizable symbols of sovereignty connected to Lahore. It should be among the highest-status cards.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
  name: 'Golden Throne of Lahore',
  rarity: 'Treasury Night Exclusive',
  type: 'Court Object',
  set: 'Court Treasures',
  oneLineLore: 'The seat of sovereign splendour.',
  collectibleDescription: 'The Golden Throne of Lahore is the seat of sovereign splendour. This card should feel formal, quiet, and powerful — the throne waiting before or after the ruler appears. It is effective because absence creates drama. The empty throne invites the collector to imagine the court around it.',
  historicalMeaning: 'The Sikh Empire’s visual culture included courtly seating, ceremonial display, regalia, and spatial hierarchy. A throne object represents not only the Maharaja but the structure of power around him.',
  whyItIsRare: 'This is Treasury Rare because it represents the physical theatre of sovereignty. As a Treasury Night Exclusive, it can also function as a premium unlock tied to live-event status.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/k7bSK4nqttzFAiN3UnZZs2/Sarpech_of_Soverign_Court.png',
  name: 'Sarpech of the Sovereign Court',
  rarity: 'Treasury Rare',
  type: 'Regalia',
  set: 'Court Treasures',
  oneLineLore: 'Pure prestige. Pearls, feathers, goldwork, emeralds.',
  collectibleDescription: 'The sarpech card is pure prestige. Pearls, feathers, goldwork, emeralds, and a central jewel make it feel ceremonial and immediately collectible. This is not a weapon. It is a mark of presence. It says: the wearer has entered the court with rank.',
  historicalMeaning: 'Turban ornaments and jeweled headpieces were important signs of courtly status across South Asian royal cultures. In the Lahore Court context, the sarpech signals elegance, rank, and refined authority.',
  whyItIsRare: 'This is Treasury Rare because it is symbolic rather than functional. It is for collectors drawn to elegance, court rank, and status.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/cP1VhHHXb1qWwnqErsLGjY/Sword_of_Akal_Sahai.png',
  name: 'Sword of Akal Sahai',
  rarity: 'Epic',
  type: 'Regalia / Weapon',
  set: 'Court Treasures',
  oneLineLore: 'The blade of sovereign resolve.',
  collectibleDescription: 'The Sword of Akal Sahai is the blade of sovereign resolve. It should feel ceremonial but capable — a weapon that belongs equally to court, oath, and command. The best version is not over-bloody or aggressive. It is polished, engraved, devotional, and severe.',
  historicalMeaning: 'Inscribed blades and ceremonial swords were part of South Asian martial and court culture. In a Sikh Empire context, the sword also carries deep symbolic meaning connected to justice, sovereignty, and responsibility.',
  whyItIsRare: 'This is Epic because it fuses spiritual authority with martial power. It is not just a beautiful object. It is a statement of rule under duty.'
}];
const courtScenesCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/i8yqzvFJ6TFd6dTb6YJFfy/Lahor_Darbar.png',
  name: 'The Lahore Darbar',
  rarity: "Founders' Edition",
  type: 'Court Scene',
  set: 'Court Scenes',
  oneLineLore: 'The central scene card: the court as theatre, government, petition chamber, and sovereign stage.',
  collectibleDescription: 'The Lahore Darbar is the central scene card: the court as theatre, government, petition chamber, and sovereign stage. Ranjit Singh sits within a living environment of advisers, petitioners, scribes, guards, and architecture. It feels like the viewer has stepped into the moment before a decision is made.',
  historicalMeaning: 'The Lahore Darbar was the governing centre of the Sikh Empire. It was where royal authority, administration, diplomacy, and public petition intersected.',
  whyItIsRare: 'This is Founders’ Edition because it gives the entire project its core setting. It is the court itself — the place every other collectible returns to.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ndFukn5EhjaGeCm7W2a9F8/Council_of_Empire.png',
  name: 'Council of Empire',
  rarity: 'Treasury Rare',
  type: 'Court Scene',
  set: 'Court Scenes',
  oneLineLore: 'A quieter, smarter card. It shows maps, manuscripts, advisers, and strategic conversation.',
  collectibleDescription: 'Council of Empire is a quieter, smarter card. It shows maps, manuscripts, advisers, and strategic conversation. This card is collectible because it captures power before action. Not war. Not spectacle. Deliberation.',
  historicalMeaning: 'The Sikh Empire was held together through military force, diplomacy, revenue administration, and elite negotiation. Council scenes represent the planning layer behind imperial stability.',
  whyItIsRare: 'This is Treasury Rare because it is a strategy card. It will appeal to collectors who value intelligence, counsel, and statecraft.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/qXUGTTLzFDWU3yPZqkhw1f/Review_of_the_Khalsa.png',
  name: 'Review of the Khalsa',
  rarity: 'Epic',
  type: 'Court Scene / Martial Scene',
  set: 'Court Scenes',
  oneLineLore: 'A parade of discipline and power.',
  collectibleDescription: 'Review of the Khalsa is a parade of discipline and power. The ruler on horseback, the standards, the soldiers, and the fortress backdrop create a card of imperial confidence. This is one of the most visually accessible cards for general audiences.',
  historicalMeaning: 'Military reviews and ceremonial displays communicated order, readiness, and royal authority. The Khalsa army was a central force in the rise and defence of the Sikh Empire.',
  whyItIsRare: 'This is Epic because it shows collective power. It is not one hero. It is the disciplined force of the empire.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/2NoPLkWP9pP8MsVwhpJBvm/Mehfil_of_Moran_Sarkar.png',
  name: 'Mehfil of Moran Sarkar',
  rarity: 'Live Night Edition',
  type: 'Court Scene / Chamber Culture',
  set: 'Court Scenes',
  oneLineLore: 'This card captures the inner atmosphere of the court.',
  collectibleDescription: 'This card captures the inner atmosphere of the court: music, candlelight, poetry, refined performance, and social presence. It should feel intimate and cinematic — a collectible that could connect directly to a live event, musical moment, or sensory unlock.',
  historicalMeaning: 'Courts were not only military and administrative spaces. They were centres of patronage, music, poetry, conversation, and cultural refinement.',
  whyItIsRare: 'This is a Live Night Edition because it naturally belongs to an event experience. It can unlock during a musical moment, performance sequence, or chamber-culture activation.'
}];
const memoryFragmentsCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/pUc7dhUDTQ8ibLS8igHkeA/Last_Light_of_Lahore.png',
  name: 'Last Light of Lahore',
  rarity: 'Epic',
  type: 'Memory Fragment',
  set: 'Fracture & Memory',
  oneLineLore: 'An empty chamber at sunset. The moment after sound leaves the court.',
  collectibleDescription: 'An empty chamber at sunset. A throne-like chair. Documents left behind. Lahore visible in the distance. This card captures the moment after sound leaves the court.',
  historicalMeaning: 'After Ranjit Singh’s death, the Sikh Empire entered a turbulent period of succession struggles, political instability, British pressure, and eventual annexation. This card symbolizes that fading light.',
  whyItIsRare: 'This is Epic because it carries emotional scale. It is not an object. It is the feeling of an era ending.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mpWTPqMtxSXCLW7wBLeGe9/Letter_from_Exile.png',
  name: 'Letter from Exile',
  rarity: 'Treasury Rare',
  type: 'Memory Fragment',
  set: 'Fracture & Memory',
  oneLineLore: 'A voice carried across distance.',
  collectibleDescription: 'A sealed letter, pearls, cloth, portrait, and shadow. This is one of the most intimate cards in the set. It should feel like a voice carried across distance.',
  historicalMeaning: 'This card can connect to Maharani Jind Kaur, exile, separation, colonial displacement, and the afterlife of the Sikh Empire in memory and correspondence.',
  whyItIsRare: 'This is Treasury Rare because it is emotionally specific. It is for collectors who care about voice, absence, and survival.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/xqJSFqG4cjYr84cTwzpgB1/Broken_Seal_of_the_Khalsa.png',
  name: 'Broken Seal of the Khalsa',
  rarity: 'Live Night Edition',
  type: 'Memory Fragment / Seal',
  set: 'Fracture & Memory',
  oneLineLore: 'Even when institutions break, memory remains collectible.',
  collectibleDescription: 'A cracked wax seal, torn papers, and scattered authority. This is one of the strongest symbolic cards. It shows that even when institutions break, memory remains collectible.',
  historicalMeaning: 'The broken seal represents the collapse of sovereign authority after the fall of the Sikh Empire and the absorption of Punjab into British rule.',
  whyItIsRare: 'This should be Live Night Edition because it can be unlocked at the emotional turning point of the immersive experience.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5yTx3Dg8JyVsPEfTEouexU/Desparting_the_Koh-i-Noor.png',
  name: 'The Departing Koh-i-Noor',
  rarity: 'Treasury Night Exclusive',
  type: 'Memory Fragment / Treasure',
  set: 'Fracture & Memory',
  oneLineLore: 'The missing jewel becomes the focal point.',
  collectibleDescription: 'An open jewel box. A missing light. Pearls, papers, and candlelit silence. This card is powerful because the object is absent. The missing jewel becomes the focal point.',
  historicalMeaning: 'The Koh-i-Noor’s removal from Lahore became one of the most famous symbols of imperial loss and contested heritage.',
  whyItIsRare: 'This is Treasury Night Exclusive because it should feel almost sacred to the live experience. It is not just a card. It is a moment of loss.'
}];
const sacredAuthorityCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/vRFN3KMcBvDZegNaWmJsBE/Hukamnama_of_the_Akal_Court.png',
  name: 'Hukamnama of the Akal Court',
  rarity: "Founders' Edition",
  type: 'Sacred Document',
  set: 'Sacred Authority',
  oneLineLore: 'A sacred command, resting under candlelight.',
  collectibleDescription: 'A sacred command, resting under candlelight. The card should feel quiet, elevated, and archival.',
  historicalMeaning: 'A hukamnama is a written order or command, and in Sikh contexts it carries sacred and institutional significance. This card links word, authority, and responsibility.',
  whyItIsRare: 'This is Founders’ Edition because it establishes that Lahore Treasury is not only royal. It is rooted in sacred authority.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/aP3xY1r5KWz2UUGdM4Kfc1/Nishan_Sahib_of_Lahore.png',
  name: 'Nishan Sahib of Lahore',
  rarity: 'Epic',
  type: 'Sacred Banner',
  set: 'Sacred Authority',
  oneLineLore: 'The symbol of timeless allegiance.',
  collectibleDescription: 'The Nishan Sahib card is visually bold: saffron flag, sky, Lahore backdrop, and the symbol of timeless allegiance.',
  historicalMeaning: 'The Nishan Sahib is the Sikh flag. It is a sacred sign of Sikh presence, identity, and collective commitment.',
  whyItIsRare: 'This is Epic because it is not merely an object. It is a banner of identity. It carries collective force.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/fk48rB7Xh8vfhU38qhWpYz/Chaur Sahib of Reverence.png',
  name: 'Chaur Sahib of Reverence',
  rarity: 'Treasury Rare',
  type: 'Sacred Object',
  set: 'Sacred Authority',
  oneLineLore: 'Graceful, tactile, and devotional.',
  collectibleDescription: 'The Chaur Sahib card is graceful, tactile, and devotional. The white hair, jeweled handle, velvet, and soft light create a card of service.',
  historicalMeaning: 'The Chaur Sahib is waved as a mark of reverence before Guru Granth Sahib. It symbolizes honour and humility.',
  whyItIsRare: 'This is Treasury Rare because it represents service before sovereignty. It teaches the hierarchy of values in the world.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/3j4pU1Rts4tctWKYUbVdyS/Palki_of_Soverign_Devotion.png',
  name: 'Palki of Sovereign Devotion',
  rarity: 'Treasury Night Exclusive',
  type: 'Sacred Object / Ceremonial Object',
  set: 'Sacred Authority',
  oneLineLore: 'The canopy of presence and honour.',
  collectibleDescription: 'The Palki card is visually rich: gold, canopy, cushion, embroidered cloth, and sacred presence. This should be treated with the most care.',
  historicalMeaning: 'The Palki Sahib is associated with the respectful placement and procession of Guru Granth Sahib. It represents devotion, honour, and ceremonial reverence.',
  whyItIsRare: 'This is Treasury Night Exclusive because it should be tied to a respectful, carefully designed live unlock — not a casual collectible drop.'
}];
const frontierDefendersCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/aSmUF7b6BWtqGCanyn7aaa/Shield_of_Hari_Singh_Nalwa.png',
  name: 'Shield of Hari Singh Nalwa',
  rarity: 'Epic',
  type: 'Martial Object',
  set: 'Frontier Defenders',
  oneLineLore: 'A shield of the western march.',
  collectibleDescription: 'This is a shield of the western march: dark metal, gold engraving, emerald bosses, and frontier mountains behind it.',
  historicalMeaning: 'Hari Singh Nalwa’s legacy is closely tied to frontier defence and the projection of Sikh power toward the northwest.',
  whyItIsRare: 'This is Epic because it represents defence at the empire’s edge. It is protective, iconic, and commander-linked.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/5ufBpHR9ZV9nnv2ZeAwmvQ/Jamrud_War_Standard.png',
  name: 'Jamrud War Standard',
  rarity: 'Treasury Rare',
  type: 'War Standard',
  set: 'Frontier Defenders',
  oneLineLore: 'A banner of the western gate.',
  collectibleDescription: 'The Jamrud War Standard is a banner of the western gate. It should feel wind-worn, ceremonial, and battle-ready.',
  historicalMeaning: 'Jamrud Fort and its frontier context are tied to the Sikh Empire’s western military story. A war standard turns place into symbol.',
  whyItIsRare: 'This is Treasury Rare because standards are identity objects. They gather soldiers, signal allegiance, and mark territory.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/8onDaZ9cdUq8uHFDtGepEP/Frontier_Matchlock_of_the_Khalsa.png',
  name: 'Frontier Matchlock of the Khalsa',
  rarity: 'Rare',
  type: 'Firearm / Martial Object',
  set: 'Frontier Defenders',
  oneLineLore: 'Rugged and practical.',
  collectibleDescription: 'The matchlock card is rugged and practical. It should look used, decorated, and dangerous without becoming modern or generic.',
  historicalMeaning: 'Firearms were part of early modern and 19th-century military culture in South Asia. This card represents the practical technology of frontier conflict.',
  whyItIsRare: 'This is Rare because it grounds the collection in material military reality. Not every martial object is a jewel. Some are tools of survival.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/grqP9Jkk1cJNAx6sRiboXR/Helm_of_the_Khyber_March.png',
  name: 'Helm of the Khyber March',
  rarity: 'Live Night Edition',
  type: 'Armour',
  set: 'Frontier Defenders',
  oneLineLore: 'A dramatic helmet card.',
  collectibleDescription: 'This is a dramatic helmet card: chainmail, steel, goldwork, dark plume, frontier fort in the distance.',
  historicalMeaning: 'Armour and helmets represent the personal risk of martial leadership and the harshness of frontier defence.',
  whyItIsRare: 'This is Live Night Edition because it has strong costume, photo-op, and immersive-event potential. It can connect to turban, armour, or warrior identity experiences.'
}];
const lahoreCourtInnerLifeCollection: CourtCard[] = [{
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
  name: "The Maharaja's Private Chamber",
  rarity: 'Treasury Rare',
  type: 'Scene',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'Where the Lion of Punjab rested and reflected.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/nVFG4hPLbQSgSkuEzm99Kr/Jind_Kaur.png',
  name: "Maharani's Mirror",
  rarity: 'Epic',
  type: 'Artifact',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'A reflection of power and poise.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
  name: "Commander's Correspondence",
  rarity: 'Mint',
  type: 'Document',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'Letters from the front, written in haste and honour.'
}, {
  img: 'https://cdn.magicpatterns.com/uploads/mma3UnUB3ESvFrjVFpoZd7/Moran_Sarkar.png',
  name: "Court Musician's Instrument",
  rarity: 'Mint',
  type: 'Artifact',
  set: 'Lahore Court Inner Life',
  oneLineLore: 'The strings that sang the stories of the Darbar.'
}];
const otherCollections: FutureCollection[] = [{
  name: "Founders' Seals",
  sub: 'Sovereign Marks',
  rarity: "Founders' Edition",
  description: 'The seals and signets that authorized empire.',
  cards: foundersSealsCollection,
  isLive: true
}, {
  name: 'Court Treasures',
  sub: 'Royal Regalia',
  rarity: "Founders' Edition",
  description: 'Crown jewels, thrones, and ceremonial arms of the Lahore Darbar.',
  cards: courtTreasuresCollection,
  isLive: true
}, {
  name: 'Court Scenes',
  sub: 'Living Tableaux',
  rarity: 'Treasury Rare',
  description: 'Painted moments from the inner life of the empire.',
  cards: courtScenesCollection,
  isLive: true
}, {
  name: 'Fracture & Memory',
  sub: 'The Empire After the Light',
  rarity: 'Treasury Rare',
  description: 'Cards of loss, exile, silence, broken seals, and memory that survives collapse.',
  cards: memoryFragmentsCollection,
  isLive: true
}, {
  name: 'Sacred Authority',
  sub: 'Reverence Before Rule',
  rarity: "Founders' Edition",
  description: 'Spiritual objects and symbols that shaped Sikh sovereignty: hukamnamas, banners, and ceremonial regalia.',
  cards: sacredAuthorityCollection,
  isLive: true
}, {
  name: 'Frontier Defenders',
  sub: 'Martial Prestige Objects',
  rarity: 'Epic',
  description: 'Shields, standards, matchlocks, and armour tied to the western edge of the empire.',
  cards: frontierDefendersCollection,
  isLive: true
}, {
  name: 'Lahore Court Inner Life',
  sub: 'Intimate Spaces',
  rarity: 'Treasury Rare',
  description: 'The private chambers, personal effects, and quiet moments of the royal court.',
  cards: lahoreCourtInnerLifeCollection,
  isLive: true
}];
const upcomingCollections = [{
  name: "The Maharaja's Inner Circle",
  sub: 'Closest confidants'
}, {
  name: 'The Khalsa Army',
  sub: 'Regiments & commanders'
}, {
  name: 'The Royal Archive',
  sub: 'Letters & manuscripts'
}, {
  name: 'The Treasury Vault',
  sub: 'Coins & ingots'
}, {
  name: 'Lost Relics of Empire',
  sub: 'Recovered fragments'
}, {
  name: 'The Darbar Ceremonies',
  sub: 'Rituals of court'
}, {
  name: 'The Cities of the Sarkar',
  sub: 'Capitals & strongholds'
}, {
  name: "The Empire's Edge",
  sub: 'Frontier outposts'
}];
// ---------- Mission Section ----------
const MissionSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative overflow-hidden border-t border-[#D4AF37]/10" data-id="element-5588">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)]" data-id="element-5589" />
    <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-5590">
      <SectionHeader eyebrow="The Mission" title={<>
            REAL HISTORY.{' '}
            <span className="text-[#D4AF37]" data-id="element-5592">REAL CONNECTION.</span>
          </>} data-id="element-5591" />
      <div className="relative px-4 sm:px-8 pt-12 sm:pt-16 pb-8 sm:pb-10" data-id="element-5593">
        {/* Massive decorative opening quote */}
        <span aria-hidden="true" className="absolute left-0 top-0 font-serif text-[160px] sm:text-[220px] leading-[0.8] text-[#D4AF37]/25 select-none pointer-events-none" data-id="element-5594">
          &ldquo;
        </span>

        {/* Quote text */}
        <blockquote className="relative font-serif text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-[1.5] tracking-tight max-w-3xl mx-auto text-center" data-id="element-5595">
          Treasures of the Sikh Empire is a digital bridge to the physical past.
          Every artifact, every figure, and every location is{' '}
          <span className="text-[#D4AF37] italic" data-id="element-5596">real history</span> from the
          Sikh Kingdoms. By collecting and trading, you aren&apos;t just playing
          a game — you are{' '}
          <span className="text-[#D4AF37] italic" data-id="element-5597">
            recovering a lost legacy
          </span>{' '}
          and building a modern Sangat.
        </blockquote>

        {/* Attribution */}
        <div className="mt-10 sm:mt-12 flex flex-col items-center gap-3" data-id="element-5598">
          <span className="h-px w-12 bg-[#D4AF37]/50" data-id="element-5599" />
          <div className="text-center" data-id="element-5600">
            <p className="font-serif text-base sm:text-lg text-[#D4AF37] tracking-wide" data-id="element-5601">
              Ajaypal Singh
            </p>
            <p className="text-[#FDFBF7]/50 text-[10px] sm:text-[11px] tracking-[0.35em] uppercase font-light mt-1.5" data-id="element-5602">
              Sikh Empire Historian
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ---------- Collections Section ----------
const CollectionsSection = () => {
  const [openCard, setOpenCard] = useState<CourtCard | null>(null);
  const [openCollection, setOpenCollection] = useState<FutureCollection | null>(null);
  return <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5603">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.08),_transparent_60%)]" data-id="element-5604" />
      <div className="max-w-7xl mx-auto relative z-10" data-id="element-5605">
        <SectionHeader eyebrow="The Collections" title={<>
              THE LAHORE COURT{' '}
              <span className="text-[#D4AF37]" data-id="element-5607">COLLECTION</span>
            </>} description="The flagship collection — eight figures who defined the Sikh Empire. Each card hand-illustrated, each story rooted in real history." data-id="element-5606" />

        {/* Sponsor credit — flagship collection partnership */}
        <div className="flex items-center justify-center gap-4 mb-12 -mt-4" data-id="element-5608">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]/40" data-id="element-5609" />
          <div className="flex items-center gap-2.5 px-4 py-2 border border-[#D4AF37]/30 bg-[#131B2B]/60" data-id="element-5610">
            <span className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-5611">
              Presented in partnership with
            </span>
            <span className="font-serif text-base text-[#D4AF37] tracking-wide" data-id="element-5612">
              Akal Steel
            </span>
          </div>
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]/40" data-id="element-5613" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-20" data-id="element-5614">
          {lahoreCourtCollection.map((card, i) => <FlipCard key={i} card={card} onOpenModal={() => setOpenCard(card)} data-id="element-5615" />)}
        </div>

        <div className="text-center mb-12" data-id="element-5616">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5617">
            The Other 7 Collections
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7]" data-id="element-5618">
            Beyond the <span className="text-[#D4AF37]" data-id="element-5619">Court</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-5620">
          {otherCollections.map((col, idx) => {
          const peekCards = (col.cards || []).slice(0, 3);
          const glowColor = col.rarity === "Founders' Edition" ? 'rgba(212,175,55,0.45)' : col.rarity === 'Treasury Rare' ? 'rgba(212,175,55,0.28)' : col.rarity === 'Epic' ? 'rgba(139,0,0,0.35)' : 'rgba(212,175,55,0.18)';
          return <motion.button key={col.name} onClick={() => setOpenCollection(col)} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: idx * 0.05,
            ease: 'easeOut'
          }} whileHover={{
            y: -6
          }} className="text-left border border-[#D4AF37]/20 bg-[#131B2B] hover:border-[#D4AF37]/60 transition-all group relative overflow-hidden flex flex-col" data-id="element-5621">
                {/* === Hero artwork: card stack peek === */}
                <div className="relative aspect-[5/3] bg-[#0B0F19] overflow-hidden border-b border-[#D4AF37]/15" data-id="element-5622">
                  {/* Rarity-tinted glow */}
                  <div aria-hidden="true" className="absolute inset-0 transition-opacity duration-500 opacity-50 group-hover:opacity-100" style={{
                background: `radial-gradient(ellipse at center, ${glowColor}, transparent 70%)`
              }} data-id="element-5623" />
                  {/* Subtle dust particles */}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,_rgba(212,175,55,0.08),_transparent_50%)]" data-id="element-5624" />

                  {/* Card stack — fanned, animates on hover */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-5625">
                    {peekCards.length > 0 ? peekCards.map((card, i) => {
                  const isCenter = i === 1 || peekCards.length === 1;
                  const offsetX = peekCards.length === 1 ? 0 : (i - 1) * 38;
                  const rotate = peekCards.length === 1 ? 0 : (i - 1) * 7;
                  return <motion.div key={i} initial={false} animate={{
                    x: offsetX,
                    rotate,
                    scale: isCenter ? 1 : 0.92
                  }} whileHover={{}} style={{
                    zIndex: isCenter ? 10 : 5 - i
                  }} className="absolute w-[88px] sm:w-[104px] aspect-[4/5] border border-[#D4AF37]/50 shadow-[0_6px_24px_rgba(0,0,0,0.6)] overflow-hidden bg-[#131B2B] transition-all duration-500 group-hover:[transform-origin:center_bottom]" data-id="element-5626">
                            <img src={card.img} alt={card.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" data-id="element-5627" />
                            {/* Card frame glint on hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/40 via-transparent to-transparent" data-id="element-5628" />
                          </motion.div>;
                }) : <div className="text-[#D4AF37]/30 text-[10px] tracking-[0.3em] uppercase" data-id="element-5629">
                        In Production
                      </div>}
                  </div>

                  {/* Spread cards apart on hover */}
                  <style data-id="element-5630">{`
                    .group:hover [data-fan-i="0"] { transform: translateX(-58px) rotate(-12deg) scale(0.94) !important; }
                    .group:hover [data-fan-i="2"] { transform: translateX(58px) rotate(12deg) scale(0.94) !important; }
                  `}</style>

                  {/* Top-right meta badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-2" data-id="element-5631">
                    {col.isLive && <span className="flex items-center gap-1.5 px-2 py-1 bg-[#0B0F19]/85 backdrop-blur-sm border border-emerald-400/40 text-[8px] tracking-widest uppercase text-emerald-400 font-mono" data-id="element-5632">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-5633" />
                        Live
                      </span>}
                    <span className="px-2 py-1 bg-[#0B0F19]/85 backdrop-blur-sm border border-[#D4AF37]/30 text-[8px] tracking-widest uppercase text-[#D4AF37] font-mono" data-id="element-5634">
                      {col.cards?.length ?? 0} Cards
                    </span>
                  </div>

                  {/* Bottom-left rarity badge */}
                  <div className="absolute bottom-3 left-3" data-id="element-5635">
                    <RarityBadge rarity={col.rarity} data-id="element-5636" />
                  </div>
                </div>

                {/* === Body === */}
                <div className="p-5 flex flex-col flex-1" data-id="element-5637">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70 mb-1.5" data-id="element-5638">
                    {col.sub}
                  </p>
                  <h4 className="font-serif text-2xl text-[#FDFBF7] mb-2 group-hover:text-[#D4AF37] transition-colors leading-tight" data-id="element-5639">
                    {col.name}
                  </h4>
                  <p className="text-xs text-[#FDFBF7]/55 font-light leading-relaxed mb-4 flex-1" data-id="element-5640">
                    {col.description}
                  </p>

                  {/* CTA strip */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#D4AF37]/10" data-id="element-5641">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium flex items-center gap-2 group-hover:gap-3 transition-all" data-id="element-5642">
                      Step Inside
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1.5" data-id="element-5643" />
                    </span>
                    <span className="text-[9px] tracking-widest uppercase text-[#FDFBF7]/30 font-mono" data-id="element-5644">
                      {String(idx + 1).padStart(2, '0')} /{' '}
                      {String(otherCollections.length).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Glimpse of upcoming */}
        <div className="mt-24" data-id="element-5645">
          <div className="text-center mb-10" data-id="element-5646">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3" data-id="element-5647">
              On the Horizon
            </p>
            <h3 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-5648">
              A Glimpse of <span className="text-[#D4AF37]" data-id="element-5649">What's Coming</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-5650">
            {upcomingCollections.map(c => <div key={c.name} className="p-4 border border-[#D4AF37]/15 bg-[#131B2B]/40 text-center" data-id="element-5651">
                <p className="font-serif text-sm text-[#FDFBF7]/80 mb-1 leading-tight" data-id="element-5652">
                  {c.name}
                </p>
                <p className="text-[9px] tracking-widest uppercase text-[#D4AF37]/50" data-id="element-5653">
                  {c.sub}
                </p>
              </div>)}
          </div>
        </div>
      </div>

      {openCard && <CardModal card={openCard} onClose={() => setOpenCard(null)} data-id="element-5654" />}
      {openCollection && <CollectionDetailModal collection={openCollection} onClose={() => setOpenCollection(null)} onOpenCard={card => setOpenCard(card)} data-id="element-5655" />}
    </section>;
};
// ---------- Rarity Section ----------
const RaritySection = () => {
  const tiers = [{
    name: 'Mint',
    sub: 'Common',
    odds: '~60%',
    copy: 'The plentiful tier — still hand-painted, still part of every story. Most packets contain one.',
    borderClass: 'border-[#FDFBF7]/20',
    accentClass: 'text-[#FDFBF7]/70',
    bgGlow: 'from-[#FDFBF7]/5 to-transparent',
    pill: 'bg-[#FDFBF7]/10 text-[#FDFBF7]/70 border-[#FDFBF7]/20',
    isFlagship: false,
    icon: Layers
  }, {
    name: 'Epic',
    sub: 'Strong narrative',
    odds: '~25%',
    copy: 'Battle scenes, frontier locations, lesser figures of court — the connective tissue of the kingdom.',
    borderClass: 'border-[#8B0000]/50',
    accentClass: 'text-[#8B0000]',
    bgGlow: 'from-[#8B0000]/12 to-transparent',
    pill: 'bg-[#8B0000]/15 text-[#8B0000] border-[#8B0000]/40',
    isFlagship: false,
    icon: Sword
  }, {
    name: 'Treasury Rare',
    sub: 'Prized',
    odds: '~10%',
    copy: 'Consorts, sub-court figures, ceremonial objects. The cards collectors quietly hunt for.',
    borderClass: 'border-[#D4AF37]/40',
    accentClass: 'text-[#D4AF37]/90',
    bgGlow: 'from-[#D4AF37]/8 to-transparent',
    pill: 'bg-[#D4AF37]/10 text-[#D4AF37]/90 border-[#D4AF37]/40',
    isFlagship: false,
    icon: Gem
  }, {
    name: "Founders' Edition",
    sub: 'Cornerstone',
    odds: '~5%',
    copy: 'Founding figures and master seals — the cards that define a Treasury. Lifetime-numbered.',
    borderClass: 'border-[#D4AF37]/80',
    accentClass: 'text-[#D4AF37]',
    bgGlow: 'from-[#D4AF37]/15 to-transparent',
    pill: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/70',
    isFlagship: true,
    icon: Crown
  }, {
    name: 'Live Night Only',
    sub: 'Unobtainable from home',
    odds: 'Venue only',
    copy: 'Earned exclusively at Treasury Nights — scan-to-unlock artifacts, sensory keys, hand-stamped tokens.',
    borderClass: 'border-violet-400/50',
    accentClass: 'text-violet-300',
    bgGlow: 'from-violet-500/15 to-transparent',
    pill: 'bg-violet-400/10 text-violet-300 border-violet-400/50',
    isFlagship: false,
    icon: Moon
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5656">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)]" data-id="element-5657" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-5658">
        <SectionHeader eyebrow="The Ladder of Rarity" title={<>
              HOW RARE IS <span className="text-[#D4AF37]" data-id="element-5660">RARE</span>
            </>} description="Five tiers. Each with its own scarcity, its own story, and its own standing in the Treasury. We publish the odds — premium collecting deserves transparency." data-id="element-5659" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-14" data-id="element-5661">
          {tiers.map((t, i) => <div key={t.name} className={`relative border ${t.borderClass} bg-[#0B0F19] p-5 flex flex-col min-h-[260px] transition-all hover:-translate-y-1 ${t.isFlagship ? 'shadow-[0_0_30px_rgba(212,175,55,0.15)]' : ''}`} data-id="element-5662">
              <div className={`absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${t.bgGlow} pointer-events-none`} data-id="element-5663" />
              {t.isFlagship && <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5664" />}
              <div className="relative flex items-center justify-between mb-4" data-id="element-5665">
                <span className="text-[#FDFBF7]/30 font-mono text-[10px]" data-id="element-5666">
                  0{i + 1} / 05
                </span>
                <span className={`px-2 py-0.5 border text-[8px] tracking-[0.25em] uppercase ${t.pill}`} data-id="element-5667">
                  {t.odds}
                </span>
              </div>
              <div className="relative flex-1 flex flex-col" data-id="element-5668">
                <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.3em] uppercase mb-1.5" data-id="element-5669">
                  {t.sub}
                </p>
                <h4 className={`font-serif text-xl ${t.accentClass} mb-4 leading-tight`} data-id="element-5670">
                  {t.name}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs font-light leading-relaxed" data-id="element-5671">
                  {t.copy}
                </p>
                <div className="mt-auto pt-5 flex items-end justify-between" data-id="element-5672">
                  <div className={`w-8 h-11 border ${t.borderClass} bg-[#131B2B] flex items-center justify-center`} data-id="element-5673">
                    <t.icon className={`w-4 h-4 ${t.accentClass}`} data-id="element-5674" />
                  </div>
                  <span className="text-[#FDFBF7]/30 text-[8px] tracking-widest uppercase" data-id="element-5675">
                    Tier {i + 1}
                  </span>
                </div>
              </div>
            </div>)}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[10px] tracking-[0.25em] uppercase" data-id="element-5676">
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-5677">
            <Sparkles className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5678" /> Odds published
            per drop
          </span>
          <span className="hidden sm:inline text-[#D4AF37]/20" data-id="element-5679">·</span>
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-5680">
            <Shield className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5681" /> No paid pulls —
            earned only
          </span>
          <span className="hidden sm:inline text-[#D4AF37]/20" data-id="element-5682">·</span>
          <span className="flex items-center gap-2 text-[#FDFBF7]/40" data-id="element-5683">
            <BookOpen className="w-3 h-3 text-[#D4AF37]/60" data-id="element-5684" /> Every card
            hand-illustrated
          </span>
        </div>
      </div>
    </section>;
};
// ---------- Trading Section ----------
const TradingSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-5685">
    <div className="max-w-4xl mx-auto text-center" data-id="element-5686">
      <SectionHeader eyebrow="The Court Exchange" title={<>
            TRADE WITH THE <span className="text-[#D4AF37]" data-id="element-5688">SANGAT</span>
          </>} description="Duplicate cards become currency. Trade with members of your clan (Misl) or the wider community to complete sets and unlock prestige." data-id="element-5687" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12" data-id="element-5689">
        {[{
        icon: Repeat,
        label: 'Direct Trade',
        body: 'Member-to-member swap with verified consent.'
      }, {
        icon: Coins,
        label: 'Open Market',
        body: 'List duplicates. Set your terms. Vetted by the Treasury.'
      }, {
        icon: Gift,
        label: 'Gifting',
        body: 'Send a card to a Sangat member as tribute.'
      }].map(it => <div key={it.label} className="p-6 border border-[#D4AF37]/20 bg-[#131B2B]" data-id="element-5690">
            <it.icon className="w-7 h-7 text-[#D4AF37] mb-4 mx-auto" data-id="element-5691" />
            <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-5692">{it.label}</p>
            <p className="text-xs text-[#FDFBF7]/60 font-light leading-relaxed" data-id="element-5693">
              {it.body}
            </p>
          </div>)}
      </div>
    </div>
  </section>;
// ---------- Rank Section ----------
const RankSection = () => {
  const ranks = [{
    name: 'Sevadar',
    xp: '0 XP',
    color: 'text-[#FDFBF7]/60'
  }, {
    name: 'Sardar',
    xp: '500 XP',
    color: 'text-[#FDFBF7]/80'
  }, {
    name: 'Jathedar',
    xp: '1,500 XP',
    color: 'text-[#D4AF37]/90'
  }, {
    name: 'Misldar (Clan Chief)',
    xp: '3,500 XP',
    color: 'text-[#D4AF37]'
  }, {
    name: 'Maharaja',
    xp: '8,000 XP',
    color: 'text-[#8B0000]'
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-5694">
      <div className="max-w-5xl mx-auto" data-id="element-5695">
        <SectionHeader eyebrow="The Order of Rank" title={<>
              RISE THROUGH THE <span className="text-[#D4AF37]" data-id="element-5697">COURT</span>
            </>} description="Every artifact earned, every set completed, every Treasury Night attended — all of it counts toward your standing." data-id="element-5696" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-12" data-id="element-5698">
          {ranks.map((r, i) => <div key={r.name} className="p-5 border border-[#D4AF37]/20 bg-[#0B0F19] text-center" data-id="element-5699">
              <p className="text-[#FDFBF7]/30 font-mono text-[10px] mb-2" data-id="element-5700">
                0{i + 1}
              </p>
              <Crown className={`w-6 h-6 mx-auto mb-3 ${r.color}`} data-id="element-5701" />
              <p className={`font-serif text-lg mb-1 ${r.color}`} data-id="element-5702">{r.name}</p>
              <p className="text-[10px] tracking-widest uppercase text-[#FDFBF7]/40" data-id="element-5703">
                {r.xp}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
// ---------- Misl Section ----------
interface MislDetail {
  name: string;
  leader: string;
  members: string;
  xp: string;
  region: string;
  group: string;
  sub: string;
}
const MislDetailModal = ({
  misl,
  onClose,
  onJoin
}: {
  misl: MislDetail;
  onClose: () => void;
  onJoin: () => void;
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  // Build deterministic mock activity & members from the misl name so it feels
  // consistent across opens but varies between misls.
  const seed = misl.name.length;
  const weeklyXp = Math.round(parseFloat(misl.xp) * 0.07 * 10) / 10;
  const rank = seed * 3 % 17 + 1;
  const newThisWeek = seed * 7 % 18 + 4;
  const cardsClaimedWeek = seed * 11 % 90 + 60;
  const eventsAttended = seed * 5 % 12 + 3;
  const activityFeed = [{
    who: 'Harnoor S.',
    what: 'claimed Royal Seal of Ranjit Singh',
    when: '2h ago',
    rarity: 'Treasury Rare'
  }, {
    who: 'Amrit K.',
    what: 'joined the Clan',
    when: '5h ago',
    rarity: null
  }, {
    who: 'Davinder G.',
    what: "completed Founders' Seals set",
    when: '8h ago',
    rarity: "Founders' Edition"
  }, {
    who: 'Jaspreet S.',
    what: 'attended Treasury Night #04',
    when: '1d ago',
    rarity: 'Live Night Only'
  }, {
    who: 'Manvir K.',
    what: 'traded 3 duplicates',
    when: '1d ago',
    rarity: null
  }, {
    who: 'Gurpreet S.',
    what: 'referred 2 new Sangat members',
    when: '2d ago',
    rarity: null
  }];
  const topMembers = [{
    rank: 1,
    name: misl.leader,
    xp: '42,180',
    tag: 'Misldar'
  }, {
    rank: 2,
    name: '@simran_k',
    xp: '28,940',
    tag: null
  }, {
    rank: 3,
    name: '@arjan_s',
    xp: '24,610',
    tag: null
  }, {
    rank: 4,
    name: '@navjot_g',
    xp: '21,480',
    tag: null
  }, {
    rank: 5,
    name: '@harleen_k',
    xp: '19,320',
    tag: null
  }];
  const perks = [{
    icon: Gift,
    title: 'Bonus Daily Packet',
    body: 'Clan (Misl) members get an extra packet draw on Sundays.'
  }, {
    icon: Crown,
    title: 'Misldar Path (Clan Chief)',
    body: 'Earn the most XP and lead the Clan. Real status.'
  }, {
    icon: Calendar,
    title: 'Member-Only Events',
    body: 'Private Court Sessions for verified Clan members.'
  }, {
    icon: Sparkles,
    title: 'Clan-Exclusive Cards',
    body: "Drops tied to your Clan's history and region."
  }];
  return <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-5704">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#0B0F19]/92 backdrop-blur-sm" data-id="element-5705">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.12),_transparent_55%)]" data-id="element-5706" />
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} transition={{
      duration: 0.4,
      type: 'spring',
      bounce: 0.2
    }} className="relative w-full max-w-5xl bg-[#0B0F19] border-2 border-[#D4AF37]/40 shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide" data-id="element-5707">
        <CornerOrnaments data-id="element-5708" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors z-10" aria-label="Close" data-id="element-5709">
          <X className="w-6 h-6" data-id="element-5710" />
        </button>

        {/* === Header === */}
        <div className="relative px-6 sm:px-10 pt-10 pb-8 border-b border-[#D4AF37]/15 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B]/40 to-transparent" data-id="element-5711">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-2" data-id="element-5712">
            {misl.group} · {misl.sub}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-id="element-5713">
            <div data-id="element-5714">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-5715">
                {misl.name} Misl
              </h2>
              <p className="text-[#FDFBF7]/60 text-sm" data-id="element-5716">{misl.region}</p>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5 border border-[#D4AF37]/30 bg-[#0B0F19]/60" data-id="element-5717">
              <div className="w-9 h-9 rounded-full border border-[#D4AF37]/50 bg-[#131B2B] flex items-center justify-center" data-id="element-5718">
                <Crown className="w-4 h-4 text-[#D4AF37]" data-id="element-5719" />
              </div>
              <div className="text-left" data-id="element-5720">
                <p className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-5721">
                  Current Misldar{' '}
                  <span className="text-[#FDFBF7]/40" data-id="element-5722">(Clan Chief)</span>
                </p>
                <p className="font-mono text-[#D4AF37] text-sm" data-id="element-5723">
                  {misl.leader}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === KPI strip === */}
        <div className="px-6 sm:px-10 py-6 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-[#D4AF37]/10" data-id="element-5724">
          {[{
          label: 'Members',
          value: misl.members,
          icon: Users,
          accent: false
        }, {
          label: 'Total XP',
          value: misl.xp,
          icon: Sparkles,
          accent: true
        }, {
          label: 'Confederacy Rank',
          value: `#${rank}`,
          icon: Crown,
          accent: false
        }, {
          label: 'New This Week',
          value: `+${newThisWeek}`,
          icon: UserPlus,
          accent: false
        }].map(stat => <div key={stat.label} className="border border-[#D4AF37]/15 bg-[#131B2B]/40 p-4 text-center" data-id="element-5725">
              <stat.icon className={`w-4 h-4 mx-auto mb-2 ${stat.accent ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-5726" />
              <p className={`font-serif text-2xl mb-1 ${stat.accent ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`} data-id="element-5727">
                {stat.value}
              </p>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#FDFBF7]/50" data-id="element-5728">
                {stat.label}
              </p>
            </div>)}
        </div>

        {/* === Weekly activity strip === */}
        <div className="px-6 sm:px-10 py-5 border-b border-[#D4AF37]/10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 text-[10px] tracking-[0.25em] uppercase" data-id="element-5729">
          <span className="text-emerald-400 flex items-center gap-2" data-id="element-5730">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-5731" />
            Active Now ·{' '}
            {Math.round((parseInt(misl.members.replace(/,/g, '')) || 0) * 0.08)}{' '}
            members
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-5732">
            <Sparkles className="w-3 h-3 text-[#D4AF37]" data-id="element-5733" />+{weeklyXp}k XP this
            week
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-5734">
            <Gift className="w-3 h-3 text-[#D4AF37]" data-id="element-5735" /> {cardsClaimedWeek} cards
            claimed
          </span>
          <span className="text-[#FDFBF7]/60 flex items-center gap-2" data-id="element-5736">
            <Calendar className="w-3 h-3 text-[#D4AF37]" data-id="element-5737" /> {eventsAttended}{' '}
            events attended
          </span>
        </div>

        {/* === Two-column: Activity feed + Top members === */}
        <div className="px-6 sm:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-5738">
          {/* Activity feed */}
          <div data-id="element-5739">
            <div className="flex items-center justify-between mb-5" data-id="element-5740">
              <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5741">
                Recent Activity
              </h3>
              <span className="text-[9px] tracking-widest uppercase text-[#D4AF37]/60" data-id="element-5742">
                Live Feed
              </span>
            </div>
            <div className="space-y-2.5" data-id="element-5743">
              {activityFeed.map((a, i) => <div key={i} className="flex items-start gap-3 p-3 border border-[#D4AF37]/10 bg-[#131B2B]/40 hover:border-[#D4AF37]/25 transition-colors" data-id="element-5744">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-[#0B0F19] border border-[#D4AF37]/25 flex items-center justify-center" data-id="element-5745">
                    <span className="text-[#D4AF37] text-[10px] font-serif" data-id="element-5746">
                      {a.who.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5747">
                    <p className="text-[#FDFBF7]/85 text-xs leading-relaxed" data-id="element-5748">
                      <span className="text-[#D4AF37]" data-id="element-5749">{a.who}</span> {a.what}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-5750">
                      <span className="text-[#FDFBF7]/35 text-[9px] tracking-widest uppercase" data-id="element-5751">
                        {a.when}
                      </span>
                      {a.rarity && <span className="text-[#D4AF37]/70 text-[9px] tracking-widest uppercase" data-id="element-5752">
                          · {a.rarity}
                        </span>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Top members leaderboard */}
          <div data-id="element-5753">
            <div className="flex items-center justify-between mb-5" data-id="element-5754">
              <h3 className="font-serif text-xl text-[#FDFBF7]" data-id="element-5755">Top Members</h3>
              <span className="text-[9px] tracking-widest uppercase text-[#D4AF37]/60" data-id="element-5756">
                XP Ladder
              </span>
            </div>
            <div className="border border-[#D4AF37]/15 bg-[#131B2B]/40" data-id="element-5757">
              {topMembers.map((m, i) => <div key={m.rank} className={`flex items-center gap-3 px-4 py-3 ${i !== topMembers.length - 1 ? 'border-b border-[#D4AF37]/10' : ''} ${m.rank === 1 ? 'bg-[#D4AF37]/5' : ''}`} data-id="element-5758">
                  <span className={`w-7 text-center font-mono text-sm ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-5759">
                    {String(m.rank).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-5760">
                    <p className={`font-mono text-sm truncate ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/85'}`} data-id="element-5761">
                      {m.name}
                    </p>
                    {m.tag && <p className="text-[#D4AF37]/70 text-[9px] tracking-widest uppercase mt-0.5" data-id="element-5762">
                        {m.tag}
                      </p>}
                  </div>
                  <span className={`font-mono text-xs ${m.rank === 1 ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/60'}`} data-id="element-5763">
                    {m.xp} XP
                  </span>
                </div>)}
            </div>
            <p className="text-center text-[#FDFBF7]/40 text-[10px] tracking-widest uppercase mt-3" data-id="element-5764">
              + {parseInt(misl.members.replace(/,/g, '')) - 5} more members
            </p>
          </div>
        </div>

        {/* === Why join === */}
        <div className="px-6 sm:px-10 py-8 border-t border-[#D4AF37]/10" data-id="element-5765">
          <div className="text-center mb-6" data-id="element-5766">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-2" data-id="element-5767">
              What you unlock
            </p>
            <h3 className="font-serif text-2xl text-[#FDFBF7]" data-id="element-5768">
              Why Join the {misl.name} Clan
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-5769">
            {perks.map(p => <div key={p.title} className="p-4 border border-[#D4AF37]/15 bg-[#131B2B]/40" data-id="element-5770">
                <p.icon className="w-5 h-5 text-[#D4AF37] mb-3" data-id="element-5771" />
                <p className="font-serif text-sm text-[#FDFBF7] mb-1" data-id="element-5772">
                  {p.title}
                </p>
                <p className="text-[11px] text-[#FDFBF7]/55 font-light leading-relaxed" data-id="element-5773">
                  {p.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* === CTA strip === */}
        <div className="sticky bottom-0 px-6 sm:px-10 py-5 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19] to-[#0B0F19]/90 border-t border-[#D4AF37]/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3" data-id="element-5774">
          <div data-id="element-5775">
            <p className="text-[#FDFBF7] text-sm font-medium" data-id="element-5776">
              Ready to take your place in the {misl.name} court?
            </p>
            <p className="text-[#FDFBF7]/50 text-xs mt-0.5" data-id="element-5777">
              Register to lock in your clan and start earning XP today.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-5778">
            <button onClick={onClose} className="px-5 py-3 border border-[#FDFBF7]/20 text-[#FDFBF7]/70 text-[10px] tracking-[0.25em] uppercase hover:bg-[#FDFBF7]/5 transition-colors" data-id="element-5779">
              Browse Other Clans
            </button>
            <button onClick={onJoin} className="px-6 py-3 bg-[#D4AF37] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-[#E5C158] transition-colors flex items-center gap-2" data-id="element-5780">
              Join This Clan
              <ArrowRight className="w-3.5 h-3.5" data-id="element-5781" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>;
};
const MislSection = ({
  onRegister
}: {
  onRegister?: () => void;
}) => {
  const [selectedMisl, setSelectedMisl] = useState<MislDetail | null>(null);
  const mislGroups = [{
    group: 'Homeland / Region',
    sub: 'Spiritual and cultural homelands of Punjab',
    misls: [{
      name: 'Lahore',
      leader: '@lahore_s',
      members: '2,100',
      xp: '720k',
      region: 'Punjab · Pakistan'
    }, {
      name: 'Amritsar',
      leader: '@golden_s',
      members: '1,360',
      xp: '510k',
      region: 'Punjab · India'
    }, {
      name: 'Majha',
      leader: '@majha_s',
      members: '960',
      xp: '370k',
      region: 'Central Punjab'
    }, {
      name: 'Doaba',
      leader: '@doaba_k',
      members: '780',
      xp: '300k',
      region: 'Beas–Sutlej'
    }, {
      name: 'Malwa',
      leader: '@malwa_s',
      members: '820',
      xp: '320k',
      region: 'South Punjab'
    }]
  }, {
    group: 'Diaspora / City',
    sub: 'The modern Khalsa beyond Punjab',
    misls: [{
      name: 'Brampton',
      leader: '@brampton_s',
      members: '1,420',
      xp: '460k',
      region: 'Ontario · Canada'
    }, {
      name: 'Surrey',
      leader: '@surrey_s',
      members: '1,180',
      xp: '410k',
      region: 'British Columbia'
    }, {
      name: 'Southall',
      leader: '@southall_s',
      members: '980',
      xp: '360k',
      region: 'London · UK'
    }, {
      name: 'Toronto',
      leader: '@toronto_s',
      members: '1,260',
      xp: '440k',
      region: 'Ontario · Canada'
    }, {
      name: 'Vancouver',
      leader: '@van_s',
      members: '1,040',
      xp: '390k',
      region: 'British Columbia'
    }]
  }, {
    group: 'Family / Clan',
    sub: 'Houses bound by name and lineage',
    misls: [{
      name: 'Sandhu',
      leader: '@sandhu_s',
      members: '1,180',
      xp: '420k',
      region: 'Jat clan'
    }, {
      name: 'Gill',
      leader: '@gill_s',
      members: '1,340',
      xp: '470k',
      region: 'Jat clan'
    }, {
      name: 'Sidhu',
      leader: '@sidhu_s',
      members: '1,220',
      xp: '440k',
      region: 'Jat clan'
    }, {
      name: 'Dhillon',
      leader: '@dhillon_s',
      members: '960',
      xp: '350k',
      region: 'Jat clan'
    }, {
      name: 'Brar',
      leader: '@brar_s',
      members: '880',
      xp: '320k',
      region: 'Jat clan'
    }]
  }, {
    group: 'Historic Prestige',
    sub: 'The sovereign Misls (Clans) of the Khalsa Confederacy',
    misls: [{
      name: 'Sukerchakia',
      leader: '@ranjit_s',
      members: '1,520',
      xp: '480k',
      region: 'Gujranwala'
    }, {
      name: 'Ahluwalia',
      leader: '@jassa_singh',
      members: '1,240',
      xp: '450k',
      region: 'Kapurthala'
    }, {
      name: 'Ramgarhia',
      leader: '@jassa_r',
      members: '1,180',
      xp: '430k',
      region: 'Sri Hargobindpur'
    }, {
      name: 'Kanheya',
      leader: '@jai_singh',
      members: '850',
      xp: '310k',
      region: 'Sohian'
    }, {
      name: 'Nakai',
      leader: '@hira_s',
      members: '620',
      xp: '240k',
      region: 'Chunian'
    }]
  }];
  return <section data-misl-section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden scroll-mt-24" data-id="element-5782">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)]" data-id="element-5783" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-5784">
        <SectionHeader eyebrow="Community & Sovereignty" title={<>
              JOIN A <span className="text-[#D4AF37]" data-id="element-5786">MISL</span>{' '}
              <span className="text-[#FDFBF7]/40" data-id="element-5787">— A CLAN</span>
            </>} description="The Treasury is communal. Join a Misl — your clan in the Khalsa — invite your Sangat, and compete on the global leaderboards. Together, we recover our history." data-id="element-5785" />

        <div className="max-w-3xl mx-auto mb-10 text-center" data-id="element-5788">
          <p className="text-[#FDFBF7]/60 text-sm leading-relaxed italic" data-id="element-5789">
            A <span className="text-[#D4AF37]" data-id="element-5790">Misl</span> — a clan and
            sovereign state of the Sikh Confederacy — was led by a{' '}
            <span className="text-[#D4AF37]" data-id="element-5791">Misldar</span> (clan chief). Today,
            we honor that legacy — joining historical Misls, royal courts of
            Lahore, our homeland Sangats, and our diaspora hubs around the
            world.
          </p>
        </div>

        {/* How Leadership Works — gamification callout */}
        <div className="max-w-3xl mx-auto mb-12 relative border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/8 via-[#131B2B] to-[#0B0F19] p-6 sm:p-8" data-id="element-5792">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-5793" />
          <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left" data-id="element-5794">
            <div className="w-14 h-14 shrink-0 border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-5795">
              <Crown className="w-7 h-7 text-[#D4AF37]" data-id="element-5796" />
            </div>
            <div className="flex-1" data-id="element-5797">
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-1.5" data-id="element-5798">
                How Leadership Works
              </p>
              <p className="text-[#FDFBF7]/80 text-sm leading-relaxed" data-id="element-5799">
                The member with the most XP in each clan becomes its{' '}
                <span className="text-[#D4AF37] font-bold" data-id="element-5800">Misldar</span> (Clan
                Chief). Climb the leaderboard, claim rare cards, refer your
                Sangat — whoever earns the most XP rules the clan.
              </p>
            </div>
          </div>
        </div>

        {/* Grouped Misl categories */}
        <div className="space-y-10 mb-12" data-id="element-5801">
          {mislGroups.map(g => <div key={g.group} data-id="element-5802">
              {/* Group header */}
              <div className="flex items-end justify-between gap-4 mb-5 pb-3 border-b border-[#D4AF37]/15" data-id="element-5803">
                <div data-id="element-5804">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#FDFBF7]" data-id="element-5805">
                    {g.group}
                  </h3>
                  <p className="text-[#D4AF37]/60 text-[10px] tracking-[0.3em] uppercase mt-1.5" data-id="element-5806">
                    {g.sub}
                  </p>
                </div>
                <span className="text-[#FDFBF7]/30 text-[10px] tracking-widest uppercase shrink-0" data-id="element-5807">
                  {g.misls.length} {g.misls.length === 1 ? 'Misl' : 'Misls'}
                </span>
              </div>

              {/* 5-up grid so each category fits on one row, no orphans */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-5808">
                {g.misls.map(m => <div key={m.name} className="group relative p-4 border border-[#D4AF37]/20 bg-[#131B2B]/60 hover:border-[#D4AF37]/55 hover:bg-[#131B2B] transition-all flex flex-col" data-id="element-5809">
                    {/* Name + region */}
                    <div className="mb-3" data-id="element-5810">
                      <h4 className="font-serif text-[#FDFBF7] text-base leading-tight truncate" data-id="element-5811">
                        {m.name}
                      </h4>
                      <p className="text-[#FDFBF7]/40 text-[9px] tracking-[0.2em] uppercase mt-1 truncate" data-id="element-5812">
                        {m.region}
                      </p>
                    </div>

                    {/* Misldar row */}
                    <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-[#D4AF37]/10" data-id="element-5813">
                      <Crown className="w-3 h-3 text-[#D4AF37] shrink-0" data-id="element-5814" />
                      <p className="text-[#D4AF37]/80 text-[9px] tracking-widest uppercase font-mono truncate" data-id="element-5815">
                        {m.leader}
                      </p>
                    </div>

                    {/* Stats — 2 stacked rows for clarity */}
                    <div className="flex items-center justify-between text-[10px] mb-4" data-id="element-5816">
                      <span className="flex items-center gap-1.5 text-[#FDFBF7]/60" data-id="element-5817">
                        <Users className="w-3 h-3" data-id="element-5818" />
                        <span className="font-mono" data-id="element-5819">{m.members}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-[#D4AF37]" data-id="element-5820">
                        <Sparkles className="w-3 h-3" data-id="element-5821" />
                        <span className="font-mono" data-id="element-5822">{m.xp}</span>
                      </span>
                    </div>

                    {/* Join CTA — opens detailed Misl view with stats & activity */}
                    <button type="button" onClick={() => setSelectedMisl({
                ...m,
                group: g.group,
                sub: g.sub
              })} className="mt-auto w-full py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0F19] transition-all" data-id="element-5823">
                      Join Clan
                    </button>
                  </div>)}
              </div>
            </div>)}
        </div>

        <div className="p-8 sm:p-10 border border-[#D4AF37]/30 bg-[#131B2B] relative flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-5824">
          <CornerOrnaments data-id="element-5825" />
          <div className="text-center md:text-left" data-id="element-5826">
            <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-5827">
              Invite your Sangat
            </h3>
            <p className="text-[#FDFBF7]/60 text-sm" data-id="element-5828">
              Bring friends into your Clan (Misl) and earn bonus packets for
              every verified member.
            </p>
          </div>
          <button className="flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white hover:bg-[#128C7E] transition-all font-medium text-sm tracking-wider" data-id="element-5829">
            <MessageCircle className="w-5 h-5" data-id="element-5830" />
            SHARE ON WHATSAPP
          </button>
        </div>
      </div>

      {/* Detail modal mounted OUTSIDE inner z-10 stacking context so it always overlays correctly */}
      {selectedMisl && <MislDetailModal misl={selectedMisl} onClose={() => setSelectedMisl(null)} onJoin={() => {
      setSelectedMisl(null);
      if (onRegister) onRegister();
    }} data-id="element-5831" />}
    </section>;
};
// ---------- Create Your Own Misl Section ----------
const CreateMislSection = ({
  onRegister,
  embedded = false,
  onClose
}: {
  onRegister?: () => void;
  embedded?: boolean;
  onClose?: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    region: '',
    type: 'family',
    crest: 'lion'
  });
  const [formingMembers, setFormingMembers] = useState(2);
  const inviteLink = `lahoretreasury.com/misl/${(form.name || 'your-misl').toLowerCase().replace(/\s+/g, '-')}`;
  const handleCopyLink = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleWhatsAppShare = () => {
    const msg = encodeURIComponent(`I'm forming a Misl inside Lahore Treasury — ${form.name || 'a new house'}. Join us and help activate our house in the Lahore Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const entryCards = [{
    icon: Users,
    tag: 'Join an existing house',
    title: 'Join a Misl',
    body: 'Join an existing Misl and contribute to its collection, rank, and standing inside the Lahore Court.',
    cta: 'Browse Misls',
    action: () => {
      const el = document.querySelector('[data-misl-section]');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, {
    icon: Plus,
    tag: 'Build your own',
    title: 'Create a Misl',
    body: 'Start your own Misl, invite your people through WhatsApp, and activate your house once enough members join.',
    cta: 'Create Your Misl',
    action: () => {
      const el = document.getElementById('create-misl-form');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    },
    highlight: true
  }];
  const steps = [{
    n: '01',
    title: 'Create Your Misl',
    body: 'Choose a name, region, and identity. Your house takes shape inside the Lahore Court.'
  }, {
    n: '02',
    title: 'Share Through WhatsApp',
    body: 'Bring your people in — family, friends, school, community. One link, one Misl.'
  }, {
    n: '03',
    title: 'Reach the Threshold',
    body: 'A Misl awakens when its members arrive. Five voices are enough to activate a house.'
  }, {
    n: '04',
    title: 'Unlock Active Status',
    body: 'Enter the leaderboard, earn collective XP, and step into group rewards together.'
  }];
  const statusLadder = [{
    tag: 'Stage 01',
    name: 'Forming Misl',
    body: 'Created, but still gathering its first members. Your house is being assembled.',
    dot: 'bg-[#FDFBF7]/40',
    border: 'border-[#FDFBF7]/20',
    titleColor: 'text-[#FDFBF7]/80'
  }, {
    tag: 'Stage 02',
    name: 'Active Misl',
    body: 'Minimum threshold reached. Your Misl is officially recognised inside the Court.',
    dot: 'bg-[#D4AF37]',
    border: 'border-[#D4AF37]/50',
    titleColor: 'text-[#D4AF37]'
  }, {
    tag: 'Stage 03',
    name: 'Ranked Misl',
    body: 'A growing house that earns XP, climbs the leaderboard, and unlocks stronger benefits.',
    dot: 'bg-[#8B0000]',
    border: 'border-[#8B0000]/50',
    titleColor: 'text-[#8B0000]'
  }];
  const thresholds = [{
    count: '5',
    label: 'Active Misl',
    body: 'Your house is officially recognised. Identity, badge, and listing inside the Court.',
    progress: 100
  }, {
    count: '10+',
    label: 'Stronger Standing',
    body: 'Better leaderboard placement, group XP multipliers, and broader recognition.',
    progress: 65
  }, {
    count: '25+',
    label: 'Prestige Misl',
    body: 'Featured placement, spotlight in Court communications, and prestige drops.',
    progress: 30
  }];
  const mislTypes = [{
    id: 'family',
    label: 'Family'
  }, {
    id: 'friends',
    label: 'Friends'
  }, {
    id: 'school',
    label: 'School'
  }, {
    id: 'community',
    label: 'Community'
  }, {
    id: 'collectors',
    label: 'Collectors'
  }];
  const crestOptions = [{
    id: 'lion',
    label: 'Lion',
    icon: Shield
  }, {
    id: 'sword',
    label: 'Sword',
    icon: Sword
  }, {
    id: 'crown',
    label: 'Crown',
    icon: Crown
  }, {
    id: 'gem',
    label: 'Gem',
    icon: Gem
  }, {
    id: 'star',
    label: 'Star',
    icon: Star
  }];
  const benefits = [{
    icon: Trophy,
    title: 'Misl Leaderboard',
    body: 'Active Misls appear on the global leaderboard and compete for standing.'
  }, {
    icon: Shield,
    title: 'House Identity',
    body: 'Unlock an exclusive Misl badge, crest, and identity marker across the Court.'
  }, {
    icon: Sparkles,
    title: 'Court Visibility',
    body: 'Your Misl becomes visible to the wider Lahore Treasury community.'
  }, {
    icon: Gift,
    title: 'Group Rewards',
    body: 'Qualify for group-based rewards earned by your Misl as a whole.'
  }, {
    icon: Crown,
    title: 'Rising Status',
    body: 'As your Misl grows, your house unlocks stronger status and recognition.'
  }, {
    icon: Gem,
    title: 'Misl-Only Drops',
    body: 'Access future Misl-only drops, prestige spotlights, and Court honours.'
  }];
  const Wrapper: React.FC<{
    children: React.ReactNode;
  }> = ({
    children
  }) => embedded ? <div className="relative" data-id="element-5832">{children}</div> : <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10 overflow-hidden" data-id="element-5833">
        {/* Ambient gold glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-5834" />
        <div className="absolute -left-32 top-1/3 w-96 h-96 bg-[#D4AF37]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-5835" />
        <div className="absolute -right-32 bottom-1/3 w-96 h-96 bg-[#8B0000]/8 blur-[120px] rounded-full pointer-events-none" data-id="element-5836" />
        {children}
      </section>;
  return <Wrapper data-id="element-5837">
      <div className={`${embedded ? '' : 'max-w-6xl mx-auto relative z-10'}`} data-id="element-5838">
        {embedded ? <div className="flex flex-col gap-6 mb-10" data-id="element-5839">
            <div className="flex items-center justify-between border-b border-[#D4AF37]/20 pb-4" data-id="element-5840">
              <div data-id="element-5841">
                <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-1.5" data-id="element-5842">
                  Build Your House
                </p>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight" data-id="element-5843">
                  Create Your Own <span className="text-[#D4AF37]" data-id="element-5844">Misl</span>
                </h3>
              </div>
              {onClose && <button onClick={onClose} className="flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-5845">
                  <ChevronLeft className="w-3 h-3" data-id="element-5846" />
                  Back to My Clan
                </button>}
            </div>
            <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-3xl" data-id="element-5847">
              Build your own house inside Lahore Treasury. Invite your people
              through WhatsApp, grow your rank, and unlock rewards together.
              Your Misl begins as a forming house and activates once enough
              members join.
            </p>
          </div> : <>
            <SectionHeader eyebrow="Build Your House" title={<>
                  CREATE YOUR OWN <span className="text-[#D4AF37]" data-id="element-5849">MISL</span>
                </>} description="Build your own house inside Lahore Treasury. Invite your people, grow your rank, and unlock rewards together." data-id="element-5848" />

            <div className="max-w-3xl mx-auto mb-16 text-center" data-id="element-5850">
              <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-5851">
                Lahore Treasury is not only about individual collecting. You can
                also create your own{' '}
                <span className="text-[#D4AF37]" data-id="element-5852">Misl</span>, bring your people
                in through WhatsApp, and grow together inside the Lahore Court.
              </p>
            </div>
          </>}

        {/* ---------- 1. Two Entry Cards ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-20" data-id="element-5853">
          {entryCards.map(c => <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className={`relative p-8 sm:p-10 border ${c.highlight ? 'border-[#D4AF37]/50 bg-gradient-to-br from-[#131B2B] to-[#0B0F19]' : 'border-[#D4AF37]/20 bg-[#131B2B]'} group hover:border-[#D4AF37]/70 transition-all`} data-id="element-5854">
              <CornerOrnaments data-id="element-5855" />
              {c.highlight && <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5856" />}
              <div className="flex items-start justify-between mb-6" data-id="element-5857">
                <div className="w-12 h-12 border border-[#D4AF37]/40 bg-[#0B0F19] flex items-center justify-center" data-id="element-5858">
                  <c.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-5859" />
                </div>
                <span className="text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase" data-id="element-5860">
                  {c.tag}
                </span>
              </div>
              <h3 className="font-serif text-3xl text-[#FDFBF7] mb-3 leading-tight" data-id="element-5861">
                {c.title}
              </h3>
              <p className="text-[#FDFBF7]/60 text-sm leading-relaxed mb-8" data-id="element-5862">
                {c.body}
              </p>
              <button onClick={c.action} className={`w-full py-3.5 text-[10px] tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 transition-all ${c.highlight ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.2)]' : 'border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`} data-id="element-5863">
                {c.cta}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-5864" />
              </button>
            </motion.div>)}
        </div>

        {/* ---------- 2. How Misls Work ---------- */}
        <div className="mb-20" data-id="element-5865">
          <div className="text-center mb-10" data-id="element-5866">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5867">
              The Path
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-5868">
              How Misls Work
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-5869">
              Misls begin as forming houses. Once enough members join, they
              become active and can start appearing in rankings, earning status,
              and unlocking collective benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5870">
            {steps.map((s, i) => <div key={s.n} className="relative p-6 border border-[#D4AF37]/20 bg-[#131B2B]/60 hover:border-[#D4AF37]/40 transition-colors" data-id="element-5871">
                <CornerOrnaments data-id="element-5872" />
                <div className="flex items-center justify-between mb-5" data-id="element-5873">
                  <span className="font-mono text-[#D4AF37] text-xs tracking-[0.2em]" data-id="element-5874">
                    {s.n}
                  </span>
                  {i < steps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-[#D4AF37]/40 hidden lg:block" data-id="element-5875" />}
                </div>
                <h4 className="font-serif text-lg text-[#FDFBF7] mb-2 leading-tight" data-id="element-5876">
                  {s.title}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed" data-id="element-5877">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 3. Status Ladder ---------- */}
        <div className="mb-20" data-id="element-5878">
          <div className="text-center mb-10" data-id="element-5879">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5880">
              Misl Status
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight" data-id="element-5881">
              From Forming to Ranked
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5882">
            {statusLadder.map(s => <div key={s.name} className={`relative p-7 border ${s.border} bg-[#131B2B] hover:bg-[#131B2B]/80 transition-colors`} data-id="element-5883">
                <CornerOrnaments data-id="element-5884" />
                <div className="flex items-center gap-3 mb-5" data-id="element-5885">
                  <span className={`w-2 h-2 rounded-full ${s.dot} shadow-[0_0_12px_currentColor]`} data-id="element-5886" />
                  <span className="text-[#FDFBF7]/40 text-[9px] tracking-[0.3em] uppercase" data-id="element-5887">
                    {s.tag}
                  </span>
                </div>
                <h4 className={`font-serif text-2xl ${s.titleColor} mb-3 leading-tight`} data-id="element-5888">
                  {s.name}
                </h4>
                <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-5889">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 4. Member Threshold Logic ---------- */}
        <div className="mb-20 border border-[#D4AF37]/20 bg-[#131B2B]/60 p-8 sm:p-10 relative" data-id="element-5890">
          <CornerOrnaments data-id="element-5891" />
          <div className="text-center mb-10" data-id="element-5892">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5893">
              Activation Thresholds
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-5894">
              When a Misl Awakens
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-5895">
              A Misl is not active by default. It is awakened by its people.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-5896">
            {thresholds.map((t, i) => <div key={t.label} className="relative border border-[#D4AF37]/15 bg-[#0B0F19] p-6" data-id="element-5897">
                <div className="flex items-baseline gap-2 mb-3" data-id="element-5898">
                  <span className="font-serif text-5xl text-[#D4AF37] leading-none" data-id="element-5899">
                    {t.count}
                  </span>
                  <span className="text-[#FDFBF7]/50 text-[10px] tracking-[0.25em] uppercase" data-id="element-5900">
                    Members
                  </span>
                </div>
                <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-5901">
                  {t.label}
                </p>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed mb-5" data-id="element-5902">
                  {t.body}
                </p>
                <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden" data-id="element-5903">
                  <motion.div initial={{
                width: 0
              }} whileInView={{
                width: `${t.progress}%`
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.2,
                delay: i * 0.2
              }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]" data-id="element-5904" />
                </div>
              </div>)}
          </div>
        </div>

        {/* ---------- 5. Create A Misl Form ---------- */}
        <div id="create-misl-form" className="mb-20 border border-[#D4AF37]/30 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] p-8 sm:p-12 relative scroll-mt-24" data-id="element-5905">
          <CornerOrnaments data-id="element-5906" />
          <span className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-5907" />

          <div className="text-center mb-10" data-id="element-5908">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5909">
              The Founding
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-5910">
              Found Your Misl
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-5911">
              Shape the identity of your house. You can refine these details
              after your Misl is created.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6" data-id="element-5912">
            {/* Name */}
            <div data-id="element-5913">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5914">
                Misl Name
              </label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({
              ...f,
              name: e.target.value
            }))} placeholder="The Singh House, Lahore Circle, Anand Misl…" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-5915" />
            </div>

            {/* Description */}
            <div data-id="element-5916">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5917">
                Short Description
              </label>
              <textarea rows={3} value={form.description} onChange={e => setForm(f => ({
              ...f,
              description: e.target.value
            }))} placeholder="A few lines about your Misl, who it's for, and what you stand for." className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors resize-none" data-id="element-5918" />
            </div>

            {/* City / Region */}
            <div data-id="element-5919">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-5920">
                City or Region
              </label>
              <div className="relative" data-id="element-5921">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-5922" />
                <input type="text" value={form.region} onChange={e => setForm(f => ({
                ...f,
                region: e.target.value
              }))} placeholder="Toronto, Amritsar, London, Punjab…" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 pl-11 pr-4 py-3.5 text-[#FDFBF7] text-sm placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-5923" />
              </div>
            </div>

            {/* Type / Inspiration */}
            <div data-id="element-5924">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-5925">
                Type of Misl
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-5926">
                {mislTypes.map(t => {
                const active = form.type === t.id;
                return <button key={t.id} type="button" onClick={() => setForm(f => ({
                  ...f,
                  type: t.id
                }))} className={`py-3 text-[10px] tracking-[0.2em] uppercase border transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#D4AF37]/15 bg-[#0B0F19] text-[#FDFBF7]/50 hover:border-[#D4AF37]/40 hover:text-[#FDFBF7]/80'}`} data-id="element-5927">
                      {t.label}
                    </button>;
              })}
              </div>
            </div>

            {/* Crest / Badge */}
            <div data-id="element-5928">
              <label className="block text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase mb-3" data-id="element-5929">
                Crest{' '}
                <span className="text-[#FDFBF7]/30 normal-case tracking-normal" data-id="element-5930">
                  (optional)
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2" data-id="element-5931">
                {crestOptions.map(c => {
                const active = form.crest === c.id;
                return <button key={c.id} type="button" onClick={() => setForm(f => ({
                  ...f,
                  crest: c.id
                }))} className={`py-4 flex flex-col items-center gap-2 border transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/15 bg-[#0B0F19] hover:border-[#D4AF37]/40'}`} data-id="element-5932">
                      <c.icon className={`w-4 h-4 ${active ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/50'}`} data-id="element-5933" />
                      <span className={`text-[9px] tracking-[0.2em] uppercase ${active ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/40'}`} data-id="element-5934">
                        {c.label}
                      </span>
                    </button>;
              })}
              </div>
            </div>

            {/* Primary action */}
            <div className="pt-4" data-id="element-5935">
              <button onClick={() => {
              setFormingMembers(1);
              const el = document.getElementById('misl-invite-block');
              if (el) el.scrollIntoView({
                behavior: 'smooth'
              });
            }} disabled={!form.name.trim()} className={`w-full py-4 text-[10px] tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 transition-all ${form.name.trim() ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.25)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-5936">
                Found This Misl
                <ArrowRight className="w-3.5 h-3.5" data-id="element-5937" />
              </button>
              <p className="text-[#FDFBF7]/35 text-[10px] tracking-[0.2em] uppercase text-center mt-3" data-id="element-5938">
                Your Misl begins as a forming house · Activate it by inviting
                your people
              </p>
            </div>
          </div>
        </div>

        {/* ---------- 6. WhatsApp Invite Block ---------- */}
        <div id="misl-invite-block" className="mb-20 relative border border-[#D4AF37]/30 bg-[#131B2B] p-8 sm:p-12 scroll-mt-24" data-id="element-5939">
          <CornerOrnaments data-id="element-5940" />

          {/* Forming Misl status */}
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-5941">
            <span className="w-2 h-2 rounded-full bg-[#FDFBF7]/50 animate-pulse" data-id="element-5942" />
            <span className="text-[#FDFBF7]/50 text-[9px] tracking-[0.3em] uppercase" data-id="element-5943">
              Forming Misl · {formingMembers} of 5 members
            </span>
          </div>

          {/* Progress to active */}
          <div className="max-w-md mx-auto mb-10" data-id="element-5944">
            <div className="h-1 bg-[#D4AF37]/10 relative overflow-hidden" data-id="element-5945">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${formingMembers / 5 * 100}%`
            }} transition={{
              duration: 0.8
            }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]" data-id="element-5946" />
            </div>
            <p className="text-center text-[#FDFBF7]/40 text-[9px] tracking-[0.25em] uppercase mt-2" data-id="element-5947">
              {5 - formingMembers} more to activate
            </p>
          </div>

          <div className="text-center mb-8" data-id="element-5948">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5949">
              The Activation
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-4" data-id="element-5950">
              Invite Your People
            </h3>
            <p className="text-[#FDFBF7]/60 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-5951">
              Every Misl grows through its people. Share your Misl through
              WhatsApp, bring your group into Lahore Treasury, and activate your
              house together.
            </p>
          </div>

          {/* Invite link display */}
          <div className="max-w-2xl mx-auto mb-6" data-id="element-5952">
            <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-4 py-3" data-id="element-5953">
              <span className="font-mono text-[#D4AF37]/80 text-xs truncate flex-1" data-id="element-5954">
                {inviteLink}
              </span>
              <button onClick={handleCopyLink} className="shrink-0 flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-5955">
                {copied ? <>
                    <Check className="w-3 h-3" data-id="element-5956" />
                    Copied
                  </> : <>
                    <Copy className="w-3 h-3" data-id="element-5957" />
                    Copy
                  </>}
              </button>
            </div>
          </div>

          {/* CTAs */}
          <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5958">
            <button onClick={handleWhatsAppShare} className="flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-white hover:bg-[#128C7E] transition-all text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-5959">
              <MessageCircle className="w-4 h-4" data-id="element-5960" />
              Share on WhatsApp
            </button>
            <button onClick={handleCopyLink} className="flex items-center justify-center gap-3 px-6 py-4 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-5961">
              <Share2 className="w-4 h-4" data-id="element-5962" />
              Copy Invite Link
            </button>
          </div>

          <p className="text-[#FDFBF7]/40 text-xs text-center mt-6 italic max-w-xl mx-auto leading-relaxed" data-id="element-5963">
            Build your Misl through family, friends, classmates, and community
            groups. Five members is all it takes to step into the Court.
          </p>
        </div>

        {/* ---------- 7. Misl Benefits ---------- */}
        <div className="mb-16" data-id="element-5964">
          <div className="text-center mb-10" data-id="element-5965">
            <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5966">
              Why It Matters
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] leading-tight mb-3" data-id="element-5967">
              The Power of an Active Misl
            </h3>
            <p className="text-[#FDFBF7]/55 text-sm max-w-2xl mx-auto leading-relaxed" data-id="element-5968">
              Activating your Misl unlocks identity, recognition, and rewards
              that no individual member can earn alone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5969">
            {benefits.map(b => <div key={b.title} className="relative p-6 border border-[#D4AF37]/15 bg-[#131B2B]/60 hover:border-[#D4AF37]/40 hover:bg-[#131B2B] transition-all group" data-id="element-5970">
                <CornerOrnaments data-id="element-5971" />
                <div className="w-10 h-10 border border-[#D4AF37]/30 bg-[#0B0F19] flex items-center justify-center mb-4 group-hover:border-[#D4AF37]/60 transition-colors" data-id="element-5972">
                  <b.icon className="w-4 h-4 text-[#D4AF37]" data-id="element-5973" />
                </div>
                <h4 className="font-serif text-lg text-[#FDFBF7] mb-2 leading-tight" data-id="element-5974">
                  {b.title}
                </h4>
                <p className="text-[#FDFBF7]/55 text-xs leading-relaxed" data-id="element-5975">
                  {b.body}
                </p>
              </div>)}
          </div>
        </div>

        {/* ---------- 8. Leaderboard / Status Connection ---------- */}
        <div className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B0F19] via-[#131B2B] to-[#0B0F19] p-8 sm:p-12 overflow-hidden" data-id="element-5976">
          <CornerOrnaments data-id="element-5977" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-5978" />
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8" data-id="element-5979">
            <div className="shrink-0 w-16 h-16 border border-[#D4AF37]/50 bg-[#0B0F19] flex items-center justify-center" data-id="element-5980">
              <Trophy className="w-7 h-7 text-[#D4AF37]" data-id="element-5981" />
            </div>
            <div className="flex-1 text-center lg:text-left" data-id="element-5982">
              <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-3 font-light" data-id="element-5983">
                Status & Recognition
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] mb-3 leading-tight" data-id="element-5984">
                Misls Rise in the Court
              </h3>
              <p className="text-[#FDFBF7]/65 text-sm leading-relaxed max-w-2xl" data-id="element-5985">
                Misls are not just social groups. Once active, they rise through
                ranking, XP, and participation, giving members another path to
                status and recognition inside Lahore Treasury.
              </p>
            </div>
            {!embedded && <button onClick={onRegister} className="shrink-0 flex items-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] transition-all text-[10px] tracking-[0.25em] uppercase font-bold shadow-[0_0_30px_rgba(212,175,55,0.2)]" data-id="element-5986">
                Enter the Court
                <ArrowRight className="w-3.5 h-3.5" data-id="element-5987" />
              </button>}
          </div>
        </div>
      </div>
    </Wrapper>;
};
// ---------- Treasury Nights Section ----------
const TreasuryNightsSection = () => {
  const rewards = [{
    n: '01',
    title: 'Scan & Unlock',
    body: 'Hidden codes reveal Live Night Edition artifacts exclusive to the venue.',
    icon: Sparkles
  }, {
    n: '02',
    title: 'Sensory Rituals',
    body: 'Scent blending, taste pairings, turban ceremonies keyed to your rank.',
    icon: Crown
  }, {
    n: '03',
    title: 'Commemorative Tokens',
    body: 'Hand-stamped physical coins for completed sets. Numbered. Engraved.',
    icon: Award
  }, {
    n: '04',
    title: 'Step Inside the Painting',
    body: 'Walk through the chambers your artifacts depict. The Darbar — full size.',
    icon: BookOpen
  }];
  return <section className="px-4 py-24 bg-[#131B2B] relative border-t border-[#D4AF37]/10" data-id="element-5988">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.12),_transparent_60%)]" data-id="element-5989" />
      <div className="max-w-6xl mx-auto relative z-10" data-id="element-5990">
        <div className="text-center max-w-3xl mx-auto" data-id="element-5991">
          <SectionHeader eyebrow="The Real World · In Person" title={<>
                TREASURY <span className="text-[#D4AF37]" data-id="element-5993">NIGHTS</span>
              </>} data-id="element-5992" />
          <p className="text-[#FDFBF7]/80 text-base font-light leading-relaxed" data-id="element-5994">
            Monthly preview evenings at{' '}
            <span className="text-[#D4AF37]" data-id="element-5995">Illuminarium Toronto</span> — where
            your digital Treasury comes alive in the physical world.
          </p>
        </div>
        <div className="mt-16" data-id="element-5996">
          <p className="text-center text-[#D4AF37]/60 text-[10px] tracking-[0.35em] uppercase mb-8" data-id="element-5997">
            What You Can Win — In Person Only
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5998">
            {rewards.map(r => {
            const Icon = r.icon;
            return <div key={r.n} className="p-6 border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-5999">
                  <p className="text-[#D4AF37]/40 font-mono text-[10px] mb-3" data-id="element-6000">
                    {r.n}
                  </p>
                  <Icon className="w-7 h-7 text-[#D4AF37] mb-4" data-id="element-6001" />
                  <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6002">
                    {r.title}
                  </p>
                  <p className="text-xs text-[#FDFBF7]/55 font-light leading-relaxed" data-id="element-6003">
                    {r.body}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
};
// ---------- Rewards Section ----------
const RewardsSection = () => <section className="px-4 py-24 bg-[#0B0F19] relative border-t border-[#D4AF37]/10" data-id="element-6004">
    <div className="max-w-4xl mx-auto text-center" data-id="element-6005">
      <SectionHeader eyebrow="The Returns" title={<>
            WHAT YOU <span className="text-[#D4AF37]" data-id="element-6007">EARN</span>
          </>} description="Collecting is the path. These are the privileges that come with it." data-id="element-6006" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12" data-id="element-6008">
        {[{
        icon: Trophy,
        label: 'Prestige',
        body: 'Verified rank, public profile, leaderboard standing.'
      }, {
        icon: Gift,
        label: 'Physical Rewards',
        body: 'Hand-stamped coins, printed cards, physical regalia.'
      }, {
        icon: Calendar,
        label: 'Event Access',
        body: 'Priority entry to Treasury Nights and private sessions.'
      }].map(it => <div key={it.label} className="p-6 border border-[#D4AF37]/20 bg-[#131B2B]" data-id="element-6009">
            <it.icon className="w-7 h-7 text-[#D4AF37] mb-4 mx-auto" data-id="element-6010" />
            <p className="font-serif text-lg text-[#FDFBF7] mb-2" data-id="element-6011">{it.label}</p>
            <p className="text-xs text-[#FDFBF7]/60 font-light leading-relaxed" data-id="element-6012">
              {it.body}
            </p>
          </div>)}
      </div>
    </div>
  </section>;
// ---------- Final CTA ----------
const FinalCTASection = ({
  onJoin
}: {
  onJoin: () => void;
}) => <section className="px-4 py-32 bg-[#131B2B] relative border-t border-[#D4AF37]/20 overflow-hidden" data-id="element-6013">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.15),_transparent_60%)]" data-id="element-6014" />
    <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-6015">
      <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-6" data-id="element-6016">
        The Treasury Awaits
      </p>
      <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-6017">
        ENTER THE <span className="text-[#D4AF37]" data-id="element-6018">LAHORE COURT</span>
      </h2>
      <OrnateDivider className="mb-8" data-id="element-6019" />
      <p className="text-[#FDFBF7]/70 text-base font-light leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-6020">
        Become a founding member. Open your first packet. Take your place among
        the Sangat recovering our shared heritage.
      </p>
      <button type="button" onClick={onJoin} className="px-10 py-4 bg-[#D4AF37] text-[#0B0F19] font-medium text-sm tracking-[0.25em] uppercase hover:bg-[#E5C158] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] inline-flex items-center gap-3 group" data-id="element-6021">
        Claim Your First Packet
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-6022" />
      </button>
    </div>
  </section>;
// ---------- Page Footer ----------
const PageFooter = () => <footer className="px-4 py-16 bg-[#0B0F19] border-t border-[#D4AF37]/20 text-center" data-id="element-6023">
    <div className="max-w-4xl mx-auto" data-id="element-6024">
      <OrnateDivider className="mb-10" data-id="element-6025" />

      <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-12" data-id="element-6026">
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6027">
          Treasury Home
        </Link>
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6028">
          Collections
        </Link>
        <Link to="/treasure" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6029">
          Court Packet
        </Link>
        <Link to="/support-the-court" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors" data-id="element-6030">
          Support the Court
        </Link>
      </div>

      <p className="text-[#D4AF37] font-serif text-lg tracking-[0.3em] uppercase mb-2" data-id="element-6031">
        Treasures of the Sikh Empire
      </p>
      <p className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase font-light" data-id="element-6032">
        An archive of the Lahore Darbar ·{' '}
        <Link to="/treasure/blueprint" aria-label="Sealed Chamber" title="Sealed Chamber" className="hover:text-[#D4AF37] transition-colors duration-500" data-id="element-6033">
          1799 — 1839
        </Link>
      </p>
    </div>
  </footer>;
// ---------- Top Bar ----------
const TopBar = () => <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F19]/90 border-b border-[#D4AF37]/20" data-id="element-6034">
    <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-6035">
      <Link to="/" className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase flex items-center gap-1 transition-colors" data-id="element-6036">
        <ChevronLeft className="w-4 h-4" data-id="element-6037" /> Kingdoms
      </Link>
      <div className="font-serif text-xs sm:text-sm text-[#D4AF37] tracking-[0.25em] uppercase text-center" data-id="element-6038">
        Treasures of the Sikh Empire
      </div>
      <div className="w-16" data-id="element-6039" />
    </div>
  </header>;
export const LahoreTreasuryPage = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [invite, setInvite] = useState<{
    from?: string;
    misl?: string;
    code?: string;
  }>({});
  // Detect ?ref=...&from=...&misl=... on mount — opens onboarding pre-filled
  // when the user arrives via someone else's invite link.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref') || undefined;
    const from = params.get('from') || undefined;
    const misl = params.get('misl') || undefined;
    if (code || from || misl) {
      setInvite({
        from: from || 'a friend',
        misl,
        code
      });
      setIsOnboardingOpen(true);
    }
  }, []);
  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    setIsOnboardingOpen(false);
    const dashboard = document.getElementById('member-dashboard');
    if (dashboard) {
      dashboard.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full bg-[#0B0F19] text-[#FDFBF7] font-sans selection:bg-[#D4AF37] selection:text-[#0B0F19]" data-id="element-6040">
      <style data-id="element-6041">{`
        html { scroll-behavior: smooth; }
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Only mount Onboarding once the user actually opens it — keeps its bundle
                                          off the initial render path entirely. */}
      {isOnboardingOpen && <Suspense fallback={null} data-id="element-6042">
          <TreasuryOnboarding isOpen={isOnboardingOpen} onClose={() => setIsOnboardingOpen(false)} onComplete={handleOnboardingComplete} invitedBy={invite.from} invitedMisl={invite.misl} inviteCode={invite.code} data-id="element-6043" />
        </Suspense>}

      <TopBar data-id="element-6044" />
      <HeroSection onJoin={() => setIsOnboardingOpen(true)} data-id="element-6045" />

      {/* ---------- 3. Mission Section ---------- */}
      <MissionSection data-id="element-6046" />

      {/* ---------- 4. Daily Court Packet Section ---------- */}
      <DailyPacketSection onRegister={() => setIsOnboardingOpen(true)} data-id="element-6047" />

      {/* ---------- 4.5 Member Dashboard Preview Section ---------- */}
      <MemberDashboardPreviewSection data-id="element-6048" />

      {/* ---------- 5. Collections Section ---------- */}
      <CollectionsSection data-id="element-6049" />

      {/* ---------- 6. Rarity Section ---------- */}
      <RaritySection data-id="element-6050" />

      {/* ---------- 7. Trading Section ---------- */}
      <TradingSection data-id="element-6051" />

      {/* ---------- 8. Rank Section ---------- */}
      <RankSection data-id="element-6052" />

      {/* ---------- 9. Misl Section ---------- */}
      <MislSection onRegister={() => setIsOnboardingOpen(true)} data-id="element-6053" />

      {/* ---------- 10. Treasury Nights Section ---------- */}
      <TreasuryNightsSection data-id="element-6054" />

      {/* ---------- 11. Rewards Section ---------- */}
      <RewardsSection data-id="element-6055" />

      {/* ---------- 12. Final CTA Section ---------- */}
      <FinalCTASection onJoin={() => setIsOnboardingOpen(true)} data-id="element-6056" />

      {/* ---------- 13. Page Footer ---------- */}
      <PageFooter data-id="element-6057" />
    </div>;
};